<script lang="ts">
  // BJL-CHECK: none applicable; this component renders no DOM of its own beyond the
  // container MapLibre attaches to. Everything on screen is drawn by MapLibre GL and a
  // deck.gl overlay. See artifacts/deviations/TransitMap.md.
  //
  // The live map. deck.gl is used IMPERATIVELY (MapboxOverlay, never @deck.gl/react), so
  // the port from React changed the shell, not the rendering: `useRef` → plain locals,
  // one `useEffect(…, [])` → one `$effect`, and the rAF loop is untouched. The frozen-
  // segment glide math is the subtlest code in the repo and is unchanged.
  //
  // The geometry, colour and shape-matching half of the old file now lives in
  // `transitMapGeometry.ts` (mesh builders, badge atlas, glide math) so its module-level
  // caches are still built once per process rather than once per component instance.
  import 'maplibre-gl/dist/maplibre-gl.css'
  import { MapboxOverlay } from '@deck.gl/mapbox'
  import { IconLayer } from '@deck.gl/layers'
  import { ScenegraphLayer, SimpleMeshLayer } from '@deck.gl/mesh-layers'
  import {
    focusedRouteIds,
    showsMode,
    useTransitStore,
    type VehiclePosition,
    type VehicleType,
  } from '../../store/transit'
  import { useRiderStore } from '../../store/rider'
  import { useSettingsStore } from '../../store/settings'
  import { useLayersStore } from '../../store/layers'
  import { useStopsStore } from '../../store/stops'
  import { useTripPlanStore } from '../../store/tripPlan'
  import { useVehicleWatchStore } from '../../store/vehicleWatch'
  import { useFerryStore } from '../../store/ferry'
  import { useRailStore } from '../../store/rail'
  import { useNjBusStore } from '../../store/njbus'
  import { getCurrentScheduleMode, useDevStore, type ScheduleMode } from '../../store/dev'
  import { Colors, getRouteColor, isSubwayRoute } from '../../ui/colors'
  import { canonicalRouteLineGeometry } from '../../services/routeLineSmoothing'
  import { storeSelect } from '../../lib/store.svelte'
  import { mapControls } from './mapControls'
  import {
    METRES_PER_DEGREE,
    layoutTrailingConsist,
    offsetPoint,
    recordTrailPoint,
  } from './trainConsist'
  import { followWatchCameraPose, watchCameraPose, type WatchCameraFollowPose } from './watchCamera'
  import {
    R160_CAB_FORWARD_YAW,
    R160_FAR_SHELL_MATERIAL,
    R160_LABEL_ALT,
    R160_LIVERY_SURFACES,
    R160_MESH_MIN_ZOOM,
    R160_MODELS,
    R160_SIZE_SCALE,
    createR160FrontBullet,
    createR160Placard,
    createR160SideSign,
    r160CarKind,
    r160CarPlacards,
    r160FleetLod,
    r160TierUsesPbr,
    type R160CarKind,
    type R160Lod,
    type R160Placement,
  } from './r160Model'
  import {
    r160PlacementEligibleForProfile,
    r160PlacementIsSign,
    type R160DecalCar,
  } from './r160DecalLod'
  import {
    TrainRenderLodPolicy,
    trainRenderProfileForGeometry,
    type TrainRenderProfile,
  } from './trainRenderLod'
  import { getTrainLodBenchmarkSink, type TrainLodTierCounts } from './trainLodBenchmark'
  import {
    createTrainLodBenchmarkRunner,
    publishTrainLodBenchmarkCompactScenarioReport,
    type TrainLodBenchmarkRunner,
  } from './trainLodBenchmarkRunner'
  import {
    runTrainLodBenchmarkProfile,
    trainLodBenchmarkProfileFromEnv,
    trainLodFixtureVehiclePosition,
  } from './trainLodBenchmarkRuntime'
  import {
    createTrainLodDrawCallCounter,
    resolvedTrainLodScenegraphLayers,
  } from './trainLodDrawCallCounter'
  import { r160PbrEnvironment } from './pbrEnvironment'
  import { VENDORED_GLB_LOAD_OPTIONS } from './gltfLoadOptions'
  import { liveryPaint } from './liveries'
  import { liveryScenegraph } from './liveryScenegraph'
  import {
    XCEL40_FAR_SHELL_MATERIAL,
    XCEL40_LIVERY_SURFACES,
    XCEL40_SHELL_MATERIAL,
    XCEL40_LOAD_OPTIONS,
    XCEL40_MODELS,
    XCEL40_SIGN_PLACEMENTS,
    createXcel40Sign,
    xcel40LodForZoom,
    xcel40TierModelsSigns,
    xcel40TierUsesPbr,
    type Xcel40SignPlacement,
  } from './xcel40Model'
  import { VehicleSideIconLayer } from './vehicleSigns'
  import {
    FERRY_LABEL_ALT,
    FERRY_SIZE_SCALE,
    advanceFerryGlide,
    buildFerryHullMesh,
    buildFerryStripeMesh,
    ferryRenderMode,
    type FerryGlide,
  } from './ferryModel'
  import {
    RAIL_CAR_LENGTH_M,
    RAIL_DRAWN_CARS,
    RAIL_LABEL_ALT,
    advanceRailGlide,
    buildRailCarMesh,
    buildRailPath,
    buildRailStripeMesh,
    layoutRailConsist,
    railRenderMode,
    type RailGlide,
    type RailPath,
  } from './railModel'
  import { NJ_BUS_LABEL_ALT, njBusRenderMode } from './njBusModel'
  import { advanceNjBusMotion, type NjBusCall, type NjBusMotion } from './njBusMotion'
  import { serverNowSec } from '../../services/serverClock'
  import type { ShapeSeg, TrailPt, VehicleDatum } from './transitMapGeometry'
  import {
    BASE_URL,
    BHX,
    BUS_LABEL_ALT,
    BUS_SIZE_SCALE,
    CAR_SPACING_M,
    CAR_TRUCK_CENTRES_M,
    DISTANCE_TRAIN_LOD_ENABLED,
    HEADING_TAU,
    ITINERARY_WALK_COLOR,
    MAX_CATCHUP_MPS,
    NYC_CENTER,
    NYC_ZOOM,
    PROGRESS_SMOOTH_TIME,
    RENDER_POS_TAU,
    RENDER_SNAP_M,
    ROUTE_ALIAS,
    ROUTE_OFFSETS,
    SEG_MATCH_TOL_SQ,
    SELECTED_BUS_ROUTE_COLOR,
    SELECTED_COLOR,
    STALE_STATE_GAP_S,
    TRAIL_KEEP_M,
    TRAIL_STEP_M,
    TRAIN_CARS,
    TRAIN_CARS_EXIT_ZOOM,
    TRAIN_CARS_MIN_ZOOM,
    TRAIN_MARKER_ARROW_PX,
    TRAIN_MARKER_BADGE_PX,
    USER_LOCATION_COLOR,
    anchorGlideTrack,
    applyItinerary,
    approxMeters,
    bearingAtT,
    bucketTrainCarsByLod,
    calcBearing,
    closestShapeIdx,
    getRouteColorRgb,
    createBusBadge,
    createBusStopBadge,
    createFerryBadge,
    createRailBadge,
    createNjBusBadge,
    createStopBadge,
    stopBadgeRows,
    createTrainBadgeUrl,
    createTrainHeadingArrow,
    injectRiderPulseCSS,
    matchSegmentInShapes,
    routeLineOffsetExpression,
    routeLineOffsetMetres,
    sampleSeg,
    seedTrail,
    shouldCullProjectedVehicle,
    shouldUseLabelOnlyVehicleLayers,
    tintActive,
    TrainLodClassificationCache,
    vehicleColorRgb,
  } from './transitMapGeometry'
  import {
    BUILDING_MIN_ZOOM,
    MAP_STYLES,
    applyMapSchemePaint,
    currentMapScheme,
    watchMapScheme,
    type MapScheme,
  } from './mapTheme'

  // Extruded basemap buildings. Named here because three places need the same id: the
  // layer definition, the visibility switch, and the restyle, which has to re-insert this
  // one UNDER the incoming style's labels while every other app layer goes above them.
  const BUILDINGS_LAYER_ID = 'buildings-3d'

  interface Props {
    onVehiclePress?: (vehicle: VehiclePosition) => void
    onStationPress?: (station: { id: string; name: string; routes: string[] }) => void
    onBusStopPress?: (stop: { id: string; name: string }) => void
    onFerryLandingPress?: (landing: { id: string; name: string }) => void
    onRailStationPress?: (station: { id: string; name: string }) => void
    onNjBusStopPress?: (stop: { id: string; name: string }) => void
    userLocation?: { latitude: number; longitude: number; accuracy?: number } | null
  }

  // Destructured props are reactive in Svelte 5, so the map event handlers below read the
  // current callback at invocation time. That is what the React version needed the three
  // `onXPressRef` refs for; they are gone.
  const {
    onVehiclePress,
    onStationPress,
    onBusStopPress,
    onFerryLandingPress,
    onRailStationPress,
    onNjBusStopPress,
    userLocation,
  }: Props = $props()

  let container: HTMLDivElement
  // Plain locals, not `$state`: nothing here is rendered from, it is all handed to
  // MapLibre / deck.gl imperatively. These were `useRef` under React for the same reason.
  let map: any = null
  let MarkerClass: any = null
  let riderMarker: any = null
  let benchmarkRunner: TrainLodBenchmarkRunner | null = null
  let benchmarkFleetActive = false
  let benchmarkWatchedVehicleId: string | null = null
  let benchmarkBoardedVehicleId: string | null = null
  let benchmarkModelCacheToken = ''
  let animFrame = 0
  let lastFrame = 0

  let stopCoords: Record<string, { lat: number; lon: number }> = {}
  let vehicles: VehiclePosition[] = []
  let routeShapes = new Map<string, [number, number][][]>()
  // Shared cache of on-track geometry between two stops (keyed route|prev|next).
  // This is the ONLY per-vehicle/route state the animation needs: position itself is a
  // pure function of the wall clock against the feed's fixed timestamps (no accumulated
  // animation state to drift out of sync with the feed).
  const segCache = new Map<string, ShapeSeg | null>()
  // Cap + eviction batch for segCache; see the prune block in frame(). ~50 points per
  // entry, so 4000 entries is ~10 MB, several times the live fleet's working set, which
  // is what keeps eviction off the hot path. Evicting a batch rather than a single entry
  // means the scan runs once per few thousand inserts instead of on every frame past the cap.
  const SEG_CACHE_MAX = 4000
  const SEG_CACHE_EVICT = 1000
  // Per-vehicle animation state: the FROZEN segment the model is gliding right now
  // (prev→next stops + the timestamps captured when the segment was adopted) and its
  // progress along it. Freezing is the whole point: per-poll feed revisions to timing or
  // to which segment the train is "on" are NOT applied mid-glide: the train finishes its
  // current segment smoothly and only re-syncs with the feed once it reaches the stop,
  // where the hand-off is continuous. This is what makes motion stutter-free on a feed
  // that rewrites itself every 15 s.
  // nextSeq = the feed stopSequence of `next`, captured when this segment was adopted.
  // It's the monotonic key the hand-off uses to refuse a feed that has REGRESSED the train
  // to an earlier stop (a stale snapshot arriving out of order, or a trip re-assignment),
  // which would otherwise build a backward segment and glide the model in reverse. 0 means
  // "no usable sequence this poll" (NYC's subway feed sometimes omits stopSequence), in
  // which case the hand-off falls back to the id-based guard.
  // vel = the SmoothDamp-tracked rate of change of `progress` (progress units / s), carried
  // across frames so the critically-damped follow has continuous velocity (C1): without it
  // each frame would re-derive speed from scratch and the motion could not be made smooth.
  interface VehAnim {
    prev: string
    next: string
    dep: number
    arr: number
    progress: number
    at: number
    nextSeq: number
    vel: number
  }
  const anim = new Map<string, VehAnim>()
  let deckOverlay: InstanceType<typeof MapboxOverlay> | null = null
  // Per (vehicleId:segIdx) last RENDERED heading. Each frame it's eased toward the live
  // target with a shortest-angle exponential (see HEADING_TAU) so a step in the track
  // tangent at a shape vertex / segment hand-off, or a jump in a bus's movement-vector
  // heading, never snap-rotates or spins the 3D model. Seeded to the target on first sight
  // (no spin-up from north); persisted across frames.
  const renderHeading = new Map<string, number>()
  let headingLastNow = 0
  // Per BUS (`${vehicleId}`) position-follow state: [lon, lat, vLon, vLat].
  // Each drawn instance SmoothDamps toward its target inside emit(), so a segment-hand-off pop
  // or wrong-track re-match becomes a smooth glide (or a >RENDER_SNAP_M snap) instead of a
  // one-frame skip / blink-out. Subway cars are deliberately NOT here: they are all sampled
  // from the one breadcrumb path, and a per-car Cartesian spring made them cut across curves.
  // Persisted across frames; pruned when large.
  const emitSmooth = new Map<string, number[]>()
  // Per-train (`${vehicleId}`) smoothed CENTER heading: the front car's facing (and the
  // straight-spread fallback axis). Easing it (shortest-angle, slew-limited) means a sharp
  // track-direction change at a corner / hand-off rotates the model gently instead of
  // snapping. Persisted across frames; pruned when large.
  const centerHead = new Map<string, number>()
  // Per-train breadcrumb trail (see TrailPt): the rendered center path, sampled by
  // follower cars in multi-car mode so the train bends along the route. Recorded for
  // every live subway even off-screen (cheap: one distance check per frame, one point
  // per TRAIL_STEP_M moved) so a zoom-in or viewport re-entry always finds real path
  // history. Pruned by arc length per train; bounded as a whole like the other maps.
  const trails = new Map<string, TrailPt[]>()
  const WATCH_TRAIL_STEP_M = 0.12
  let watchCameraFollow: WatchCameraFollowPose | null = null
  // The camera the map was on before the chase camera borrowed it, so exiting can hand it
  // back. See `unsubWatchedVehicle` for why this exists and why it holds no centre.
  let preWatchCamera: { zoom: number; pitch: number; bearing: number } | null = null
  // Reusable datum pool. The ~30 fps render path mutates these objects in place instead of
  // allocating ~cars×trains fresh VehicleDatum + [lon,lat] tuples every tick: that churn
  // was a periodic GC-pause source, visible as intermittent stutter across the whole fleet.
  const ordinaryDatumPool: VehicleDatum[] = []
  const pinnedDatumPool: VehicleDatum[] = []
  const ordinarySignPool: { car: VehicleDatum; placement: R160Placement }[] = []
  const pinnedSignPool: { car: VehicleDatum; placement: R160Placement }[] = []
  // The bus's three destination signs, pooled for the same reason. Only filled above
  // XCEL40_SIGN_MIN_ZOOM, so at the zooms where the bus field is dense these stay empty.
  const ordinaryBusSignPool: { bus: VehicleDatum; placement: Xcel40SignPlacement }[] = []
  const pinnedBusSignPool: { bus: VehicleDatum; placement: Xcel40SignPlacement }[] = []
  let ordinaryVehicleLayers: any[] = []
  let pinnedVehicleLayers: any[] = []
  const trainLodDrawCallCounter = createTrainLodDrawCallCounter()
  const trainRenderLodPolicy = new TrainRenderLodPolicy()
  const trainLodClassificationCache = new TrainLodClassificationCache<TrainRenderProfile>()
  let lastFleetLayerUpdate = -Infinity
  let lastPinnedVehicleKey = ''
  const ORDINARY_FLEET_FRAME_MS = 1000 / 30
  // Production builds keep instrumentation tree-shaken unless the dedicated device
  // profile opts in. DEV retains the hook for local browser diagnostics.
  const TRAIN_LOD_BENCHMARK_BUILD =
    import.meta.env.DEV || import.meta.env.VITE_TRAIN_LOD_BENCHMARK === 'true'
  const TRAIN_LOD_BENCHMARK_AUTO_RUN = import.meta.env.VITE_TRAIN_LOD_BENCHMARK === 'true'
  const renderWatchedVehicleId = () => benchmarkFleetActive
    ? benchmarkWatchedVehicleId
    : useVehicleWatchStore.getState().watchedVehicleId
  const renderBoardedVehicleId = () => benchmarkFleetActive
    ? benchmarkBoardedVehicleId
    : useRiderStore.getState().trackedVehicleId
  type VehicleLayerMetrics = {
    layerBuildMs: number
    trainDatumCount: number
    stickerInstanceCount: number
    triangles: number
    drawCalls: number | null
    scenegraphLayerCount: number
    emptyModelTierLayerCount: number
    tiers: TrainLodTierCounts
  }
  const emptyVehicleLayerMetrics = (): VehicleLayerMetrics => ({
    layerBuildMs: 0,
    trainDatumCount: 0,
    stickerInstanceCount: 0,
    triangles: 0,
    drawCalls: null,
    scenegraphLayerCount: 0,
    emptyModelTierLayerCount: 0,
    tiers: { marker: 0, L2: 0, L1: 0, L0: 0 },
  })
  let vehicleLayerMetrics = emptyVehicleLayerMetrics()
  let ordinaryVehicleLayerMetrics = emptyVehicleLayerMetrics()
  let pinnedVehicleLayerMetrics = emptyVehicleLayerMetrics()
  const R160_TRIANGLE_COUNTS = {
    // L0 repeats some encoded mesh primitives through glTF nodes. These are submitted
    // instance counts, not just the unique encoded index-accessor totals.
    L0: { A: 13_920, B: 11_316 },
    L1: { A: 1_444, B: 1_464 },
    L2: { A: 582, B: 368 },
  } as const
  const R160_DECODED_BYTES = {
    // L0 and L1 roughly DOUBLED when the livery panel arrived, and not because of the
    // extra primitive: glTF attributes are per primitive, so the sidewall's UV layer puts
    // a TEXCOORD_0 on every primitive of the carbody whether or not it samples one. L2 is
    // untouched because the far shell has no panel and its UV layer is stripped on export.
    L0: { A: 797_952, B: 630_456 },
    L1: { A: 114_456, B: 113_584 },
    L2: { A: 32_916, B: 20_160 },
  } as const
  const r160ModelLoadStarted = new Map<string, number>()
  const r160ModelLoadReported = new Set<string>()
  let lastTrainLodMemorySample = -Infinity
  // Per-bus glide segment. Buses are the road analog of the subway's frozen-segment
  // glide, but they lack the stop-time + shape data that snaps trains to track, so a
  // bus segment is simply its two most recent GPS fixes (from→to) plus the wall-clock
  // window to traverse between them. Each frame interpolates `from`→`to` by elapsed
  // time, turning the ~20 s teleport into continuous motion. Heading is derived from
  // the segment's own from→to displacement (the SIRI Bearing field is unreliable,
  // ~83° median error) and held while a bus barely moves so idle buses don't spin.
  interface BusGlideSeg {
    fromLon: number
    fromLat: number
    toLon: number
    toLat: number
    t0: number
    t1: number // wall-clock (s) window the segment is gliding over
    lon: number
    lat: number // last interpolated position (next segment's start)
    heading: number
    track: ShapeSeg | null // route slice this segment glides along, or null for straight-line
    moved: boolean // has this bus ever been observed to genuinely move?
    routeFaced: boolean // has facing been set from route geometry yet (vs the feed-Bearing seed)?
    lastFixAt: number // wall-clock (s) the last new fix was adopted; sizes the glide window
    lastFixTs: number // feed RecordedAtTime (epoch s) of the last adopted fix; rejects stale/out-of-order fixes
    sampledAt: number // wall-clock (s) this glide was last integrated; detects a stale (unrendered) glide
  }
  const busGlides = new Map<string, BusGlideSeg>()
  // id → [lon, lat] for every NYC bus stop, built from the same lazy /api/bus-stops
  // load that feeds the bus-stop dots. Used to plot the boarded bus's route line +
  // stops from its onward-call stop ids (which carry no coordinates of their own).
  const busStopCoords = new Map<string, [number, number]>()
  // id → the lines serving that stop, from the same load. The boarded bus's onward calls
  // name stop ids and nothing else, so this is what turns one into a route-pill badge.
  const busStopRoutes = new Map<string, string[]>()
  // routeId → its representative shape polyline(s), each tagged with the GTFS direction
  // it's drawn in (so a bus can be faced along the correct travel direction). Lazy-loaded
  // with the bus stops. Drives road-snapped motion, the boarded-route highlight, and
  // (via DirectionRef) the 3D facing of stopped buses.
  const busRouteShapes = new Map<string, { dir: string; coords: [number, number][] }[]>()
  let storeUnsub: (() => void) | null = null
  // NYC Ferry. Boats are NOT in `vehicles`: that list feeds the train/bus glide pipeline,
  // which snaps motion to route shapes and stop-time segments a boat doesn't have. They
  // keep their own list, glide (ferryModel.ts) and layers, appended beside the fleet's on
  // every deck submission. See `buildFerryLayers`.
  let ferries: VehiclePosition[] = []
  const ferryGlides = new Map<string, FerryGlide>()
  let ferryGlideLastNow = 0
  // Built once per map: ~200 triangles each, shared by every boat.
  const FERRY_HULL_MESH = buildFerryHullMesh()
  const FERRY_STRIPE_MESH = buildFerryStripeMesh()
  // LIRR + Metro-North, kept out of `vehicles` for the ferry's reason: they are GPS-placed,
  // and glide along their own branch lines (railModel.ts), not the subway's stop-time
  // segments. Own list, glide state and layers; see `buildRailLayers`.
  let railTrains: VehiclePosition[] = []
  const railGlides = new Map<string, RailGlide>()
  let railGlideLastNow = 0
  // Branch id → its drawn track lines, prepared for projection once per network load.
  let railPaths = new Map<string, RailPath[]>()
  const RAIL_CAR_MESH = buildRailCarMesh()
  const RAIL_STRIPE_MESH = buildRailStripeMesh()
  // NJ Transit's buses: the railroads' arrangement — own list, own motion state (PREDICTED
  // along each route's drawn line, see njBusMotion.ts), own layers in `buildNjBusLayers`. Kept
  // out of `vehicles` because that list feeds the MTA fleet's track-snapped glide and liveries.
  let njBuses: VehiclePosition[] = []
  const njBusGlides = new Map<string, NjBusMotion>()
  let njBusGlideLastNow = 0
  // Route → all its lines, and `route|direction` → that direction's (a bus moves along its own
  // trip direction's; njBusMotion.ts, "Which line").
  let njBusPaths = new Map<string, RailPath[]>()
  let njBusDirPaths = new Map<string, RailPath[]>()
  // Stop id → place, for resolving a quiet bus's `upcomingCalls` (njBusMotion's estimate).
  let njBusStopCoords = new Map<string, { lon: number; lat: number }>()
  // A payload's calls, resolved — keyed by the payload array itself, so the same push resolves
  // once and the motion sees the same array (and keeps its projection) until the next one.
  let njBusCallCache = new WeakMap<NonNullable<VehiclePosition['upcomingCalls']>, NjBusCall[]>()
  let selectedStationId: string | null = null
  let selectedBusStopId: string | null = null
  // The light/dark basemap currently up, and the watcher that reports when the resolved
  // answer changes (see mapTheme.ts: the preference, the OS setting, or the clock moved).
  let mapScheme: MapScheme = 'dark'
  let stopSchemeWatch: (() => void) | null = null

  // Per-schedule in-memory cache so switching modes swaps a local instead of waiting for a
  // network round-trip.
  type ShapeCache = { canonicalShapes: Map<string, [number, number][][]>; colored: any }
  type StopsCache = {
    id: string; name: string; lat: number; lon: number
    routes: string[]
    transferRoutes?: string[]
    limitedTransfers?: Array<{ routes: string[]; opensHour: number; closesHour: number }>
    memberIds?: string[]
  }[]
  let scheduleShapeData: Record<string, ShapeCache> = {}
  let scheduleStopsData: Record<string, StopsCache> = {}
  let activeSchedule: ScheduleMode = useDevStore.getState().scheduleMode ?? getCurrentScheduleMode()

  // The only store value this component *renders* from. Vehicle data flows
  // Zustand → imperative subscribe → MapLibre directly, bypassing the reactive graph
  // entirely; the React version also selected `selectedRouteIds`/`transitFilter`, but only
  // ever used them to re-render a static tree, so they are gone.
  const trackedVehicleId = storeSelect(useRiderStore, (s) => s.trackedVehicleId)

  $effect(() => {
    if (!container) return

    let cancelled = false
    const originalContainerStyle = container.style.cssText
    if (TRAIN_LOD_BENCHMARK_AUTO_RUN) {
      Object.assign(container.style, {
        position: 'fixed', inset: '0', width: '100vw', height: '100vh',
      })
    }

    import('maplibre-gl').then(({ Map: MapGL, NavigationControl, Marker }) => {
      if (cancelled || !container || map) return
      MarkerClass = Marker

      // Resolved once here rather than read reactively: the map is built a single time and
      // restyled in place afterwards (see applyScheme below), so this is only the style it
      // opens on.
      mapScheme = currentMapScheme()

      map = new MapGL({
        container,
        style: MAP_STYLES[mapScheme],
        center: NYC_CENTER,
        zoom: NYC_ZOOM,
        pitch: 30,
        maxPitch: 85,
        attributionControl: false,
      })

      map.addControl(new NavigationControl(), 'top-right')

      map.on('load', () => {
        if (cancelled) return

        // Every id the basemap arrived with. A restyle has to tell the app's own sources
        // and layers from the basemap's to carry only ours across, and the two basemaps
        // don't share id sets, so what's ours is captured once, positively, by diffing
        // against this (see appLayerIds below). map.getStyle() omits custom layers, so
        // deck.gl's interleaved layer group never appears in either set.
        const basemapStyle = map.getStyle()
        const basemapLayerIds = new Set<string>(basemapStyle.layers.map((l: any) => l.id))
        const basemapSourceIds = new Set<string>(Object.keys(basemapStyle.sources))

        // ---------------------------------------------------------------------
        // 3D buildings
        // ---------------------------------------------------------------------
        // The one app layer that draws the BASEMAP's data rather than the MTA's: both
        // OpenFreeMap styles ship the OpenMapTiles `building` layer flat, and the tiles
        // carry the `render_height` / `render_min_height` those flat fills ignore. So this
        // is an extrusion of geometry already on screen, not a new source — nothing is
        // fetched for it that the basemap wasn't fetching anyway.
        //
        // It goes in BELOW the basemap's first symbol layer (labels, shields, one-way
        // arrows) so a tall block never swallows the street name a rider is checking. That
        // placement is the reason the restyle path treats this id specially; everything
        // else the app adds is appended above the whole basemap.
        //
        // `hide_3d` is OSM's own "this footprint is a roof/part and must not be extruded
        // on its own" flag — without the filter, building parts stack into spikes.
        const firstSymbolId = (layers: any[]): string | undefined =>
          layers.find((l) => l.type === 'symbol')?.id
        map.addLayer(
          {
            id: BUILDINGS_LAYER_ID,
            type: 'fill-extrusion',
            source: 'openmaptiles',
            'source-layer': 'building',
            minzoom: BUILDING_MIN_ZOOM,
            filter: ['!=', ['get', 'hide_3d'], true],
            layout: {
              visibility: useLayersStore.getState().buildings ? 'visible' : 'none',
            },
            paint: {
              // Real metres, no exaggeration: the vehicles are modelled 1:1 and drawn in
              // the same space, so a bus beside a two-storey rowhouse has to look like one.
              'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 0],
              'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
              // Color and opacity are scheme-dependent; applyMapSchemePaint owns both,
              // exactly as the note below says.
            },
          } as any,
          firstSymbolId(basemapStyle.layers),
        )

        // The handful of app colors that flip with the basemap are NOT set in the layer
        // definitions below: applyMapSchemePaint owns every one of them, and is called
        // once at the end of this handler and again after each restyle. Writing them here
        // as well is what made adding a scheme-dependent color a two-file edit whose
        // easy-to-miss half produced a color that was right on boot and wrong after the
        // first theme flip. The layers below simply omit those properties.
        map.addSource('route-lines', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })

        // Same stop table routeLineOffsetScale reads, so the drawn line and the vehicles
        // placed on it can never drift apart. See ROUTE_OFFSET_STOPS.
        const zoomOffset = routeLineOffsetExpression() as any
        map.addLayer({
          id: 'rl-casing', type: 'line', source: 'route-lines',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 4, 15, 6],
            'line-opacity': 1,
            'line-offset': zoomOffset,
          },
        } as any)
        map.addLayer({
          id: 'rl-fill', type: 'line', source: 'route-lines',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.75, 12, 2.5, 15, 4],
            'line-opacity': 1,
            'line-offset': zoomOffset,
          },
        } as any)

        // Selected-bus-route highlight: the GTFS shapes of the bus lines picked in the
        // Routes panel, in each route's color: the bus counterpart of filtering the
        // subway route-lines to a selection. Added right after the subway route layers
        // so both kinds of highlight sit at the same z-level, under stops and vehicles.
        // Empty unless bus lines are selected; repopulated imperatively by
        // updateSelectedBusRoutes.
        map.addSource('selected-bus-routes', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        map.addLayer({
          id: 'sbr-casing', type: 'line', source: 'selected-bus-routes',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 12, 4, 15, 6],
            'line-opacity': 1,
          },
        } as any)
        map.addLayer({
          id: 'sbr-fill', type: 'line', source: 'selected-bus-routes',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.75, 12, 2.5, 15, 4],
            'line-opacity': 1,
          },
        } as any)

        // NYC Ferry route lines, in each route's own colour. DASHED, where every other line
        // on this map is solid: a subway line is where the track is, but a ferry's drawn
        // shape is only the timetable's idea of the crossing — the boat takes whatever line
        // the harbour allows (see ferryModel.ts), and a dash is the cartographic convention
        // for exactly that. Under the stations and stops, like the subway's lines. Empty
        // until the lazy ferry load (useFerryStore); visibility is applyLayerVisibility's.
        map.addSource('ferry-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'ferry-route-casing', type: 'line', source: 'ferry-routes',
          layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 2, 12, 4, 15, 6],
            'line-opacity': 0.55,
          },
        } as any)
        map.addLayer({
          id: 'ferry-route-fill', type: 'line', source: 'ferry-routes',
          layout: { visibility: 'none', 'line-join': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 1, 12, 2.5, 15, 4],
            'line-dasharray': [2, 1.5],
          },
        } as any)

        // LIRR + Metro-North track lines, in each branch's own colour — SOLID, unlike the
        // ferry's dashes, because a train is where its track is. Thinner than a subway line
        // with a dark casing, so a branch reads as "railroad" beside the subway's bold
        // strokes where the two run side by side (Queens, the Bronx). Empty until the lazy
        // rail load (useRailStore); visibility is applyLayerVisibility's.
        map.addSource('rail-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'rail-route-casing', type: 'line', source: 'rail-routes',
          layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#1b1f27',
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4.5, 15, 7],
            'line-opacity': 0.8,
          },
        } as any)
        map.addLayer({
          id: 'rail-route-fill', type: 'line', source: 'rail-routes',
          layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 12, 2.5, 15, 4],
          },
        } as any)

        // NJ Transit bus route lines. Every route is NJ Transit blue (the feed colours them all
        // black, so there is nothing per-route to show), drawn thin and half-transparent: ~340
        // lines across north Jersey overlap on every arterial, and at full strength they would
        // read as one blue sheet. Each route's main pattern plus genuine branches only — see
        // services/njbus/static.ts. Empty until the lazy NJ load (useNjBusStore).
        map.addSource('njbus-routes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'njbus-route-lines', type: 'line', source: 'njbus-routes',
          // One line per road: the other direction's lines are in the source for the buses to
          // move along, not to paint (NjBusShapeFeature.drawn). A server predating the flag
          // sends none, and everything draws as before.
          filter: ['!=', ['get', 'drawn'], false],
          layout: { visibility: 'none', 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.8, 13, 2, 16, 3.5],
            'line-opacity': 0.55,
          },
        } as any)

        map.addSource('stops', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        // Gray dot visible at all zoom levels: always marks the station's precise position
        map.addLayer({
          id: 'stop-circles', type: 'circle', source: 'stops',
          paint: {
            'circle-color': '#7a7a7a',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 2, 13, 3, 16, 4],
            'circle-stroke-width': 1.5,
          },
        })
        // Route badges + station name offset to the right of the dot so they don't cover it
        map.addLayer({
          id: 'stop-badges', type: 'symbol', source: 'stops', minzoom: 13,
          layout: {
            'icon-image': ['get', 'badgeKey'],
            'icon-allow-overlap': false,
            'icon-anchor': 'top',
            'icon-offset': [0, 4],
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 13, 0, 14, 10],
            'text-font': ['Noto Sans Bold'],
            'text-anchor': 'top',
            // Badges are 14px per row, so a complex that wraps to two rows would collide
            // with its own name at the single-row 2.2em. Only wrapped ones pay the gap.
            'text-offset': ['case', ['>=', ['get', 'badgeRows'], 2], ['literal', [0, 3.8]], ['literal', [0, 2.2]]],
            'text-optional': true,
            'text-max-width': 10,
          },
          paint: {
            'text-halo-width': 1.5,
          },
        })

        // Selected-station highlight. Two filtered circle layers sitting above the
        // normal stop dot: an expanding/fading pulse ring (paint props animated by
        // the rAF loop) plus a solid teal core so the tapped station stands out
        // against trains, route lines, and neighbouring stops. Filter starts empty
        // (matches nothing) and is swapped to the selected id imperatively.
        const SEL_FILTER_NONE = ['==', ['get', 'id'], '__none__'] as any
        map.addLayer({
          id: 'stop-selected-pulse', type: 'circle', source: 'stops',
          filter: SEL_FILTER_NONE,
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-opacity': 0.35,
            'circle-radius': 10,
            'circle-stroke-width': 0,
          },
        })
        map.addLayer({
          id: 'stop-selected-core', type: 'circle', source: 'stops',
          filter: SEL_FILTER_NONE,
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 5, 13, 7, 16, 9],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        })

        // Bus stops: every NYC bus stop (~15k), loaded lazily the first time the
        // bus (or all) filter is shown so the heavy payload never costs anything on a
        // subway-only session. Rendered as small blue dots, visually distinct from the
        // gray subway-station dots. minzoom keeps the citywide cloud from becoming
        // unreadable noise (and saves draw cost) until the user is zoomed in.
        map.addSource('bus-stops', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        map.addLayer({
          id: 'bus-stop-circles', type: 'circle', source: 'bus-stops', minzoom: 12,
          layout: { visibility: 'none' },
          paint: {
            'circle-color': '#00457C',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 1.5, 14, 3, 16, 4.5],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#cfe2f3',
          },
        })
        // Stop-name size curves: the default keeps names to street-level zooms so 13k
        // stops stay readable; the selected variant surfaces names from the city-wide
        // default zoom because a single line's stops are sparse. applyLayerVisibility
        // swaps between them with the selection filter (text-size 0 below the curve's
        // start would otherwise blank names even with the layer's minzoom relaxed).
        const BUS_LABEL_SIZE_DEFAULT = ['interpolate', ['linear'], ['zoom'], 15, 0, 16, 9] as any
        const BUS_LABEL_SIZE_SELECTED = ['interpolate', ['linear'], ['zoom'], 12, 8, 16, 10] as any
        map.addLayer({
          id: 'bus-stop-labels', type: 'symbol', source: 'bus-stops', minzoom: 15,
          layout: {
            visibility: 'none',
            'text-field': ['get', 'name'],
            'text-size': BUS_LABEL_SIZE_DEFAULT,
            'text-font': ['Noto Sans Regular'],
            'text-anchor': 'top',
            'text-offset': [0, 2.2],
            'text-optional': true,
            'text-max-width': 9,
          },
          paint: {
            'text-halo-width': 1.2,
          },
        })
        // Route pills for each bus stop: the lines that stop there, mirroring how a subway
        // station shows its bullets. Held back to a closer zoom than the dots (minzoom 14) so
        // the citywide field of ~15k stops doesn't turn the map into a wall of pills; icon
        // collision (allow-overlap:false) further thins them where stops crowd together. The
        // badge sits just below the dot, with the stop name below it: the same dot→lines→name
        // stack a subway station uses. Empty for stops the feed lists no routes for
        // (badgeKey === '' filtered out). Populated with the dots in the lazy bus-stop load.
        // Base filter shared with applyLayerVisibility, which composes the selected-bus-line
        // stop filter on top of it and must restore exactly this when the selection clears.
        const BUS_BADGE_BASE_FILTER = ['!=', ['get', 'badgeKey'], ''] as any
        map.addLayer({
          id: 'bus-stop-badges', type: 'symbol', source: 'bus-stops', minzoom: 14,
          filter: BUS_BADGE_BASE_FILTER,
          layout: {
            visibility: 'none',
            'icon-image': ['get', 'badgeKey'],
            'icon-allow-overlap': false,
            'icon-anchor': 'top',
            'icon-offset': [0, 4],
            // Scale the route-pill list down as the map zooms out (the badge images are
            // drawn at full zoom-16 size). Matters most with a bus line selected, when
            // the layer's minzoom is relaxed to 12 and full-size pills would dominate a
            // city-wide view.
            'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.6, 14, 0.8, 16, 1],
          },
        })

        // Selected-bus-stop highlight. The bus-stop analog of stop-selected-{pulse,core}:
        // a fading pulse ring plus a solid teal core on the bus-stops source, filtered to
        // the tapped stop's id (empty filter matches nothing until a stop is selected).
        // Kept in the same teal accent as the subway-station highlight so a selected stop
        // reads the same whichever mode you're in. Radii run a touch smaller than the
        // subway core because the bus dots themselves are smaller.
        map.addLayer({
          id: 'bus-stop-selected-pulse', type: 'circle', source: 'bus-stops', minzoom: 12,
          filter: SEL_FILTER_NONE,
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-opacity': 0.35,
            'circle-radius': 9,
            'circle-stroke-width': 0,
          },
        })
        map.addLayer({
          id: 'bus-stop-selected-core', type: 'circle', source: 'bus-stops', minzoom: 12,
          filter: SEL_FILTER_NONE,
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 4, 14, 6, 16, 8],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        })

        // Ferry landings. Only 25 of them, so unlike the ~15k bus stops they are drawn from a
        // city-wide zoom and named from 12: a landing is a destination in its own right, the
        // way a station is. White dot ringed in the scheme's stop stroke (a hollow-looking
        // dot reads as "pier", distinct from the grey station dot and blue bus-stop dot),
        // with the routes calling there as badges under it and the name under those — the
        // same dot→lines→name stack as a station, built from the same `badge-…` images.
        map.addSource('ferry-landings', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'ferry-landings', type: 'circle', source: 'ferry-landings',
          layout: { visibility: 'none' },
          paint: {
            'circle-color': '#ffffff',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 2.5, 13, 4.5, 16, 6],
            'circle-stroke-width': 2,
          },
        } as any)
        map.addLayer({
          id: 'ferry-landing-selected', type: 'circle', source: 'ferry-landings',
          filter: SEL_FILTER_NONE,
          layout: { visibility: 'none' },
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 5, 13, 7, 16, 9],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        map.addLayer({
          id: 'ferry-landing-badges', type: 'symbol', source: 'ferry-landings', minzoom: 11,
          layout: {
            visibility: 'none',
            'icon-image': ['get', 'badgeKey'],
            'icon-allow-overlap': false,
            'icon-anchor': 'top',
            'icon-offset': [0, 5],
            'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0.7, 14, 1],
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 9, 15, 11],
            'text-font': ['Noto Sans Bold'],
            'text-anchor': 'top',
            'text-offset': [0, 2.3],
            'text-optional': true,
            'text-max-width': 9,
          },
          paint: { 'text-halo-width': 1.5 },
        } as any)

        // LIRR + Metro-North stations: ~240, most of them far outside the subway's reach, so
        // drawn from a regional zoom and named from 10.5. A dark-cored dot in a white ring —
        // neither the grey station dot, the blue bus-stop dot nor the ferry's white pier
        // dot. Named but not badged: Jamaica alone is served by ten branches, and a row of
        // ten badges under one dot says less than the station's own sheet does.
        map.addSource('rail-stations', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'rail-stations', type: 'circle', source: 'rail-stations',
          layout: { visibility: 'none' },
          paint: {
            'circle-color': '#1b1f27',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2, 12, 4, 16, 6],
            'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 12, 2],
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        map.addLayer({
          id: 'rail-station-selected', type: 'circle', source: 'rail-stations',
          filter: SEL_FILTER_NONE,
          layout: { visibility: 'none' },
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 13, 7, 16, 9],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        map.addLayer({
          id: 'rail-station-labels', type: 'symbol', source: 'rail-stations', minzoom: 10.5,
          layout: {
            visibility: 'none',
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 10.5, 9, 15, 12],
            'text-font': ['Noto Sans Bold'],
            'text-anchor': 'top',
            'text-offset': [0, 0.7],
            'text-optional': true,
            'text-max-width': 9,
          },
          paint: { 'text-halo-width': 1.5 },
        } as any)

        // NJ Transit bus stops: ~16.5k, the MTA's bus-stop cloud's size, so the same rules —
        // nothing below zoom 13, names only at street level. NJ Transit blue ringed in its
        // orange, so an NJ stop beside an MTA one (Manhattan's west side, Port Authority) is
        // told apart by its dot before its name.
        map.addSource('njbus-stops', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
        map.addLayer({
          id: 'njbus-stop-circles', type: 'circle', source: 'njbus-stops', minzoom: 13,
          layout: { visibility: 'none' },
          paint: {
            'circle-color': Colors.njbus,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 2, 15, 3.5, 17, 5],
            'circle-stroke-width': 1.2,
            'circle-stroke-color': Colors.njbusAccent,
          },
        } as any)
        map.addLayer({
          id: 'njbus-stop-selected', type: 'circle', source: 'njbus-stops',
          filter: SEL_FILTER_NONE,
          layout: { visibility: 'none' },
          paint: {
            'circle-color': SELECTED_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 5, 16, 8],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        map.addLayer({
          id: 'njbus-stop-labels', type: 'symbol', source: 'njbus-stops', minzoom: 16,
          layout: {
            visibility: 'none',
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 16, 9, 18, 11],
            'text-font': ['Noto Sans Regular'],
            'text-anchor': 'top',
            'text-offset': [0, 0.8],
            'text-optional': true,
            'text-max-width': 9,
          },
          paint: { 'text-halo-width': 1.2 },
        } as any)

        // Boarded-bus route highlight. When a rider boards a bus, the bus's onward-call
        // stops are drawn as a route line + emphasized stop dots in the route's color,
        // the bus analog of the subway route a rider is on. One source carries both the
        // LineString and the stop Points; layers split them by geometry-type. Empty until
        // a bus is boarded, repopulated imperatively from the rider store.
        map.addSource('boarded-route', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        map.addLayer({
          id: 'br-casing', type: 'line', source: 'boarded-route',
          filter: ['==', ['geometry-type'], 'LineString'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#ffffff',
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 7, 16, 9],
            'line-opacity': 0.85,
          },
        } as any)
        map.addLayer({
          id: 'br-fill', type: 'line', source: 'boarded-route',
          filter: ['==', ['geometry-type'], 'LineString'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 14, 5, 16, 6.5],
            'line-opacity': 1,
          },
        } as any)
        map.addLayer({
          id: 'br-stops', type: 'circle', source: 'boarded-route',
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-color': '#000000',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 14, 5, 16, 6.5],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        // Each of those dots, identified: the lines serving it as route pills, then the stop
        // name under them — the same dot→lines→name stack `bus-stop-{badges,labels}` gives an
        // ambient bus stop, and the same one a subway station uses. A rider on a bus is being
        // shown where they can change, which is the question the bare dots left open.
        //
        // These are their own layers rather than a relaxed filter on `bus-stop-*` for two
        // reasons: the ambient bus-stop layers are off whenever the rider has the bus-stop
        // toggle off (most sessions), and their zoom curves are tuned to keep ~15k stops
        // quiet, which is the opposite of what one boarded route wants. Both start at a
        // city-wide zoom here because one route's stops are sparse.
        //
        // Added AFTER `bus-stop-*`, which is load-bearing: MapLibre resolves symbol collisions
        // top layer first, so where both sets cover the same stop these win and the ambient
        // pair is suppressed, rather than the two fighting and drawing one of each.
        map.addLayer({
          id: 'br-stop-badges', type: 'symbol', source: 'boarded-route', minzoom: 12,
          // Points only, and only those the feed lists routes for — an empty badgeKey names
          // no image, which MapLibre warns about once per feature.
          filter: ['all', ['==', ['geometry-type'], 'Point'], ['!=', ['get', 'badgeKey'], '']] as any,
          layout: {
            'icon-image': ['get', 'badgeKey'],
            'icon-allow-overlap': false,
            'icon-anchor': 'top',
            'icon-offset': [0, 5],
            // The badge images are drawn at full zoom-16 size; scale them back as the map
            // zooms out, exactly as the ambient bus badges do.
            'icon-size': ['interpolate', ['linear'], ['zoom'], 12, 0.7, 16, 1],
          },
        } as any)
        map.addLayer({
          id: 'br-stop-labels', type: 'symbol', source: 'boarded-route', minzoom: 13,
          filter: ['==', ['geometry-type'], 'Point'],
          layout: {
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 13, 9, 16, 11],
            'text-font': ['Noto Sans Regular'],
            'text-anchor': 'top',
            // Clear of the badge above it: that sits 5px under the dot and is 16px tall, so
            // at this layer's own text-size 2.6em lands the name just below its bottom edge.
            // text-offset is in ems, so it tracks the size curve on its own.
            'text-offset': [0, 2.6],
            'text-optional': true,
            'text-max-width': 9,
          },
          paint: {
            'text-halo-width': 1.4,
          },
        } as any)

        // Planned-trip itinerary: dashed amber walking legs, ride legs colored by their
        // route, and dots at the origin/destination/transfer stations. Empty until the
        // rider picks an itinerary in the Plan sheet; fed by the activeItinerary effect.
        // Sits above route lines and below the user-location dot.
        map.addSource('trip-itinerary', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        map.addLayer({
          id: 'ti-walk', type: 'line', source: 'trip-itinerary',
          filter: ['==', ['get', 'mode'], 'walk'],
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': ITINERARY_WALK_COLOR,
            'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2.5, 14, 4, 16, 5],
            'line-dasharray': [0.5, 1.8],
            'line-opacity': 0.95,
          },
        } as any)
        map.addLayer({
          id: 'ti-points', type: 'circle', source: 'trip-itinerary',
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-color': ITINERARY_WALK_COLOR,
            // One zoom interpolate whose stops branch on kind: MapLibre allows only a
            // single zoom-based subexpression per expression, not one per case branch.
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              11, ['case', ['==', ['get', 'kind'], 'end'], 5, 3.5],
              16, ['case', ['==', ['get', 'kind'], 'end'], 8, 6],
            ],
            'circle-stroke-width': 2.5,
            'circle-stroke-color': '#ffffff',
          },
        } as any)
        // A picked itinerary can predate map load (unlikely but free to handle).
        applyItinerary(map, useTripPlanStore.getState().activeItinerary, false)

        // User's own location: a translucent accuracy halo plus a solid magenta dot
        // with a white casing ring (the standard "you are here" idiom, recolored off the
        // MTA palette). Added last so it paints above stops and route lines. Empty until
        // the first GPS fix; fed imperatively by the userLocation effect (never the RAF
        // loop; GPS updates ~1 Hz, so a setData on a 1-feature source is negligible).
        map.addSource('user-location', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
        // Accuracy halo. Radius is the reported accuracy (m) converted to pixels for the
        // current zoom/latitude and written into the feature (accuracyPx) on each update.
        map.addLayer({
          id: 'user-accuracy', type: 'circle', source: 'user-location',
          paint: {
            'circle-color': USER_LOCATION_COLOR,
            'circle-opacity': 0.15,
            'circle-radius': ['coalesce', ['get', 'accuracyPx'], 0],
            'circle-stroke-width': 0,
            'circle-pitch-alignment': 'map', // halo lies flat on the ground like a real ring
          },
        })
        map.addLayer({
          id: 'user-dot', type: 'circle', source: 'user-location',
          paint: {
            'circle-color': USER_LOCATION_COLOR,
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 5, 16, 7],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
            'circle-pitch-alignment': 'viewport', // stays a round dot even when the map is pitched
          },
        })

        // Every scheme-dependent color, in one call, now that the layers it paints exist.
        // Same call the restyle path makes; see the note above the first addSource.
        applyMapSchemePaint(map, mapScheme)

        // ---------------------------------------------------------------------
        // Light/dark basemap
        // ---------------------------------------------------------------------
        // Everything above is the app's own; the diff against the ids the basemap arrived
        // with is therefore exactly what a restyle has to carry across. Fixed from here on,
        // so it stays correct however many times the basemap is swapped.
        const currentStyle = map.getStyle()
        const appLayerIds = new Set<string>(
          currentStyle.layers.map((l: any) => l.id).filter((id: string) => !basemapLayerIds.has(id)),
        )
        const appSourceIds = new Set<string>(
          Object.keys(currentStyle.sources).filter((id) => !basemapSourceIds.has(id)),
        )

        // Symbol images registered on the style, by key. The key encodes the route set the
        // badge draws (`badge-A|C|E`, `bus-badge-M15|M15-SBS`), which is the whole input to
        // drawing it, so a badge can be rebuilt from its key alone after a restyle drops
        // the style's images. Only the keys are kept: the ~15k bus stops resolve to
        // thousands of distinct route sets, and holding that many ImageData would be tens
        // of megabytes to avoid a redraw that happens at most twice a day.
        const SUBWAY_BADGE_PREFIX = 'badge-'
        const BUS_BADGE_PREFIX = 'bus-badge-'
        const badgeKeys = new Set<string>()
        const registerBadge = (key: string) => {
          if (map.hasImage(key)) return
          const bus = key.startsWith(BUS_BADGE_PREFIX)
          const routes = key.slice((bus ? BUS_BADGE_PREFIX : SUBWAY_BADGE_PREFIX).length).split('|')
          map.addImage(key, bus ? createBusStopBadge(routes) : createStopBadge(routes))
        }
        const addBadge = (key: string) => {
          badgeKeys.add(key)
          registerBadge(key)
        }

        // Swapping the basemap replaces the whole style: every source, layer and image.
        // transformStyle grafts the app's own sources and layers onto the incoming one,
        // which carries their live GeoJSON (MapLibre serializes a geojson source's current
        // data), their filters and their layout visibility across untouched: a plain
        // setStyle would drop a session's worth of lazily-loaded stops, shapes and
        // selection state and there is no cheap way to rebuild it. deck.gl re-adds its own
        // interleaved layer group on `styledata`, so the vehicles need nothing from us.
        const applyScheme = (scheme: MapScheme) => {
          if (!map || scheme === mapScheme) return
          mapScheme = scheme
          map.setStyle(MAP_STYLES[scheme], {
            diff: false,
            transformStyle: (previous: any, next: any) => {
              if (!previous) return next
              const carriedSources: Record<string, any> = {}
              for (const [id, source] of Object.entries(previous.sources)) {
                if (appSourceIds.has(id)) carriedSources[id] = source
              }
              const carried = previous.layers.filter((l: any) => appLayerIds.has(l.id))
              // Buildings are the one exception to "app layers go on top": they draw the
              // incoming basemap's own `openmaptiles` source and belong beneath its labels,
              // so they are spliced back in at the same place addLayer put them the first
              // time. Both styles carry that source; a basemap without it would have to
              // grow a guard here, since MapLibre rejects a layer naming a missing source.
              const buildings = carried.filter((l: any) => l.id === BUILDINGS_LAYER_ID)
              const above = carried.filter((l: any) => l.id !== BUILDINGS_LAYER_ID)
              const labelAt = next.layers.findIndex((l: any) => l.type === 'symbol')
              const base =
                labelAt === -1
                  ? [...next.layers, ...buildings]
                  : [
                      ...next.layers.slice(0, labelAt),
                      ...buildings,
                      ...next.layers.slice(labelAt),
                    ]
              return {
                ...next,
                sources: { ...next.sources, ...carriedSources },
                // Appended, so the app's layers stay above the basemap and keep the order
                // they were added in.
                layers: [...base, ...above],
              }
            },
          })
        }

        // Fires on every style AFTER this one (the first had already loaded by the time
        // this handler was registered): images belong to the style, so each badge has to be
        // re-registered or its symbol layer draws nothing, and the carried-over layers
        // still hold the outgoing scheme's paint.
        map.on('style.load', () => {
          if (cancelled) return
          for (const key of badgeKeys) registerBadge(key)
          applyMapSchemePaint(map, mapScheme)
        })
        stopSchemeWatch = watchMapScheme(applyScheme)

        // deck.gl overlay for vehicle rendering: uses WebGL instanced draw calls
        // so rendering cost is O(1) regardless of asset count (10k+ assets viable).
        // MapLibre handles the basemap, route lines, and stop badges; deck.gl handles
        // the vehicle layer exclusively.

        // Legacy kill-switch car count. The projected policy derives 1 vs TRAIN_CARS
        // independently from each train's resolved profile.
        let trainCars = 1
        const resolveTrainRenderProfile = (input: {
          vehicleId: string
          lon: number
          lat: number
          bearing: number
          boarded: boolean
          watched: boolean
          vehicleDisplay: 'labels' | 'trains' | 'both' | 'optimized'
          camera: {
            longitude: number
            latitude: number
            zoom: number
            pitch: number
            bearing: number
            width: number
            height: number
          }
          project: (position: readonly [number, number]) => { x: number; y: number } | null
        }): { profile: TrainRenderProfile; cacheHit: boolean } => {
          if (!DISTANCE_TRAIN_LOD_ENABLED) {
            // Watching outranks the display mode; BOARDING does not. The order of these
            // three is the whole rule — see `shouldUseLabelOnlyVehicleLayers`. A ride lasts
            // the trip, so a boarded vehicle that outranked `labels` was a 3D train the
            // rider had no setting to turn off, and it is why releasing the 3D toggle
            // looked like it did nothing. Below `labels`, boarding still pins L0: within
            // the mesh modes your own ride is never the one demoted by distance.
            if (input.watched) {
              return { profile: trainRenderProfileForGeometry('L0'), cacheHit: false }
            }
            if (input.vehicleDisplay === 'labels') {
              return { profile: trainRenderProfileForGeometry('marker'), cacheHit: false }
            }
            if (input.boarded) {
              return { profile: trainRenderProfileForGeometry('L0'), cacheHit: false }
            }
            const lod = r160FleetLod(
              input.camera.zoom,
              input.vehicleDisplay === 'trains' || input.vehicleDisplay === 'both',
            )
            return {
              // The kill switch intentionally keeps the legacy all-placards mesh behavior.
              profile: lod ? { geometry: lod, decals: 'full' } : trainRenderProfileForGeometry('marker'),
              cacheHit: false,
            }
          }

          const cached = trainLodClassificationCache.resolve({
            vehicleId: input.vehicleId,
            anchor: [input.lon, input.lat],
            heading: input.bearing,
            watched: input.watched,
            boarded: input.boarded,
            displayMode: input.vehicleDisplay,
            camera: input.camera,
          }, () => {
            let profile = trainRenderLodPolicy.resolve({
              vehicleId: input.vehicleId,
              anchor: [input.lon, input.lat],
              heading: input.bearing,
              project: input.project,
              watched: input.watched,
              boarded: input.boarded,
            })
            // `!input.watched` only: the policy above already returned L0 for a boarded
            // vehicle, and in `labels` that is demoted right back to a marker here. Boarding
            // pins detail WITHIN the mesh modes; it does not outrank the mode itself, which
            // is what made the 3D toggle look stuck on. See `shouldUseLabelOnlyVehicleLayers`.
            if (input.vehicleDisplay === 'labels' && !input.watched) {
              profile = trainRenderProfileForGeometry('marker')
            } else if (
              (input.vehicleDisplay === 'trains' || input.vehicleDisplay === 'both')
              && profile.geometry === 'marker'
            ) {
              profile = trainRenderProfileForGeometry('L2')
            }
            return profile
          })
          return { profile: cached.value, cacheHit: cached.cacheHit }
        }

        function buildVehicleLayers(
          datums: VehicleDatum[],
          layerScope: 'ordinary' | 'pinned' = 'ordinary',
          collectBenchmarkMetrics = false,
        ) {
          // Read display settings imperatively (same pattern the frame loop uses for the
          // transit/rider stores). buildVehicleLayers runs every frame, so a toggle takes
          // effect on the very next frame with no reload: exactly the live-update requirement.
          const currentSettings = useSettingsStore.getState()
          // Physical benchmark profiles must not inherit persisted presentation choices
          // from whichever user previously used the device. Freeze the production-default
          // optimized path and normal stainless skin without mutating persisted settings.
          const vehicleDisplay = benchmarkFleetActive ? 'optimized' : currentSettings.vehicleDisplay
          // Benchmarks must measure the as-built models, not whatever paint the last user
          // of this device happened to choose: a liveried body is a patched glTF with an
          // extra texture, and comparing one against an unliveried capture would compare
          // two different scenes. Same reasoning as the line above — and since the Minion
          // skin is a livery now, freezing this freezes its overlay meshes with it.
          // Two ids, because the fleets are chosen separately — a rider can run today's
          // buses beside as-built trains, and `classic` exists for the bus alone.
          const subwayLiveryId = benchmarkFleetActive ? 'default' : currentSettings.subwayLivery
          const busLiveryId = benchmarkFleetActive ? 'default' : currentSettings.busLivery
          const subwayLivery = liveryPaint(subwayLiveryId, 'subway')
          const busLivery = liveryPaint(busLiveryId, 'bus')
          // The Minion face used to be six SimpleMeshLayers bolted to the bus's nose,
          // because there was no front surface a texture could land on. `assign_end_cap`
          // in `export_bus.py` gives the cap its own material and elevation unwrap, so the
          // face is `drawFront` in the livery table now — six layers per bus gone, and the
          // face and the flank can no longer drift apart because they are one livery.
          // Only the WATCHED vehicle takes this scope off the label-only path — a boarded
          // one no longer does, so a rider in `labels` mode gets bullets for the whole map
          // including their own ride. See `shouldUseLabelOnlyVehicleLayers`.
          const scopedWatchedVehicleId = datums.find((d) => d.watched)?.vehicleId ?? null
          const scopedId = (id: string) => layerScope === 'ordinary' ? id : `${layerScope}-${id}`
          const signPool = layerScope === 'ordinary' ? ordinarySignPool : pinnedSignPool
          const busSignPool =
            layerScope === 'ordinary' ? ordinaryBusSignPool : pinnedBusSignPool
          const layerTint = useLayersStore.getState()
          const bodyIsTinted = benchmarkFleetActive ? false : tintActive(layerTint)
          const zoom = map.getZoom()
          type SubwayCarDatum = VehicleDatum & R160DecalCar & { trainRenderProfile: TrainRenderProfile }
          const sd = datums.filter(
            (d): d is SubwayCarDatum => d.type === 'subway' && !!d.trainRenderProfile,
          )
          const sdFront = sd.filter((d) => d.segIdx === 0)
          const markerFront = sdFront.filter((d) => d.trainRenderProfile.geometry === 'marker')
          // `both` is the one mode where the bullet is not the mesh's STAND-IN but its
          // label, so it keeps drawing over the model instead of only below the floor.
          const alwaysBullet = vehicleDisplay === 'both'
          const tierCounts: TrainLodTierCounts = collectBenchmarkMetrics
            ? { marker: 0, L2: 0, L1: 0, L0: 0 }
            : vehicleLayerMetrics.tiers
          if (collectBenchmarkMetrics) {
            for (const front of sdFront) tierCounts[front.trainRenderProfile.geometry]++
          }
          // ...tapered as the field opens up, the same way the line-label badges are: by
          // zoom 8 the whole system is on screen and full-size markers stop being markers
          // and become a wall of bullets. Full size where the mesh takes over, so the LOD
          // swap does not also change the marker's size.
          const MARKER_Z_OUT = 8
          const markerScale =
            0.72 +
            0.28 *
              Math.max(0, Math.min(1, (zoom - MARKER_Z_OUT) / (R160_MESH_MIN_ZOOM - MARKER_Z_OUT)))
          // Buses are a single rigid model → exactly one datum per vehicle.
          const bd = datums.filter((d) => d.type === 'bus')

          // ── Display mode `labels`: bullets instead of 3D models, at every zoom ─────
          // Render each vehicle as its route bullet/badge at the vehicle's position
          // instead of the full 3D mesh. Pickable so tapping a label still opens the
          // vehicle sheet (the onClick handler keys off the datum's vehicleId).
          if (shouldUseLabelOnlyVehicleLayers(vehicleDisplay, scopedWatchedVehicleId)) {
            // Zoom-aware pixel sizing: the bullets read too large when zoomed out, so taper
            // them 30% smaller at the zoomed-out end and back to full size when zoomed in.
            // labelScale = 0.70 at/below zoom 11 → 1.00 at/above zoom 15, linear in between.
            // buildVehicleLayers runs every frame, so `zoom` is current and the taper is live.
            const LABEL_Z_OUT = 11,
              LABEL_Z_IN = 15
            const zt = Math.max(0, Math.min(1, (zoom - LABEL_Z_OUT) / (LABEL_Z_IN - LABEL_Z_OUT)))
            const labelScale = 0.7 + 0.3 * zt
            const labelLayers = [
              new IconLayer<VehicleDatum>({
                id: scopedId('vehicle-label'),
                data: sdFront,
                getPosition: (d) => [d.position[0], d.position[1], 0] as [number, number, number],
                getIcon: (d) => ({ url: createTrainBadgeUrl(d.routeId), width: 22, height: 22 }),
                getSize: 30 * labelScale,
                sizeUnits: 'pixels' as const,
                pickable: true,
                parameters: { depthTest: false } as any,
              }),
              new IconLayer<VehicleDatum>({
                id: scopedId('bus-label'),
                data: bd,
                getPosition: (d) => [d.position[0], d.position[1], 0] as [number, number, number],
                getIcon: (d) => {
                  const b = createBusBadge(d.routeId)
                  return { url: b.url, width: b.width, height: b.height }
                },
                getSize: 26 * labelScale,
                sizeUnits: 'pixels' as const,
                pickable: true,
                parameters: { depthTest: false } as any,
              }),
            ]
            if (collectBenchmarkMetrics) {
              vehicleLayerMetrics = {
                layerBuildMs: 0,
                trainDatumCount: sd.length,
                stickerInstanceCount: 0,
                triangles: 0,
                drawCalls: null,
                scenegraphLayerCount: 0,
                emptyModelTierLayerCount: 0,
                tiers: tierCounts,
              }
            }
            return labelLayers
          }

          // One route badge per (route, ~position cell). Buses of the SAME line bunched or
          // laid over at the same spot (terminals, shared stops) would otherwise stack
          // identical pills on top of each other, visually "two labels on one bus". Collapse
          // those to a single label; buses of DIFFERENT routes at the same spot still each
          // keep their own (that distinction is real information).
          const BADGE_CELL = 0.0005 // ≈ one rendered bus length (~45–55 m)
          const bdBadges: VehicleDatum[] = []
          const badgeSeen = new Set<string>()
          for (const d of bd) {
            const key = `${d.routeId}|${Math.round(d.position[0] / BADGE_CELL)}|${Math.round(d.position[1] / BADGE_CELL)}`
            if (badgeSeen.has(key)) continue
            badgeSeen.add(key)
            bdBadges.push(d)
          }

          type RGBA = [number, number, number, number]
          type ColorAccessor = RGBA | ((d: VehicleDatum) => RGBA)

          // ⚠️ NOTHING tints the body any more — not watching, and no longer boarding.
          // Both tints were removed for the same reason, and the second one finished the
          // argument the first one started: a flat per-instance color multiplies over the
          // WHOLE model, so it does not mark the vehicle, it repaints it. On the R160 that
          // means the stainless, the cab black, the chrome and every placard go teal
          // together, and the livery the model exists to show is the thing you lose.
          //
          // Boarding already has signals that do not cost the model: the boarded route is
          // drawn as its own highlighted line with its stops (the `boarded-route` source),
          // the rider panel is open over the map naming the next stop, and the camera
          // follows. Painting the vehicle on top of that was redundant with all three.
          //
          // This gets more load-bearing, not less, as the models gain real liveries and
          // decals — an MTA bus IS its color, so a tint over it is destroying the only
          // thing that says which bus it is.

          // There is no `mkBus` any more. It existed for the six Minion overlay meshes,
          // the last hand-built quads on this vehicle; the face is `drawFront` on the
          // Minion livery now, so a bus is exactly one ScenegraphLayer (mkXcel40 below).

          // The bus body: one authored Xcelsior 40 per vehicle. Orientation carries a roll
          // of 90 that the SimpleMeshLayer overlays above do not, because a glTF is
          // exported Y-up (`export_yup=True`) while those meshes are authored in deck's
          // own Z-up frame — the same reason the R160's layer rolls 90.
          const busLod = xcel40LodForZoom(zoom)
          const mkXcel40 = () =>
            new ScenegraphLayer<VehicleDatum>({
              id: scopedId('b-body'),
              data: bd,
              scenegraph: liveryScenegraph(
                XCEL40_MODELS[busLod],
                busLivery,
                {
                  body: 'xcel40',
                  surfaceMaterials: XCEL40_LIVERY_SURFACES,
                  farShellMaterial: XCEL40_FAR_SHELL_MATERIAL,
                  // The roof. Straight down is this map's default look, so a livery that
                  // paints only the flanks leaves the thing you actually see unpainted.
                  shellMaterial: XCEL40_SHELL_MATERIAL,
                },
                busLiveryId,
              ) as any,
              // Vendored Draco. Required, not tuning — see VENDORED_GLB_LOAD_OPTIONS.
              loadOptions: XCEL40_LOAD_OPTIONS,
              getPosition: (d) => [d.position[0], d.position[1], 0] as [number, number, number],
              getColor: busBody as any,
              getOrientation: (d) => [0, 90 - d.bearing, 90] as [number, number, number],
              sizeScale: BUS_SIZE_SCALE,
              // Per tier, exactly as the R160 does it: L2 is one non-metallic material
              // exported for the unlit default, L0/L1 are the authored materials and need
              // the lit path. See xcel40TierUsesPbr.
              ...(xcel40TierUsesPbr(busLod)
                ? {
                    _lighting: 'pbr' as const,
                    _imageBasedLightingEnvironment: ({
                      layer,
                    }: {
                      layer: ScenegraphLayer<VehicleDatum>
                    }) => r160PbrEnvironment(layer.context.device),
                  }
                : {}),
              pickable: true,
            })

          const mkR160 = (
            id: string,
            scenegraph: string,
            data: SubwayCarDatum[],
            body: 'A' | 'B',
            tier: R160Lod,
          ) => {
            const loadKey = benchmarkModelCacheToken
              ? `${tier}:${body}:${benchmarkModelCacheToken}`
              : `${tier}:${body}`
            if (collectBenchmarkMetrics && !r160ModelLoadStarted.has(loadKey)) {
              r160ModelLoadStarted.set(loadKey, performance.now())
            }
            return new ScenegraphLayer<SubwayCarDatum>({
              id: scopedId(id),
              data,
              // Returns `scenegraph` itself (a URL deck loads) for the as-built livery and
              // until a patched one is ready, so paint arrives a beat late rather than the
              // fleet blanking while it loads. Cached by identity — see liveryScenegraph.
              scenegraph: liveryScenegraph(
                scenegraph,
                subwayLivery,
                {
                  // Which of the two R160 bodies this is, not just "a train": the agency
                  // scheme paints a cab car differently from a non-cab one, and the A car's
                  // band is anchored to the end away from its cab.
                  body: body === 'A' ? 'r160-a' : 'r160-b',
                  surfaceMaterials: R160_LIVERY_SURFACES,
                  farShellMaterial: R160_FAR_SHELL_MATERIAL,
                },
                subwayLiveryId,
              ) as any,
              // Points the Draco decoder at our vendored copy. Required, not tuning — see
              // VENDORED_GLB_LOAD_OPTIONS: the default CDN is unpkg, which our CSP
              // refuses, and the trains then silently vanish once the mesh takes over.
              loadOptions: VENDORED_GLB_LOAD_OPTIONS,
              getPosition: (d) => [d.position[0], d.position[1], 0] as [number, number, number],
              getColor: subwayBody as any,
              getOrientation: (d) => [
                0,
                90 - d.bearing + (
                  body === 'A' && r160CarKind(d.segIdx, TRAIN_CARS) === 'cab-forward'
                    ? R160_CAB_FORWARD_YAW
                    : 0
                ),
                90,
              ] as [number, number, number],
              sizeScale: R160_SIZE_SCALE,
              // Per TIER, not per layer: see r160TierUsesPbr. L0/L1 are the authored
              // metals and need the physically-based path; L2 is one matte material
              // exported for the unlit default, which is by far the cheapest fragment
              // shader on the map — and L2 is the tier that draws the most cars, since
              // it covers the whole 12–48 px band.
              ...(r160TierUsesPbr(tier)
                ? {
                    _lighting: 'pbr' as const,
                    // Without this the stainless shell renders near-black: metal has no
                    // diffuse to light, so punctual lights alone leave it with a couple of
                    // highlights and nothing else. The environment is what a mirror finish
                    // actually shows. See pbrEnvironment.ts.
                    _imageBasedLightingEnvironment: ({
                      layer,
                    }: {
                      layer: ScenegraphLayer<SubwayCarDatum>
                    }) => r160PbrEnvironment(layer.context.device),
                  }
                : {}),
              onFirstDraw: () => {
                if (!TRAIN_LOD_BENCHMARK_BUILD || r160ModelLoadReported.has(loadKey)) return
                const startedAt = r160ModelLoadStarted.get(loadKey)
                const sink = getTrainLodBenchmarkSink()
                if (startedAt == null || !sink) return
                const atMs = performance.now()
                sink.recordModelLoad({
                  atMs,
                  body,
                  tier,
                  durationMs: atMs - startedAt,
                  decodedBytes: R160_DECODED_BYTES[tier][body],
                })
                r160ModelLoadReported.add(loadKey)
              },
              pickable: true,
            })
          }

          // One layer per (body, cab direction): a scenegraph layer draws a single mesh at a
          // single orientation offset, and a consist mixes both bodies. Layer ids stay put
          // across frames even when a group is momentarily empty, so deck reuses the loaded
          // models instead of tearing them down every time a train enters or leaves the view.
          // One pass, three buckets, not one filter per layer. Each of these ran the
          // whole datum list and re-derived r160CarKind for every car in it, three times
          // over (six when a tracked train splits the fleet), every frame.
          const splitByKind = (data: VehicleDatum[]): Record<R160CarKind, VehicleDatum[]> => {
            const byKind: Record<R160CarKind, VehicleDatum[]> = {
              body: [], 'cab-forward': [], 'cab-rear': [],
            }
            for (const d of data) byKind[r160CarKind(d.segIdx, trainCars)].push(d)
            return byKind
          }
          // A null `lod` is marker mode: the three layers stay put holding the mesh they
          // already loaded, drawing nothing, so zooming back in costs no reload.
          const mkConsist = (idPrefix: string, lod: R160Lod | null, data: VehicleDatum[]) => {
            const mesh = lod ?? 'L1'
            const cars = splitByKind(data)
            return [
              mkR160(`${idPrefix}-b`, R160_MODELS.B[mesh], cars.body as SubwayCarDatum[], 'B', mesh),
              mkR160(`${idPrefix}-a-front`, R160_MODELS.A[mesh], cars['cab-forward'] as SubwayCarDatum[], 'A', mesh),
              mkR160(`${idPrefix}-a-rear`, R160_MODELS.A[mesh], cars['cab-rear'] as SubwayCarDatum[], 'A', mesh),
            ]
          }

          // Subway cars are normally a fixed silver, so an analysis layer has to switch the
          // body to the per-datum colour to be visible at all. Nothing else may write here:
          // the Minion skin used to multiply this yellow, which took the glazing, the trucks
          // and the wheels with it and is exactly what the no-tint rule in
          // `transitMapGeometry.ts` forbids. It is a livery now and paints the panel.
          const subwayBody: ColorAccessor = bodyIsTinted
            ? (d) => d.color
            : ([255, 255, 255, 255] as RGBA)
          // ⚠️ A LIVERIED BUS DROPS THE ROUTE COLOUR, and it has to.
          //
          // `getColor` multiplies the entire model, so painting the body its route colour —
          // which is what a bus has always done here — multiplies straight through the
          // livery artwork as well. Measured, not guessed: an ad-wrapped Xcelsior under the
          // QM6's blue rendered as a flat blue bus with no wrap visible anywhere on it.
          //
          // Nothing is lost by dropping it. The route already floats over every bus as its
          // own badge (`bus-badge` below), which is legible at zooms where the body is a
          // few pixels and the colour would not be. And an MTA bus IS its livery — that is
          // the whole reason this feature exists — so a route tint over it is destroying
          // the one cue the rider actually reads off the vehicle.
          //
          // The analysis tint still outranks everything: while a legend on screen claims
          // red means "10+ min late", every red body must mean that, livery or not.
          const busBody: ColorAccessor = bodyIsTinted
            ? (d) => d.color
            : busLivery
              ? ([255, 255, 255, 255] as RGBA)
              : (d) => d.color

          // Bucket in one pass by the already-resolved train tier and A/B body. A single A
          // layer handles forward and rear cabs through its per-instance orientation accessor.
          // Missing buckets create no ScenegraphLayer at all, so their GLBs cannot be loaded.
          const buckets = bucketTrainCarsByLod(
            sd,
            (d) => d.trainRenderProfile.geometry,
            (d) => r160CarKind(d.segIdx, DISTANCE_TRAIN_LOD_ENABLED ? TRAIN_CARS : trainCars) === 'body' ? 'B' : 'A',
          )
          const modelLayers: ScenegraphLayer<SubwayCarDatum>[] = []
          let triangles = 0
          for (const tier of ['L2', 'L1', 'L0'] as const) {
            for (const body of ['A', 'B'] as const) {
              const cars = buckets.get(`${tier}:${body}`) ?? []
              if (!cars.length) continue
              modelLayers.push(mkR160(
                `s-r160-${tier}-${body.toLowerCase()}`,
                benchmarkModelCacheToken
                  ? `${R160_MODELS[body][tier]}?trainLodCold=${encodeURIComponent(benchmarkModelCacheToken)}`
                  : R160_MODELS[body][tier],
                cars,
                body,
                tier,
              ))
              if (collectBenchmarkMetrics) triangles += R160_TRIANGLE_COUNTS[tier][body] * cars.length
            }
          }

          // ⚠️ SIGNS ARE FOR THE FOLLOWED VEHICLE ONLY — and signs ONLY.
          //
          // A sign (`r160PlacementIsSign`: the side rollsign and the front bullet) is a
          // lit dot matrix carrying live service data, so it is a per-(route,destination)
          // canvas AND the placard drawn at the most tiers — `minimumTier: 'route'`,
          // where every other placard needs the close tier. That combination is what
          // costs: across a rush-hour viewport it was a measurable frame-time regression,
          // to render text that is sub-pixel on a car drawn 10 px long. The followed
          // vehicle is the one the watch camera frames and the rider panel describes, so
          // it is the only one where a destination sign can be read at all.
          //
          // ⚠️ EVERY OTHER PLACARD STILL DRAWS ON THE WHOLE FLEET — car numbers, the US
          // flag, the MTA roundel, the tail and blacked-out cab lamps. Those are static
          // art rather than per-frame canvas work, the LOD policy already holds them to
          // the close tier, and they are most of what makes an R160 read as an R160.
          // Gating them too was tried and it is not the change that was wanted.
          //
          // Keyed off the DATUM, not off `layerScope`. The pinned pool happens to hold
          // precisely these vehicles today (see `targetPool`), but that is layer
          // plumbing; `boarded || watched` is the rule itself and stays correct if the
          // pooling is ever reorganised.
          //
          // Apply the frozen decal selector only after every train's shared profile exists.
          // Its marker/none branches return before placard iteration, so far trains allocate
          // and submit zero sticker datums.
          const carsByTrain = new Map<string, SubwayCarDatum[]>()
          for (const car of sd) {
            const consist = carsByTrain.get(car.vehicleId)
            if (consist) consist.push(car)
            else carsByTrain.set(car.vehicleId, [car])
          }
          type SubwaySignDatum = { car: SubwayCarDatum; placement: R160Placement }
          let signCount = 0
          for (const cars of carsByTrain.values()) {
            const profile = cars[0].trainRenderProfile
            if (profile.geometry === 'marker' || profile.decals === 'none') continue
            // One check per train, not per placard: every car of a consist is boarded or
            // watched together.
            const followed = cars[0].boarded || cars[0].watched
            for (const car of cars) {
              for (const placement of r160CarPlacards(car.carIndex, cars.length)) {
                if (!r160PlacementEligibleForProfile(profile, placement)) continue
                if (!followed && r160PlacementIsSign(placement.kind)) continue
                const pooled = signPool[signCount]
                if (pooled) {
                  pooled.car = car
                  pooled.placement = placement
                } else {
                  signPool[signCount] = { car, placement }
                }
                signCount++
              }
            }
          }
          const sdSigns = signPool.slice(0, signCount) as SubwaySignDatum[]

          // The bus's three destination signs — front, curb side and rear. Every bus
          // wears the same three (XCEL40_SIGN_PLACEMENTS is a constant, not a function:
          // a bus has no consist and no handedness to resolve), so this is a flat
          // product of the followed bus and that table.
          //
          // Same `boarded || watched` rule as the train placards above, for the same
          // reason — see that note. The tier check is the separate, non-performance one:
          // L2 merges `MAT_SIGN_DISPLAY` away, so a sign there would decal a panel that
          // is not in the mesh.
          type BusSignDatum = { bus: VehicleDatum; placement: Xcel40SignPlacement }
          let busSignCount = 0
          if (xcel40TierModelsSigns(busLod)) {
            for (const bus of bd) {
              if (!bus.boarded && !bus.watched) continue
              for (const placement of XCEL40_SIGN_PLACEMENTS) {
                const pooled = busSignPool[busSignCount]
                if (pooled) {
                  pooled.bus = bus
                  pooled.placement = placement
                } else {
                  busSignPool[busSignCount] = { bus, placement }
                }
                busSignCount++
              }
            }
          }
          const bdSigns = busSignPool.slice(0, busSignCount) as BusSignDatum[]

          const layers = [
            ...modelLayers,

            // Bus: one authored Xcelsior 40 per vehicle. This used to be five
            // SimpleMeshLayers of hand-built boxes — body, roof pod, glass, wheels,
            // headlights — each a flat colour. The glTF carries all five as real
            // geometry in its own materials, so they are one layer now.
            mkXcel40(),

            // ── The lowest LOD: bullet + heading arrow ──────────────────────────────
            // What replaces the mesh once a car is a few pixels long (r160LodForZoom).
            // One marker per TRAIN, not per car: sdFront is the lead car, and below this
            // zoom the consist is a single car anyway (TRAIN_CARS_MIN_ZOOM is 13.5).
            //
            // The arrow lies FLAT on the map (billboard: false) so it points along the
            // ground the way the train is running, at any map rotation or pitch; deck's
            // icon angle is counter-clockwise while a bearing is clockwise from north,
            // hence the negation. The bullet on top of it stays a billboard, because a
            // rotated route letter is not a readable route letter. Drawn before the
            // bullet so the bullet covers the arrow's tail.
            new IconLayer<SubwayCarDatum>({
              id: scopedId('train-marker-arrow'),
              data: markerFront,
              getPosition: (d) => [d.position[0], d.position[1], 0] as [number, number, number],
              getIcon: (d) => createTrainHeadingArrow(d.routeId),
              getAngle: (d) => -d.bearing,
              getSize: TRAIN_MARKER_ARROW_PX * markerScale,
              sizeUnits: 'pixels' as const,
              billboard: false,
              parameters: { depthTest: false } as any,
            }),
            //
            // The bullet is the half of the marker `both` keeps once there IS a mesh: it
            // carries the route, which the mesh cannot: every R160 is the same silver
            // whatever line it runs. The arrow above is dropped there instead of kept,
            // because heading is the one thing a 144 m consist already states.
            new IconLayer<SubwayCarDatum>({
              id: scopedId('train-marker'),
              data: alwaysBullet ? sdFront : markerFront,
              // Lifted to hang above the roof only when a mesh is under it; at ground
              // level it would sit in the middle of the car it is labelling. See
              // R160_LABEL_ALT.
              getPosition: (d) =>
                [d.position[0], d.position[1], d.trainRenderProfile.geometry === 'marker' ? 0 : R160_LABEL_ALT] as [number, number, number],
              getIcon: (d) => ({ url: createTrainBadgeUrl(d.routeId), width: 22, height: 22 }),
              getSize: TRAIN_MARKER_BADGE_PX * markerScale,
              sizeUnits: 'pixels' as const,
              // The mesh this stands in for was pickable, so tapping still opens the sheet.
              pickable: true,
              parameters: { depthTest: false } as any,
            }),

            new VehicleSideIconLayer<SubwaySignDatum>({
              id: scopedId('vehicle-route-signs'),
              data: sdSigns,
              getPosition: ({ car, placement }) => {
                const br = (car.bearing * Math.PI) / 180
                const cosLat = Math.cos((car.position[1] * Math.PI) / 180)
                const forwardOffset = placement.x * R160_SIZE_SCALE
                const sideOffset = placement.y * R160_SIZE_SCALE
                return [
                  car.position[0] + (forwardOffset * Math.sin(br) - sideOffset * Math.cos(br)) / (METRES_PER_DEGREE * cosLat),
                  car.position[1] + (forwardOffset * Math.cos(br) + sideOffset * Math.sin(br)) / METRES_PER_DEGREE,
                  placement.z * R160_SIZE_SCALE,
                ] as [number, number, number]
              },
              getIcon: ({ car, placement }) => placement.kind === 'side-sign'
                ? createR160SideSign(
                    car.routeId, car.destinationName || `${car.routeId} TRAIN`, placement.aspect!,
                  )
                : placement.kind === 'front-bullet'
                ? createR160FrontBullet(car.routeId)
                : createR160Placard(
                    placement.kind,
                    placement.kind === 'car-number' ? (car.carLabel || car.vehicleId.slice(-5)) : '',
                  ),
              getBearing: (d) => d.car.bearing,
              getSide: (d) => d.placement.side,
              getFacing: (d) => d.placement.facing,
              getSize: (d) => d.placement.height,
              sizeUnits: 'meters' as const,
              billboard: false,
              parameters: { depthTest: true } as any,
            }),

            // The bus's destination signs, on the same layer type and the same rules as
            // the train's above — the positions come straight out of the body frame
            // because a placement's +Y is port and so is the offset applied here.
            //
            // Positions ride BUS_SIZE_SCALE, and so does `getSize`: the whole vehicle is
            // drawn 1.5x life (see XCEL40_SIZE_SCALE), so a sign left at its true metres
            // would be a correctly-placed sign at two-thirds the size of the panel it is
            // supposed to fill.
            new VehicleSideIconLayer<BusSignDatum>({
              id: scopedId('bus-route-signs'),
              data: bdSigns,
              getPosition: ({ bus, placement }) => {
                const br = (bus.bearing * Math.PI) / 180
                const cosLat = Math.cos((bus.position[1] * Math.PI) / 180)
                const forwardOffset = placement.x * BUS_SIZE_SCALE
                const sideOffset = placement.y * BUS_SIZE_SCALE
                return [
                  bus.position[0] + (forwardOffset * Math.sin(br) - sideOffset * Math.cos(br)) / (METRES_PER_DEGREE * cosLat),
                  bus.position[1] + (forwardOffset * Math.cos(br) + sideOffset * Math.sin(br)) / METRES_PER_DEGREE,
                  placement.z * BUS_SIZE_SCALE,
                ] as [number, number, number]
              },
              getIcon: ({ bus, placement }) =>
                createXcel40Sign(placement, bus.routeId, bus.destinationName || ''),
              getBearing: (d) => d.bus.bearing,
              getSide: (d) => d.placement.side,
              getFacing: (d) => d.placement.facing,
              getSize: (d) => d.placement.height * BUS_SIZE_SCALE,
              sizeUnits: 'meters' as const,
              billboard: false,
              parameters: { depthTest: true } as any,
            }),

            // Floating route-id pill in front of each bus: the bus counterpart of the
            // train badge above, so a rider can read the line straight off the map without
            // tapping the bus. One per bus (buses are a single datum). Hidden until zoom 12
            // to keep the denser bus field from turning into a wall of pills, except in
            // `both`, where asking for the label on top of the model is exactly the wall
            // that gate was declining to draw on the rider's behalf.
            new IconLayer<VehicleDatum>({
              id: scopedId('bus-badge'),
              data: zoom >= 12 || alwaysBullet ? bdBadges : [],
              getPosition: (d) => {
                const dist = BHX * BUS_SIZE_SCALE // bus's front bumper, in its facing direction
                const br = (d.bearing * Math.PI) / 180
                const cosLat = Math.cos((d.position[1] * Math.PI) / 180)
                return [
                  d.position[0] + (dist * Math.sin(br)) / (METRES_PER_DEGREE * cosLat),
                  d.position[1] + (dist * Math.cos(br)) / METRES_PER_DEGREE,
                  BUS_LABEL_ALT,
                ] as [number, number, number]
              },
              getIcon: (d) => {
                const b = createBusBadge(d.routeId)
                return { url: b.url, width: b.width, height: b.height }
              },
              getSize: 18,
              sizeUnits: 'pixels' as const,
              parameters: { depthTest: false } as any,
            }),
          ]
          if (collectBenchmarkMetrics) {
            const emptyModelTierLayerCount = modelLayers.reduce(
              (count, layer) => count + (Array.isArray(layer.props.data) && layer.props.data.length === 0 ? 1 : 0),
              0,
            )
            vehicleLayerMetrics = {
              layerBuildMs: 0,
              trainDatumCount: sd.length,
              stickerInstanceCount: sdSigns.length,
              triangles,
              // Filled after deck renders by the luma Model counter integration below.
              drawCalls: null,
              scenegraphLayerCount: modelLayers.length,
              emptyModelTierLayerCount,
              tiers: tierCounts,
            }
          }
          return layers
        }

        const overlay = new MapboxOverlay({
          interleaved: true,
          layers: [],
          ...(TRAIN_LOD_BENCHMARK_BUILD
            ? {
                onAfterRender: () => {
                  if (!getTrainLodBenchmarkSink()) return
                  trainLodDrawCallCounter.observe(resolvedTrainLodScenegraphLayers(overlay as any))
                },
              }
            : {}),
          onClick: (info: any) => {
            if (!info.object) return
            const v = useTransitStore.getState().vehicles[(info.object as VehicleDatum).vehicleId]
            if (v) onVehiclePress?.(v)
          },
          onHover: (info: any) => {
            if (info.object) map.getCanvas().style.cursor = 'pointer'
          },
        })
        map.addControl(overlay)
        deckOverlay = overlay

        // Station click: fires when a stop dot or badge is tapped and no vehicle was hit.
        const STATION_FOCUS_ZOOM = 15.5
        const handleStationClick = (e: any) => {
          if (!e.features?.length) return
          const feature = e.features[0]
          const props = feature.properties as { id: string; name: string; routes: string }

          // Fly to the tapped station so it's centered and prominent. Runs in the
          // same frame the sheet opens (the onStationPress callback below) so the
          // camera move and sheet animation feel simultaneous. Skip the move if the
          // user is already at/closer than the focus zoom to avoid a jarring zoom-out.
          const coords = feature.geometry?.coordinates as [number, number] | undefined
          if (coords && map.getZoom() < STATION_FOCUS_ZOOM) {
            map.flyTo({ center: [coords[0], coords[1]], zoom: STATION_FOCUS_ZOOM, duration: 600 })
          }

          onStationPress?.({
            id: props.id,
            name: props.name,
            routes: props.routes.split(',').filter(Boolean),
          })
        }
        map.on('click', 'stop-circles', handleStationClick)
        map.on('click', 'stop-badges', handleStationClick)
        map.on('mouseenter', 'stop-circles', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'stop-circles', () => { map.getCanvas().style.cursor = '' })
        map.on('mouseenter', 'stop-badges', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'stop-badges', () => { map.getCanvas().style.cursor = '' })

        // Bus-stop click: open the bus-stop sheet (routes serving it + live arrivals).
        const handleBusStopClick = (e: any) => {
          if (!e.features?.length) return
          const feature = e.features[0]
          const props = feature.properties as { id: string; name: string }
          const coords = feature.geometry?.coordinates as [number, number] | undefined
          if (coords && map.getZoom() < STATION_FOCUS_ZOOM) {
            map.flyTo({ center: [coords[0], coords[1]], zoom: STATION_FOCUS_ZOOM, duration: 600 })
          }
          onBusStopPress?.({ id: props.id, name: props.name })
        }
        map.on('click', 'bus-stop-circles', handleBusStopClick)
        map.on('click', 'bus-stop-labels', handleBusStopClick)
        map.on('click', 'bus-stop-badges', handleBusStopClick)
        // The boarded route's own name/badge carry the same `id`/`name`, so they answer the
        // same tap. Registered because they SUPPRESS the ambient pair by collision wherever
        // both cover a stop — without this, boarding a bus would take the tap target away
        // from every stop on the route it drew over.
        map.on('click', 'br-stop-badges', handleBusStopClick)
        map.on('click', 'br-stop-labels', handleBusStopClick)
        map.on('mouseenter', 'bus-stop-circles', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'bus-stop-circles', () => { map.getCanvas().style.cursor = '' })

        // Ferry-landing click: the landing sheet (departures → one boat's timetable).
        const handleFerryLandingClick = (e: any) => {
          if (!e.features?.length) return
          const feature = e.features[0]
          const props = feature.properties as { id: string; name: string }
          const coords = feature.geometry?.coordinates as [number, number] | undefined
          if (coords && map.getZoom() < STATION_FOCUS_ZOOM) {
            map.flyTo({ center: [coords[0], coords[1]], zoom: STATION_FOCUS_ZOOM, duration: 600 })
          }
          onFerryLandingPress?.({ id: props.id, name: props.name })
        }
        map.on('click', 'ferry-landings', handleFerryLandingClick)
        map.on('click', 'ferry-landing-badges', handleFerryLandingClick)
        map.on('mouseenter', 'ferry-landings', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'ferry-landings', () => { map.getCanvas().style.cursor = '' })

        // The ferry network into the map, once loaded. Landing badges reuse the station
        // badge images (`badge-FERRY-ER|FERRY-SB`): `createStopBadge` draws each route from
        // ui/colors, which has the ferry colours registered by the time this runs — the
        // store registers them before it publishes `network`.
        const applyFerryNetwork = () => {
          const network = useFerryStore.getState().network
          if (!network) return
          ;(map.getSource('ferry-routes') as any)?.setData(network.shapes)
          for (const l of network.landings) addBadge(`${SUBWAY_BADGE_PREFIX}${l.routes.join('|')}`)
          ;(map.getSource('ferry-landings') as any)?.setData({
            type: 'FeatureCollection',
            features: network.landings.map((l) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [l.lon, l.lat] },
              properties: { id: l.id, name: l.name, badgeKey: `${SUBWAY_BADGE_PREFIX}${l.routes.join('|')}` },
            })),
          })
        }

        // Rail-station click: the station sheet (departure board → one train's run).
        const handleRailStationClick = (e: any) => {
          if (!e.features?.length) return
          const feature = e.features[0]
          const props = feature.properties as { id: string; name: string }
          const coords = feature.geometry?.coordinates as [number, number] | undefined
          if (coords && map.getZoom() < 13) {
            map.flyTo({ center: [coords[0], coords[1]], zoom: 13.5, duration: 600 })
          }
          onRailStationPress?.({ id: props.id, name: props.name })
        }
        map.on('click', 'rail-stations', handleRailStationClick)
        map.on('click', 'rail-station-labels', handleRailStationClick)
        map.on('mouseenter', 'rail-stations', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'rail-stations', () => { map.getCanvas().style.cursor = '' })

        // Both railroads' network into the map, once loaded: track lines, stations, and the
        // per-branch paths the trains glide along.
        const applyRailNetwork = () => {
          const network = useRailStore.getState().network
          if (!network) return
          ;(map.getSource('rail-routes') as any)?.setData(network.shapes)
          ;(map.getSource('rail-stations') as any)?.setData({
            type: 'FeatureCollection',
            features: network.stations.map((st) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [st.lon, st.lat] },
              properties: { id: st.id, name: st.name },
            })),
          })
          const paths = new Map<string, RailPath[]>()
          for (const f of network.shapes.features) {
            const list = paths.get(f.properties.routeId) ?? []
            list.push(buildRailPath(f.geometry.coordinates))
            paths.set(f.properties.routeId, list)
          }
          railPaths = paths
          // Glides hold a reference to the old paths; let them re-place on the new ones.
          railGlides.clear()
        }

        // NJ Transit bus-stop click: the stop sheet (departure board → one bus's run).
        const handleNjBusStopClick = (e: any) => {
          if (!e.features?.length) return
          const props = e.features[0].properties as { id: string; name: string }
          onNjBusStopPress?.({ id: props.id, name: props.name })
        }
        map.on('click', 'njbus-stop-circles', handleNjBusStopClick)
        map.on('click', 'njbus-stop-labels', handleNjBusStopClick)
        map.on('mouseenter', 'njbus-stop-circles', () => { map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', 'njbus-stop-circles', () => { map.getCanvas().style.cursor = '' })

        // NJ Transit's network into the map, once loaded: route lines, stops, and the per-route
        // paths the buses glide along.
        const applyNjBusNetwork = () => {
          const network = useNjBusStore.getState().network
          if (!network) return
          ;(map.getSource('njbus-routes') as any)?.setData(network.shapes)
          ;(map.getSource('njbus-stops') as any)?.setData({
            type: 'FeatureCollection',
            features: network.stops.map((st) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [st.lon, st.lat] },
              properties: { id: st.id, name: st.name },
            })),
          })
          const paths = new Map<string, RailPath[]>()
          const dirPaths = new Map<string, RailPath[]>()
          const add = (into: Map<string, RailPath[]>, key: string, path: RailPath) => {
            const list = into.get(key)
            if (list) list.push(path)
            else into.set(key, [path])
          }
          for (const f of network.shapes.features) {
            const path = buildRailPath(f.geometry.coordinates)
            add(paths, f.properties.routeId, path)
            // A server predating per-direction lines sends no `direction`: every bus then picks
            // among the route's lines by bearing.
            if (f.properties.direction) add(dirPaths, `${f.properties.routeId}|${f.properties.direction}`, path)
          }
          njBusPaths = paths
          njBusDirPaths = dirPaths
          njBusStopCoords = new Map(network.stops.map((st) => [st.id, { lon: st.lon, lat: st.lat }]))
          njBusCallCache = new WeakMap()
          njBusGlides.clear()
        }

        // Apply pre-parsed shape data to the map and routeShapes instantly (no network call).
        const applyShapeData = (cache: ShapeCache) => {
          routeShapes = cache.canonicalShapes
          segCache.clear()
          anim.clear()
          // The trails were recorded against the OLD geometry (or, on first load, against the
          // straight-line stop-to-stop fallback used while no shapes existed). Keeping them
          // leaves the follower cars bent along a path the train no longer runs, and the
          // re-seed check only fires on a > RENDER_SNAP_M leap, which a chord→track correction
          // is not. Drop them so each live train re-seeds its tail on the real curve next frame.
          trails.clear()
          ;(map.getSource('route-lines') as any).setData(cache.colored)
        }

        // Apply pre-fetched stops data to the map instantly.
        const applyStopsData = (stops: StopsCache) => {
          const seen = new Set<string>()
          for (const stop of stops) {
            const key = stop.routes.join('|')
            if (!seen.has(key)) {
              seen.add(key)
              addBadge(`${SUBWAY_BADGE_PREFIX}${key}`)
            }
          }
          ;(map.getSource('stops') as any).setData({
            type: 'FeatureCollection',
            features: stops.map((s) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
              properties: {
                id: s.id,
                name: s.name,
                badgeKey: `badge-${s.routes.join('|')}`,
                // Read by the label offset above so a two-row badge doesn't sit on the name.
                badgeRows: stopBadgeRows(s.routes),
                routes: s.routes.join(','),
                // Kept as an array so the selected-line station filter can do exact
                // membership tests (the comma string above feeds the click handler).
                routesArr: s.routes,
              },
            })),
          })
          // Mirror exactly what's drawn (correct schedule mode) into the shared store so
          // the nearest-stations scan reads the same set without a second fetch.
          useStopsStore.getState().setStops(
            stops.map((s) => ({
              id: s.id, name: s.name, lat: s.lat, lon: s.lon,
              routes: s.routes,
              // The map draws `routes` (what stops here); the timetable's transfer badges
              // want `transferRoutes` (what you can reach) and `limitedTransfers` (what
              // you can reach while the passage is open).
              transferRoutes: s.transferRoutes,
              limitedTransfers: s.limitedTransfers,
              memberIds: s.memberIds,
            })),
          )
        }

        const loadStops = (attempt = 0, schedule: ScheduleMode = activeSchedule) => {
          const schedParam = schedule !== 'weekday' ? `?schedule=${schedule}` : ''
          fetch(`${BASE_URL}/api/stops/with-routes${schedParam}`)
            .then((r) => r.json())
            .then((data: { stops: StopsCache }) => {
              if (!data.stops?.length && attempt < 8) {
                setTimeout(() => loadStops(attempt + 1, schedule), 3000)
                return
              }
              scheduleStopsData[schedule] = data.stops
              if (activeSchedule === schedule) applyStopsData(data.stops)
            })
            .catch(() => {
              if (attempt < 8) setTimeout(() => loadStops(attempt + 1, schedule), 3000)
            })
        }
        loadStops(0, activeSchedule)

        // Bus stops are fetched on demand (not at startup): the first time the bus or
        // "all" filter is selected. busStopsLoaded guards against duplicate fetches and
        // is reset on failure so a retry can still fire.
        let busStopsLoaded = false
        const loadBusStops = (attempt = 0) => {
          if (busStopsLoaded) return
          busStopsLoaded = true
          fetch(`${BASE_URL}/api/bus-stops`)
            .then((r) => r.json())
            .then(
              (data: {
                stops: Array<{ id: string; name: string; lat: number; lon: number; routes?: string[] }>
              }) => {
                if (!data.stops?.length) {
                  busStopsLoaded = false
                  if (attempt < 8) setTimeout(() => loadBusStops(attempt + 1), 3000)
                  return
                }
                // Register one route-pill badge image per unique route-set (built once, reused
                // across every stop sharing that set) so the bus-stop-badges layer can show the
                // lines serving each stop. Stops the feed lists no routes for get badgeKey ''.
                const seen = new Set<string>()
                for (const s of data.stops) {
                  const routes = s.routes ?? []
                  if (!routes.length) continue
                  const key = routes.join('|')
                  if (seen.has(key)) continue
                  seen.add(key)
                  addBadge(`${BUS_BADGE_PREFIX}${key}`)
                }
                ;(map.getSource('bus-stops') as any)?.setData({
                  type: 'FeatureCollection',
                  features: data.stops.map((s) => ({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
                    properties: {
                      id: s.id,
                      name: s.name,
                      badgeKey: (s.routes ?? []).length ? `bus-badge-${(s.routes ?? []).join('|')}` : '',
                      // Kept as an array so the selected-bus-line stop filter can do exact
                      // membership tests ('M15' must not match 'M15-SBS').
                      routes: s.routes ?? [],
                    },
                  })),
                })
                // Build the id→coord lookup and refresh the boarded-route highlight, which
                // may have rendered empty if a bus was boarded before coords finished loading.
                busStopCoords.clear()
                busStopRoutes.clear()
                for (const s of data.stops) {
                  busStopCoords.set(s.id, [s.lon, s.lat])
                  if (s.routes?.length) busStopRoutes.set(s.id, s.routes)
                }
                updateBoardedRoute()
                // Mirror into the shared store so the nearby-stations panel can list bus
                // stops with the lines serving each one (the /api/bus-stops feed carries
                // routes[] per stop).
                useStopsStore.getState().setBusStops(
                  data.stops.map((s) => ({
                    id: s.id, name: s.name, lat: s.lat, lon: s.lon, routes: s.routes ?? [],
                  })),
                )
              },
            )
            .catch(() => {
              busStopsLoaded = false
              if (attempt < 8) setTimeout(() => loadBusStops(attempt + 1), 3000)
            })
        }

        // Bus route shapes: lazy-loaded alongside bus stops the first time buses are
        // shown (or a bus is boarded). Grouped routeId → array of directional polylines.
        let busShapesLoaded = false
        const loadBusRouteShapes = (attempt = 0) => {
          if (busShapesLoaded) return
          busShapesLoaded = true
          fetch(`${BASE_URL}/api/bus-routes/shapes`)
            .then((r) => r.json())
            .then((geojson: any) => {
              if (!geojson.features?.length) {
                busShapesLoaded = false
                if (attempt < 8) setTimeout(() => loadBusRouteShapes(attempt + 1), 3000)
                return
              }
              busRouteShapes.clear()
              for (const f of geojson.features) {
                const rid = f.properties?.routeId as string
                if (!rid) continue
                const coords = f.geometry?.coordinates as [number, number][]
                if (!coords || coords.length < 2) continue
                const dir = String(f.properties?.direction ?? '')
                const arr = busRouteShapes.get(rid)
                if (arr) arr.push({ dir, coords })
                else busRouteShapes.set(rid, [{ dir, coords }])
              }
              // The boarded highlight may have drawn straight stop-to-stop before shapes
              // landed: redraw it now that the real route geometry is available. Same for
              // the selection highlight, which renders empty until shapes exist.
              updateBoardedRoute()
              updateSelectedBusRoutes()
            })
            .catch(() => {
              busShapesLoaded = false
              if (attempt < 8) setTimeout(() => loadBusRouteShapes(attempt + 1), 3000)
            })
        }

        // Redraw the selected-bus-route highlight: one colored polyline set per bus line
        // currently selected in the Routes panel or ridden by the active trip plan (a
        // planned bus leg needs its full route drawn, since buses have no base route-lines
        // layer like the subway's, so the rider can board any vehicle of the route).
        // Cleared when no bus lines are selected. Shapes may not have loaded yet
        // (they're lazy); loadBusRouteShapes re-invokes this once they land, so a
        // selection made early still gets its highlight.
        const updateSelectedBusRoutes = () => {
          const src = map.getSource('selected-bus-routes') as any
          if (!src) return
          const features: any[] = []
          for (const rid of focusedRouteIds(useTransitStore.getState())) {
            if (isSubwayRoute(rid)) continue
            const shapes = busRouteShapes.get(rid)
            if (!shapes) continue
            const color = SELECTED_BUS_ROUTE_COLOR
            for (const poly of shapes) {
              features.push({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: poly.coords },
                properties: { color },
              })
            }
          }
          src.setData({ type: 'FeatureCollection', features })
        }

        // Layer visibility is the AND of two independent controls:
        //
        //   transitFilter:  which vehicles exist at all (any mix of subway / bus / ferry)
        //   useLayersStore: which overlays are drawn about them
        //
        // A layer shows only when the filter admits its transit type and the user has the
        // overlay switched on, so neither control can silently override the other.
        const applyLayerVisibility = () => {
          const state = useTransitStore.getState()
          const { transitFilter } = state
          const layers = useLayersStore.getState()
          // Positive tests, one per mode: the filter is a set, so "not bus" is not "subway".
          const subwayAllowed = showsMode(transitFilter, 'subway')
          const busAllowed = showsMode(transitFilter, 'bus')
          const ferryAllowed = showsMode(transitFilter, 'ferry')

          // Lines picked in the Routes panel plus the active trip plan's rides, split
          // by type. A selection overrides the Subway/Bus gate for route highlights:
          // the map shows exactly the selected lines' routes (and nothing else),
          // whichever pill is active.
          const focusIds = focusedRouteIds(state)
          const selSubway: string[] = []
          const selBus: string[] = []
          for (const id of focusIds) {
            if (isSubwayRoute(id)) selSubway.push(id)
            else selBus.push(id)
          }
          const hasSelection = focusIds.size > 0

          // Lazy loads are keyed on the FILTER, not on the bus-stop toggle: the boarded-bus
          // route highlight needs bus shapes and stop coords even with that layer switched off.
          if (busAllowed) {
            loadBusStops()
            loadBusRouteShapes()
          }
          // A selected bus line needs its shape for the selection highlight and its stops
          // even on a subway-filtered session (selection overrides the gate).
          if (selBus.length > 0) {
            loadBusStops()
            loadBusRouteShapes()
          }

          const set = (ids: string[], on: boolean) => {
            const vis = on ? 'visible' : 'none'
            for (const id of ids) {
              if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis)
            }
          }

          // With a selection active the subway route layers show only the selected lines
          // (filter below) even when the pill is on Bus or the route-lines overlay is
          // off, because a selection is an explicit "show me this line" ask, like the
          // boarded-route highlight. Selecting only bus lines hides them entirely.
          const rlFilter =
            hasSelection && selSubway.length > 0
              ? (['in', ['get', 'routeId'], ['literal', selSubway]] as any)
              : null
          for (const id of ['rl-casing', 'rl-fill']) {
            if (map.getLayer(id)) map.setFilter(id, rlFilter)
          }
          set(['rl-casing', 'rl-fill'], hasSelection ? selSubway.length > 0 : subwayAllowed && layers.routeLines)
          // Buildings are the only layer here the transit filter has no say over: they are
          // the city, not a vehicle type, and a rider on a subway-only session looking at
          // a station entrance wants the same block the bus rider does.
          set([BUILDINGS_LAYER_ID], layers.buildings)
          // Subway stations follow the same rule as bus stops below: with subway lines
          // selected, only the stations those lines serve remain (dots + badges); the
          // selected-station pulse/core layers keep their own id filter and just follow
          // the group's visibility.
          const stationSel = hasSelection && selSubway.length > 0
          const stationSelFilter = stationSel
            ? (['any', ...selSubway.map((r) => ['in', r, ['get', 'routesArr']])] as any)
            : null
          if (map.getLayer('stop-circles')) map.setFilter('stop-circles', stationSelFilter)
          if (map.getLayer('stop-badges')) map.setFilter('stop-badges', stationSelFilter)
          set(
            ['stop-circles', 'stop-badges', 'stop-selected-pulse', 'stop-selected-core'],
            hasSelection ? stationSel : subwayAllowed && layers.stations,
          )

          // Bus stops. With bus lines selected, show exactly the stops those lines serve:
          // dots plus the route-pill badges saying which lines stop there, regardless of
          // the pill or the bus-stops overlay toggle (same "explicit ask" rule as the
          // route highlights). Exact membership test on the stop's routes array, so
          // selecting M15 doesn't light up M15-SBS-only stops. The badge/circle minzooms
          // exist to keep the citywide field of ~13k stops readable; a single line's stops
          // are sparse, so they're relaxed below the default zoom while filtered.
          const busStopSel = hasSelection && selBus.length > 0
          const busStopSelFilter = busStopSel
            ? (['any', ...selBus.map((r) => ['in', r, ['get', 'routes']])] as any)
            : null
          if (map.getLayer('bus-stop-circles')) map.setFilter('bus-stop-circles', busStopSelFilter)
          if (map.getLayer('bus-stop-labels')) map.setFilter('bus-stop-labels', busStopSelFilter)
          if (map.getLayer('bus-stop-badges')) {
            map.setFilter(
              'bus-stop-badges',
              busStopSelFilter ? ['all', BUS_BADGE_BASE_FILTER, busStopSelFilter] : BUS_BADGE_BASE_FILTER,
            )
          }
          if (map.getLayer('bus-stop-circles')) map.setLayerZoomRange('bus-stop-circles', busStopSel ? 10 : 12, 24)
          if (map.getLayer('bus-stop-badges')) map.setLayerZoomRange('bus-stop-badges', busStopSel ? 12 : 14, 24)
          // Stop names ride along with the badges while filtered: relax the layer's
          // minzoom AND swap the size curve (whose default is 0 below zoom 15). The name
          // also drops below the route-pill badge (16px tall, 4px under the dot):
          // at the default 2.2em offset the two symbol layers collide and the badge
          // wins placement, leaving most stops nameless.
          if (map.getLayer('bus-stop-labels')) {
            map.setLayerZoomRange('bus-stop-labels', busStopSel ? 12 : 15, 24)
            map.setLayoutProperty(
              'bus-stop-labels', 'text-size',
              busStopSel ? BUS_LABEL_SIZE_SELECTED : BUS_LABEL_SIZE_DEFAULT,
            )
            map.setLayoutProperty('bus-stop-labels', 'text-offset', busStopSel ? [0, 3.4] : [0, 2.2])
          }
          set(
            ['bus-stop-circles', 'bus-stop-labels', 'bus-stop-badges',
             'bus-stop-selected-pulse', 'bus-stop-selected-core'],
            hasSelection ? busStopSel : busAllowed && layers.busStops,
          )

          // Ferries ride the same two base toggles as the subway (route lines, stations),
          // and hide under a Routes-panel selection like every unselected line does. The
          // network is loaded lazily, the first time the filter admits ferries.
          if (ferryAllowed) useFerryStore.getState().ensureLoaded()
          set(['ferry-route-casing', 'ferry-route-fill'], !hasSelection && ferryAllowed && layers.routeLines)
          set(
            ['ferry-landings', 'ferry-landing-badges', 'ferry-landing-selected'],
            !hasSelection && ferryAllowed && layers.stations,
          )

          // LIRR + Metro-North: the same two toggles and the same selection rule. Also lazy.
          const railAllowed = showsMode(transitFilter, 'rail')
          if (railAllowed) useRailStore.getState().ensureLoaded()
          set(['rail-route-casing', 'rail-route-fill'], !hasSelection && railAllowed && layers.routeLines)
          set(
            ['rail-stations', 'rail-station-labels', 'rail-station-selected'],
            !hasSelection && railAllowed && layers.stations,
          )

          // NJ Transit buses: the same two toggles and the same selection rule. Also lazy.
          const njBusAllowed = showsMode(transitFilter, 'njbus')
          if (njBusAllowed) useNjBusStore.getState().ensureLoaded()
          set(['njbus-route-lines'], !hasSelection && njBusAllowed && layers.routeLines)
          set(
            ['njbus-stop-circles', 'njbus-stop-labels', 'njbus-stop-selected'],
            !hasSelection && njBusAllowed && layers.stations,
          )
        }

        // Redraw the boarded-bus route highlight from the rider store. Clears when not
        // riding a bus; otherwise plots a line through the bus's upcoming stops (looked up
        // in busStopCoords) plus an emphasized dot per stop, in the route's color.
        // Triggers the lazy bus-stop load so coords exist even on a subway-filtered
        // session; loadBusStops re-invokes this once coords land.
        const updateBoardedRoute = () => {
          const src = map.getSource('boarded-route') as any
          if (!src) return
          const rs = useRiderStore.getState()
          const v = rs.trackedVehicle
          if (!rs.isRiding || !v || v.type !== 'bus') {
            src.setData({ type: 'FeatureCollection', features: [] })
            return
          }
          loadBusStops()
          loadBusRouteShapes()
          const color = getRouteColor(v.routeId)
          const features: any[] = []

          // Route line: prefer the real GTFS route shape(s) (drawn like the subway's
          // highlighted route). Show ONLY the direction the boarded bus is travelling
          // (SIRI DirectionRef) so an uptown rider sees the uptown line, not both. If the
          // direction can't be matched, fall back to all of the route's shapes. Fall back
          // to a straight line through the bus's known stops if no shape loaded/matched.
          const allShapes = busRouteShapes.get(v.routeId)
          const dirShapes =
            allShapes && v.directionRef != null ? allShapes.filter((s) => s.dir === v.directionRef) : []
          const shapes = dirShapes.length > 0 ? dirShapes : allShapes
          if (shapes && shapes.length > 0) {
            for (const poly of shapes) {
              features.push({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: poly.coords },
                properties: { color },
              })
            }
          } else {
            const line: [number, number][] = []
            for (const s of rs.upcomingStops) {
              const c = busStopCoords.get(s.stopId)
              if (c) line.push(c)
            }
            if (line.length >= 2) {
              features.push({
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: line },
                properties: { color },
              })
            }
          }

          // Stop dots: the bus's actual upcoming stops, emphasized over the route line, each
          // carrying what `br-stop-{badges,labels}` draw beneath it — the lines that stop
          // there and the stop's own name.
          //
          // The onward calls carry a stop NAME but no routes, and the /api/bus-stops feed
          // carries routes but is keyed by id, so the badge is looked up while the name is
          // taken from the ride itself: the two disagree on wording sometimes, and the rider
          // is reading this against the panel's list, which is the onward-call name.
          for (const s of rs.upcomingStops) {
            const c = busStopCoords.get(s.stopId)
            if (!c) continue
            const routes = busStopRoutes.get(s.stopId)
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: c },
              properties: {
                color,
                id: s.stopId,
                name: s.stopName,
                // Same key space as the ambient bus badges, so the image the lazy load
                // already registered is reused rather than drawn a second time.
                badgeKey: routes?.length ? `${BUS_BADGE_PREFIX}${routes.join('|')}` : '',
              },
            })
          }
          src.setData({ type: 'FeatureCollection', features })
        }

        const loadShapes = (attempt = 0, schedule: ScheduleMode = activeSchedule) => {
          const schedParam = schedule !== 'weekday' ? `?schedule=${schedule}` : ''
          fetch(`${BASE_URL}/api/routes/shapes${schedParam}`)
            .then((r) => r.json())
            .then((geojson: any) => {
              if (!geojson.features?.length && attempt < 8) {
                setTimeout(() => loadShapes(attempt + 1, schedule), 3000)
                return
              }
              // Canonicalize each route exactly once. Both consumers below retain these
              // same coordinate arrays: MapLibre paints them and train matching/sampling
              // follows them. Raw GTFS remains only the immutable upstream input.
              const canonicalShapes = new Map<string, [number, number][][]>()
              const colored = {
                type: 'FeatureCollection' as const,
                features: geojson.features
                  .filter((f: any) => !(f.properties.routeId in ROUTE_ALIAS))
                  .map((f: any) => {
                    const rid = f.properties.routeId as string
                    const baseRid = ROUTE_ALIAS[rid] ?? rid
                    const coordinates = canonicalRouteLineGeometry(f.geometry.coordinates)
                    const routeGeometry = canonicalShapes.get(rid)
                    if (routeGeometry) routeGeometry.push(coordinates)
                    else canonicalShapes.set(rid, [coordinates])
                    return {
                      ...f,
                      geometry: {
                        ...f.geometry,
                        coordinates,
                      },
                      properties: {
                        ...f.properties,
                        color: getRouteColor(rid),
                        offset: ROUTE_OFFSETS[baseRid] ?? 0,
                      },
                    }
                  })
                  .sort((a: any, b: any) => Math.abs(a.properties.offset) - Math.abs(b.properties.offset)),
              }
              scheduleShapeData[schedule] = { canonicalShapes, colored }
              if (activeSchedule === schedule) {
                routeShapes = canonicalShapes
                segCache.clear()
                anim.clear()
                trails.clear() // recorded against the pre-shapes straight-line fallback; see applyShapeData
                ;(map.getSource('route-lines') as any).setData(colored)
              }
            })
            .catch(() => {
              if (attempt < 8) setTimeout(() => loadShapes(attempt + 1, schedule), 3000)
            })
        }
        loadShapes(0, activeSchedule)

        // Pre-fetch inactive schedules in the background after initial schedule settles so
        // switching schedule modes in dev is instant.
        if (import.meta.env.DEV) {
          setTimeout(() => {
            const allModes: ScheduleMode[] = ['weekday', 'saturday', 'sunday']
            for (const mode of allModes) {
              if (!scheduleShapeData[mode]) loadShapes(0, mode)
              if (!scheduleStopsData[mode]) loadStops(0, mode)
            }
          }, 4000)
        }

        fetch(`${BASE_URL}/api/stops/coords`)
          .then((r) => r.json())
          .then((coords: Record<string, { lat: number; lon: number }>) => {
            stopCoords = coords
          })
          .catch(() => {})

        mapControls.resetView = () =>
          map.flyTo({ center: NYC_CENTER, zoom: NYC_ZOOM, pitch: 30, duration: 1000 })
        mapControls.flyTo = (lat, lon) => map.flyTo({ center: [lon, lat], zoom: 15, duration: 1000 })
        mapControls.reloadWithSchedule = (schedule: ScheduleMode) => {
          activeSchedule = schedule

          const shapesCache = scheduleShapeData[schedule]
          if (shapesCache) {
            applyShapeData(shapesCache)
          } else {
            ;(map.getSource('route-lines') as any).setData({ type: 'FeatureCollection', features: [] })
            loadShapes(0, schedule)
          }

          const stopsCache = scheduleStopsData[schedule]
          if (stopsCache) {
            applyStopsData(stopsCache)
          } else {
            ;(map.getSource('stops') as any).setData({ type: 'FeatureCollection', features: [] })
            loadStops(0, schedule)
          }
        }
        mapControls.clearScheduleCache = () => {
          scheduleShapeData = {}
          scheduleStopsData = {}
          const active = activeSchedule
          ;(map.getSource('route-lines') as any).setData({ type: 'FeatureCollection', features: [] })
          ;(map.getSource('stops') as any).setData({ type: 'FeatureCollection', features: [] })
          loadShapes(0, active)
          loadStops(0, active)
        }

        // Live-glide a bus between its consecutive feed positions so it moves continuously
        // instead of teleporting each ~20 s poll: the road-vehicle counterpart of the
        // subway's frozen-segment glide. The returned position is a pure function of the
        // wall clock against the segment's frozen endpoints + window, so calling it at the
        // ~30 fps setData cadence (rather than every rAF) introduces no drift. Each new fix
        // starts a fresh segment from the CURRENT interpolated position (never the raw feed
        // lat/lon), so hand-offs never snap; the window is sized to the real fix gap so the
        // bus is still gliding when the next fix lands and never freezes mid-route.
        // Glide-window sizing. The window each segment glides over is the MEASURED gap
        // since the bus's previous fix, stretched by GLIDE_SLACK so the bus is still in
        // motion when the next fix lands: the bus never reaches its target early and
        // freezes (the old fixed-20 s window stalled buses whenever a fix ran late). The
        // gap is clamped so one missing poll can't make a bus crawl for a minute.
        const BUS_GLIDE_DEFAULT = 22 // first segment, before any gap has been measured
        const BUS_GLIDE_SLACK = 1.25
        const BUS_GLIDE_MIN = 8
        // The cap must stay ABOVE the real client-visible delivery cadence, or every bus's
        // glide finishes before the next (synchronized) snapshot lands and the whole fleet
        // freezes then restarts in lockstep. Snapshots nominally arrive every 20 s
        // (BUS_POLL_INTERVAL_MS) but in practice every ~25-40 s under SIRI latency + the bus
        // worker sharing concurrency with the 15 s subway poll. With slack, a 60 s gap sizes a
        // 75 s window, so for any gap up to the TTL prune horizon the window outlasts the gap
        // and each bus keeps gliding continuously at its own pace until its next fix arrives.
        const BUS_GLIDE_MAX = 75 // ≈ VEHICLE_TTL_MS; beyond this a bus is pruned + re-seeded fresh
        // A bus must move at least this far between fixes for the move to update FACING
        // (route snap / heading); below it the move is treated as GPS jitter and the bus
        // holds its last facing so it doesn't spin on noise. Position still glides to every
        // fix regardless, so the bus is always moving smoothly, never snapping or freezing.
        const BUS_FACE_MIN_M = 15
        // Max combined squared-degree distance (both fixes to their nearest shape vertex)
        // for a bus to be considered "on" a route shape. ~90 m off-route per fix → snap;
        // farther → the route id didn't match or the GPS is wild, fall back to straight-line.
        const BUS_SHAPE_TOL_SQ = 0.0008 * 0.0008 * 2

        // Facing for a bus from its ROUTE GEOMETRY rather than movement: this is what
        // orients a stopped bus correctly (the SIRI Bearing field is too noisy to trust,
        // and a parked bus has no movement vector). Picks the route's polyline for the bus's
        // GTFS direction (DirectionRef), finds the nearest point, and returns the shape's
        // tangent there, i.e. the way the road runs in the bus's direction of travel.
        // When DirectionRef doesn't match a known direction, it falls back to the nearest
        // polyline of either direction and disambiguates the 180° with the feed Bearing.
        // Returns null when the route has no loaded shape.
        const routeFacing = (v: VehiclePosition, lon: number, lat: number): number | null => {
          const entries = busRouteShapes.get(v.routeId)
          if (!entries || entries.length === 0) return null
          const dirMatch = v.directionRef != null ? entries.filter((e) => e.dir === v.directionRef) : []
          const directional = dirMatch.length > 0
          const candidates = directional ? dirMatch : entries

          const cosLat = Math.cos((lat * Math.PI) / 180)
          let bestCoords: [number, number][] | null = null
          let bestIdx = 0,
            bestD = Infinity
          for (const e of candidates) {
            const i = closestShapeIdx(e.coords, lon, lat)
            const dx = (e.coords[i][0] - lon) * cosLat,
              dy = e.coords[i][1] - lat
            const d = dx * dx + dy * dy
            if (d < bestD) {
              bestD = d
              bestIdx = i
              bestCoords = e.coords
            }
          }
          if (!bestCoords) return null

          const j = bestIdx < bestCoords.length - 1 ? bestIdx + 1 : bestIdx
          const i = j === bestIdx ? bestIdx - 1 : bestIdx
          if (i < 0) return null
          let bearing = calcBearing(bestCoords[i][0], bestCoords[i][1], bestCoords[j][0], bestCoords[j][1])
          // The directional shape is drawn in the travel direction, so its tangent is the
          // facing. For a non-directional fallback, flip 180° if the opposite way sits
          // closer to the feed Bearing (a coarse but useful tie-breaker).
          if (!directional && Number.isFinite(v.bearing) && v.bearing !== 0) {
            // Angular gap between the tangent and the feed Bearing, in [0,180].
            const diff = Math.abs(((bearing - v.bearing + 540) % 360) - 180)
            if (diff > 90) bearing = (bearing + 180) % 360 // tangent points the "wrong" way → flip
          }
          return bearing
        }

        // 3D bus facing, best source first: the route tangent for the bus's direction
        // (works moving OR stopped, see routeFacing) → route slice tangent while gliding →
        // movement vector → feed Bearing. `g.heading` carries the last good facing and is
        // held while the bus is stopped so it never snaps to a noisy direction.
        const busGlide = (v: VehiclePosition, now: number): { lon: number; lat: number; bearing: number } => {
          let g = busGlides.get(v.vehicleId)
          // Stale glide (see STALE_STATE_GAP_S): this bus wasn't rendered for a while
          // (hidden tab / filtered out / pruned-and-returned), so its segment window and
          // last interpolated position are old news. Re-seed at the live fix instead of
          // gliding from wherever it was when rendering stopped.
          if (g && now - g.sampledAt > STALE_STATE_GAP_S) {
            busGlides.delete(v.vehicleId)
            g = undefined
          }
          if (!g) {
            // First sighting: render at the feed fix. Seed facing from the route geometry
            // (correct even though the bus may be parked); fall back to the feed Bearing.
            const rf0 = routeFacing(v, v.longitude, v.latitude)
            const seg: BusGlideSeg = {
              fromLon: v.longitude, fromLat: v.latitude,
              toLon: v.longitude, toLat: v.latitude,
              t0: now, t1: now,
              lon: v.longitude, lat: v.latitude,
              heading: rf0 ?? v.bearing,
              track: null,
              moved: false,
              routeFaced: rf0 != null,
              lastFixAt: now,
              lastFixTs: v.timestamp ?? 0,
              sampledAt: now,
            }
            busGlides.set(v.vehicleId, seg)
            return { lon: seg.lon, lat: seg.lat, bearing: seg.heading }
          }

          // A bus seeded from the feed Bearing before its route shape had loaded re-derives
          // its facing from the route geometry as soon as the shape is available: runs the
          // shape scan at most once per bus (until it succeeds), then never again.
          if (!g.moved && !g.routeFaced) {
            const rf = routeFacing(v, g.lon, g.lat)
            if (rf != null) {
              g.heading = rf
              g.routeFaced = true
            }
          }

          const cosLat = Math.cos((v.latitude * Math.PI) / 180)
          const targetMovedM =
            Math.hypot((v.longitude - g.toLon) * cosLat, v.latitude - g.toLat) * 111_320
          // Out-of-order / stale-fix guard. MTA's VehicleMonitoring feed intermittently
          // replays an OLDER position (a cached fix from a different upstream server) AFTER a
          // newer one. Adopting it would glide the bus BACKWARD: the visible stutter. Reject
          // any fix whose RecordedAtTime predates the fix we're already gliding to. Escape
          // hatch: if we've been starved of a fresh fix for longer than two max windows, take
          // the next fix regardless so one bad future-dated stamp can't freeze a bus forever.
          const staleFix =
            v.timestamp != null &&
            g.lastFixTs > 0 &&
            v.timestamp < g.lastFixTs &&
            now - g.lastFixAt < BUS_GLIDE_MAX * 2
          // Only re-segment on a move large enough to be real, not GPS jitter. targetMovedM
          // is measured against the LAST ADOPTED fix (g.toLon/g.toLat) and so accumulates
          // across held frames: a genuinely creeping bus still adopts once it has covered
          // BUS_FACE_MIN_M, while sub-threshold jitter (forward OR backward) is ignored so it
          // can never nudge the model off its smooth glide. Held offsets stay within the
          // model's own ~58 m footprint, so the displayed position still reads as live.
          if (targetMovedM > BUS_FACE_MIN_M && !staleFix) {
            // A new GPS fix landed. Size this segment's window from the real gap since the
            // last fix (stretched) so the bus keeps moving until the next fix lands instead
            // of finishing early and freezing.
            const gap = now - g.lastFixAt
            g.lastFixAt = now
            g.lastFixTs = v.timestamp ?? g.lastFixTs
            const window = Math.max(
              BUS_GLIDE_MIN,
              Math.min(BUS_GLIDE_MAX, gap > 0 ? gap * BUS_GLIDE_SLACK : BUS_GLIDE_DEFAULT),
            )

            // Distance the bus actually travelled from where it is right now to the new fix.
            // Drives FACING only; position always glides to the fix below, no snapping.
            const segMovedM = Math.hypot((v.longitude - g.lon) * cosLat, v.latitude - g.lat) * 111_320
            let track: ShapeSeg | null = null
            if (segMovedM >= BUS_FACE_MIN_M) {
              // Genuine movement → update facing and, when the route shape covers this
              // stretch, snap the glide onto the road so the bus follows the route and faces
              // its tangent. matchSegmentInShapes orients the slice in the travel direction.
              const entries = busRouteShapes.get(v.routeId)
              if (entries) {
                const m = matchSegmentInShapes(
                  entries.map((e) => e.coords),
                  { lon: g.lon, lat: g.lat },
                  { lon: v.longitude, lat: v.latitude },
                )
                if (m.seg && m.seg.pts.length >= 2 && m.dist <= BUS_SHAPE_TOL_SQ) {
                  // Re-anchor the matched slice to [current rendered pos … true fix] so the
                  // glide starts where the model is (no vertex snap) and ends on the real
                  // location, following the route arc in between.
                  track = anchorGlideTrack(m.seg, g.lon, g.lat, v.longitude, v.latitude)
                }
              }
              g.moved = true
              if (!track) g.heading = calcBearing(g.lon, g.lat, v.longitude, v.latitude)
            }
            // ALWAYS glide from the current interpolated position to the new fix: for a
            // jitter-sized move this is an imperceptibly slow drift (facing held), for a real
            // move it's the full glide. Never a snap, never a freeze.
            g.track = track
            g.fromLon = g.lon
            g.fromLat = g.lat
            g.toLon = v.longitude
            g.toLat = v.latitude
            g.t0 = now
            g.t1 = now + window
          }

          const span = g.t1 - g.t0
          const p = span > 0 ? Math.max(0, Math.min(1, (now - g.t0) / span)) : 1
          let lon: number, lat: number, bearing: number
          if (g.track && g.track.pts.length >= 2) {
            const s = sampleSeg(g.track, p)
            lon = s[0]
            lat = s[1]
            g.heading = bearingAtT(g.track, p) // remember tangent so facing holds when stopped
            bearing = g.heading
          } else {
            lon = g.fromLon + (g.toLon - g.fromLon) * p
            lat = g.fromLat + (g.toLat - g.fromLat) * p
            bearing = g.heading
          }
          // Guard: never emit a non-finite position/heading; that would drop the model for
          // a frame (a flicker / disappearance). Fall back to the raw feed fix.
          if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
            lon = v.longitude
            lat = v.latitude
          }
          if (!Number.isFinite(bearing)) bearing = Number.isFinite(v.bearing) ? v.bearing : 0
          g.lon = lon
          g.lat = lat
          g.sampledAt = now
          return { lon, lat, bearing }
        }

        // ── Ferries ────────────────────────────────────────────────────────────────
        // Their own layers, rebuilt with the fleet's on every deck submission and appended
        // beside them. ~20 boats, so none of the fleet's pooling or culling is needed: this
        // allocates one small array a frame. Glide state advances HERE, on the same wall
        // clock, so a boat moves whether or not any train is being drawn.
        type FerryDatum = {
          vehicleId: string
          routeId: string
          position: [number, number]
          bearing: number
          color: [number, number, number, number]
        }
        const buildFerryLayers = (): any[] => {
          if (!ferries.length) return []
          const nowSec = Date.now() / 1000
          const dt = ferryGlideLastNow ? Math.min(0.5, Math.max(0, nowSec - ferryGlideLastNow)) : 0
          ferryGlideLastNow = nowSec
          const data: FerryDatum[] = ferries.map((v) => {
            const g = advanceFerryGlide(
              ferryGlides.get(v.vehicleId),
              { lon: v.longitude, lat: v.latitude, bearing: v.bearing },
              nowSec,
              dt,
            )
            ferryGlides.set(v.vehicleId, g)
            return {
              vehicleId: v.vehicleId,
              routeId: v.routeId,
              position: [g.lon, g.lat],
              bearing: g.heading,
              color: getRouteColorRgb(v.routeId),
            }
          })
          // Bounded by the fleet; a boat that leaves service takes its glide with it.
          if (ferryGlides.size > ferries.length + 16) {
            const live = new Set(ferries.map((v) => v.vehicleId))
            for (const id of ferryGlides.keys()) if (!live.has(id)) ferryGlides.delete(id)
          }

          const zoom = map.getZoom()
          const mode = ferryRenderMode(useSettingsStore.getState().vehicleDisplay, zoom)
          // The same zoom taper the train/bus bullets use in `labels`.
          const labelScale = 0.7 + 0.3 * Math.max(0, Math.min(1, (zoom - 11) / 4))
          const position = (d: FerryDatum) => [d.position[0], d.position[1], 0] as [number, number, number]
          // deck's yaw is counter-clockwise from east; a bearing is clockwise from north.
          const orientation = (d: FerryDatum) => [0, 90 - d.bearing, 0] as [number, number, number]
          const layers: any[] = []
          if (mode.mesh) {
            layers.push(
              new SimpleMeshLayer<FerryDatum>({
                id: 'ferry-hull',
                data,
                mesh: FERRY_HULL_MESH as any,
                getPosition: position,
                getOrientation: orientation,
                getColor: [255, 255, 255, 255],
                sizeScale: FERRY_SIZE_SCALE,
                pickable: true,
              }),
              new SimpleMeshLayer<FerryDatum>({
                id: 'ferry-stripe',
                data,
                mesh: FERRY_STRIPE_MESH as any,
                getPosition: position,
                getOrientation: orientation,
                getColor: (d) => d.color,
                sizeScale: FERRY_SIZE_SCALE,
                pickable: true,
              }),
            )
          }
          if (mode.arrow) {
            // The zoomed-out stand-in's heading: the train marker's own dart, flat on the
            // water, drawn before the badge so the badge covers its tail.
            layers.push(new IconLayer<FerryDatum>({
              id: 'ferry-arrow',
              data,
              getPosition: position,
              getIcon: (d) => createTrainHeadingArrow(d.routeId),
              getAngle: (d) => -d.bearing,
              getSize: TRAIN_MARKER_ARROW_PX * labelScale,
              sizeUnits: 'pixels' as const,
              billboard: false,
              parameters: { depthTest: false } as any,
            }))
          }
          if (mode.badge) {
            layers.push(new IconLayer<FerryDatum>({
              id: 'ferry-badge',
              data,
              // Hung over the wheelhouse when a mesh is under it (`both`), as the train's
              // bullet hangs over its roof.
              getPosition: (d) => [d.position[0], d.position[1], mode.mesh ? FERRY_LABEL_ALT : 0] as [number, number, number],
              getIcon: (d) => createFerryBadge(d.routeId),
              getSize: 26 * labelScale,
              sizeUnits: 'pixels' as const,
              pickable: true,
              parameters: { depthTest: false } as any,
            }))
          }
          return layers
        }

        // ── LIRR + Metro-North ─────────────────────────────────────────────────────
        // The ferry's arrangement: own layers, rebuilt on every deck submission and appended
        // beside the fleet's, glide advanced here on the wall clock. ~40 trains at a time,
        // each RAIL_DRAWN_CARS cars laid along its branch line.
        type RailCarDatum = {
          vehicleId: string
          routeId: string
          position: [number, number]
          bearing: number
          color: [number, number, number, number]
        }
        const buildRailLayers = (): any[] => {
          if (!railTrains.length) return []
          const nowSec = Date.now() / 1000
          const dt = railGlideLastNow ? Math.min(0.5, Math.max(0, nowSec - railGlideLastNow)) : 0
          railGlideLastNow = nowSec
          const zoom = map.getZoom()
          const mode = railRenderMode(useSettingsStore.getState().vehicleDisplay, zoom)
          const heads: RailCarDatum[] = []
          const cars: RailCarDatum[] = []
          for (const v of railTrains) {
            const g = advanceRailGlide(
              railGlides.get(v.vehicleId),
              { lon: v.longitude, lat: v.latitude, bearing: v.bearing },
              railPaths.get(v.routeId) ?? [],
              nowSec,
              dt,
            )
            railGlides.set(v.vehicleId, g)
            const color = getRouteColorRgb(v.routeId)
            heads.push({ vehicleId: v.vehicleId, routeId: v.routeId, position: [g.lon, g.lat], bearing: g.heading, color })
            if (mode.mesh) {
              for (const c of layoutRailConsist(g, RAIL_DRAWN_CARS, RAIL_CAR_LENGTH_M)) {
                cars.push({ vehicleId: v.vehicleId, routeId: v.routeId, position: [c.lon, c.lat], bearing: c.bearing, color })
              }
            }
          }
          // Bounded by the fleet; a train that leaves service takes its glide with it.
          if (railGlides.size > railTrains.length + 32) {
            const live = new Set(railTrains.map((v) => v.vehicleId))
            for (const id of railGlides.keys()) if (!live.has(id)) railGlides.delete(id)
          }

          const labelScale = 0.7 + 0.3 * Math.max(0, Math.min(1, (zoom - 11) / 4))
          const position = (d: RailCarDatum) => [d.position[0], d.position[1], 0] as [number, number, number]
          // deck's yaw is counter-clockwise from east; a bearing is clockwise from north.
          const orientation = (d: RailCarDatum) => [0, 90 - d.bearing, 0] as [number, number, number]
          const layers: any[] = []
          if (mode.mesh) {
            layers.push(
              new SimpleMeshLayer<RailCarDatum>({
                id: 'rail-car',
                data: cars,
                mesh: RAIL_CAR_MESH as any,
                getPosition: position,
                getOrientation: orientation,
                getColor: [255, 255, 255, 255],
                pickable: true,
              }),
              new SimpleMeshLayer<RailCarDatum>({
                id: 'rail-stripe',
                data: cars,
                mesh: RAIL_STRIPE_MESH as any,
                getPosition: position,
                getOrientation: orientation,
                getColor: (d) => d.color,
                pickable: true,
              }),
            )
          }
          if (mode.arrow) {
            layers.push(new IconLayer<RailCarDatum>({
              id: 'rail-arrow',
              data: heads,
              getPosition: position,
              getIcon: (d) => createTrainHeadingArrow(d.routeId),
              getAngle: (d) => -d.bearing,
              getSize: TRAIN_MARKER_ARROW_PX * labelScale,
              sizeUnits: 'pixels' as const,
              billboard: false,
              parameters: { depthTest: false } as any,
            }))
          }
          if (mode.badge) {
            layers.push(new IconLayer<RailCarDatum>({
              id: 'rail-badge',
              data: heads,
              getPosition: (d) => [d.position[0], d.position[1], mode.mesh ? RAIL_LABEL_ALT : 0] as [number, number, number],
              getIcon: (d) => createRailBadge(d.routeId),
              getSize: 24 * labelScale,
              sizeUnits: 'pixels' as const,
              pickable: true,
              parameters: { depthTest: false } as any,
            }))
          }
          return layers
        }

        // ── NJ Transit buses ──────────────────────────────────────────────────────
        // The railroads' arrangement, with the MTA bus's Xcelsior body: own layers, rebuilt on
        // every deck submission and appended beside the fleet's. Each bus is drawn where it
        // should be NOW — its fix projected along the route's line by its speed and the fix's
        // age, never sliding backwards (njBusMotion.ts) — on the SERVER's clock, since the fix
        // times are the server's. Up to ~2,000 buses, so zoomed out (by default) they are badges
        // and arrows only — see njBusRenderMode. A bus whose GPS has gone quiet is held at its
        // terminal while laying over, or carried along its timetable (njBusMotion.ts); an
        // estimated one wears the hollow badge, and the vehicle sheet says what the position is.
        type NjBusDatum = {
          vehicleId: string
          routeId: string
          position: [number, number]
          bearing: number
          estimated: boolean
        }
        // A quiet bus's calls with each stop resolved to a place; stops the network doesn't
        // know are left out. Cached per payload array (njBusCallCache).
        const njBusCalls = (v: VehiclePosition): NjBusCall[] | undefined => {
          const raw = v.upcomingCalls
          if (!raw) return undefined
          let calls = njBusCallCache.get(raw)
          if (!calls) {
            calls = []
            for (const [stopId, arr, dep] of raw) {
              const at = njBusStopCoords.get(stopId)
              if (at) calls.push({ lon: at.lon, lat: at.lat, arr, dep })
            }
            njBusCallCache.set(raw, calls)
          }
          return calls
        }
        const buildNjBusLayers = (): any[] => {
          if (!njBuses.length) return []
          const nowSec = serverNowSec()
          const dt = njBusGlideLastNow ? Math.min(0.5, Math.max(0, nowSec - njBusGlideLastNow)) : 0
          njBusGlideLastNow = nowSec
          const zoom = map.getZoom()
          const mode = njBusRenderMode(useSettingsStore.getState().vehicleDisplay, zoom)
          const data: NjBusDatum[] = []
          for (const v of njBuses) {
            const own = v.directionRef ? njBusDirPaths.get(`${v.routeId}|${v.directionRef}`) : undefined
            const g = advanceNjBusMotion(
              njBusGlides.get(v.vehicleId),
              {
                lon: v.longitude,
                lat: v.latitude,
                bearing: v.bearing,
                at: v.timestamp,
                // VehiclePosition.speed is mph.
                ...(v.speed !== undefined ? { speedMps: v.speed / 2.236_94 } : {}),
                ...(v.layoverUntil !== undefined ? { layoverUntil: v.layoverUntil } : {}),
                ...(v.upcomingCalls ? { calls: njBusCalls(v) } : {}),
              },
              own ?? njBusPaths.get(v.routeId) ?? [],
              nowSec,
              dt,
              own !== undefined,
            )
            njBusGlides.set(v.vehicleId, g)
            data.push({
              vehicleId: v.vehicleId, routeId: v.routeId, position: [g.lon, g.lat], bearing: g.heading,
              estimated: g.kind === 'estimated' || g.kind === 'lastEstimate',
            })
          }
          // Bounded by the fleet; a bus that leaves service takes its glide with it.
          if (njBusGlides.size > njBuses.length + 64) {
            const live = new Set(njBuses.map((v) => v.vehicleId))
            for (const id of njBusGlides.keys()) if (!live.has(id)) njBusGlides.delete(id)
          }

          const labelScale = 0.7 + 0.3 * Math.max(0, Math.min(1, (zoom - 11) / 4))
          const position = (d: NjBusDatum) => [d.position[0], d.position[1], 0] as [number, number, number]
          const layers: any[] = []
          if (mode.mesh) {
            const tier = mode.mesh
            layers.push(new ScenegraphLayer<NjBusDatum>({
              // ONE id for every tier, as the MTA bus's `b-body`: a tier change is then a new
              // `scenegraph` on a live layer, which keeps drawing the old model until the new
              // one has loaded. With the tier in the id, crossing zoom 14 or 16 made a NEW layer,
              // which draws nothing until its GLB is fetched and decoded — every NJ bus blanked,
              // and at 14 the badges switch off on the same frame, so nothing stood in.
              id: 'njbus-body',
              data,
              // The plain glTF, NOT liveryScenegraph: the rider's bus livery is the MTA's
              // paint (see njBusModel.ts).
              scenegraph: XCEL40_MODELS[tier] as any,
              // Vendored Draco. Required, not tuning — see VENDORED_GLB_LOAD_OPTIONS.
              loadOptions: XCEL40_LOAD_OPTIONS,
              getPosition: position,
              // Roll 90: the glTF is Y-up (see mkXcel40).
              getOrientation: (d) => [0, 90 - d.bearing, 90] as [number, number, number],
              sizeScale: BUS_SIZE_SCALE,
              ...(xcel40TierUsesPbr(tier)
                ? {
                    _lighting: 'pbr' as const,
                    _imageBasedLightingEnvironment: ({ layer }: { layer: ScenegraphLayer<NjBusDatum> }) =>
                      r160PbrEnvironment(layer.context.device),
                  }
                : {}),
              pickable: true,
            }))
          }
          if (mode.arrow) {
            layers.push(new IconLayer<NjBusDatum>({
              id: 'njbus-arrow',
              data,
              getPosition: position,
              getIcon: (d) => createTrainHeadingArrow(d.routeId),
              getAngle: (d) => -d.bearing,
              getSize: TRAIN_MARKER_ARROW_PX * labelScale,
              sizeUnits: 'pixels' as const,
              billboard: false,
              parameters: { depthTest: false } as any,
            }))
          }
          if (mode.badge) {
            layers.push(new IconLayer<NjBusDatum>({
              id: 'njbus-badge',
              data,
              getPosition: (d) => [d.position[0], d.position[1], mode.mesh ? NJ_BUS_LABEL_ALT : 0] as [number, number, number],
              getIcon: (d) => createNjBusBadge(d.routeId, d.estimated),
              getSize: 22 * labelScale,
              sizeUnits: 'pixels' as const,
              pickable: true,
              parameters: { depthTest: false } as any,
            }))
          }
          return layers
        }

        function frame(timestamp: number) {
          // Ordinary fleet layer rebuilds are capped at 30 FPS. Animation integration and
          // requestAnimationFrame continue every display frame, and a watched/boarded close-up
          // bypasses the cap so its model and camera remain display-rate smooth.
          const frameWatchedVehicleId = renderWatchedVehicleId()
          const frameTrackedVehicleId = renderBoardedVehicleId()
          const hasPinnedCloseup = frameWatchedVehicleId != null || frameTrackedVehicleId != null
          const pinnedVehicleKey = `${frameWatchedVehicleId ?? ''}|${frameTrackedVehicleId ?? ''}`
          const pinnedSelectionChanged = pinnedVehicleKey !== lastPinnedVehicleKey
          lastPinnedVehicleKey = pinnedVehicleKey
          const ordinaryFleetDue =
            pinnedSelectionChanged || timestamp - lastFleetLayerUpdate >= ORDINARY_FLEET_FRAME_MS
          const shouldSetData = hasPinnedCloseup || ordinaryFleetDue
          if (ordinaryFleetDue) lastFleetLayerUpdate = timestamp
          const benchmarkSink = TRAIN_LOD_BENCHMARK_BUILD && shouldSetData
            ? getTrainLodBenchmarkSink()
            : undefined

          // Per-frame delta from the rAF high-res timestamp (ms). Drives the per-car position
          // SmoothDamp in emit(); consumers clamp it so a tab-refocus / GC gap can't lurch.
          const frameDtMs = lastFrame ? timestamp - lastFrame : 0
          lastFrame = timestamp

          // Read the analysis-layer flags once per frame (same imperative-getState pattern
          // buildVehicleLayers uses for settings), so toggling a layer recolors on the very
          // next frame.
          const frameTint = useLayersStore.getState()

          // Pulse the selected-station ring (expand + fade on a ~1.4 s loop). Only
          // touches paint props while a station is selected, so it's free otherwise.
          if (selectedStationId && map.getLayer('stop-selected-pulse')) {
            const phase = (timestamp % 1400) / 1400 // 0 → 1
            const radius = 9 + phase * 22 // expands outward
            const opacity = 0.4 * (1 - phase) // fades to transparent
            map.setPaintProperty('stop-selected-pulse', 'circle-radius', radius)
            map.setPaintProperty('stop-selected-pulse', 'circle-opacity', opacity)
          }

          // Same pulse for the selected bus stop (only while one is selected).
          if (selectedBusStopId && map.getLayer('bus-stop-selected-pulse')) {
            const phase = (timestamp % 1400) / 1400
            const radius = 8 + phase * 20
            const opacity = 0.4 * (1 - phase)
            map.setPaintProperty('bus-stop-selected-pulse', 'circle-radius', radius)
            map.setPaintProperty('bus-stop-selected-pulse', 'circle-opacity', opacity)
          }

          if (!deckOverlay) {
            animFrame = requestAnimationFrame(frame)
            return
          }

          // Zoom-gated car count with hysteresis (enter at MIN_ZOOM, exit below
          // EXIT_ZOOM). On a mode flip, drop only FOLLOWER (segIdx > 0) smoothing
          // state: car 0 keeps its follow velocity so the visible train never hitches
          // at the flip, while stale follower entries from a previous multi-car spell
          // can't glide in from an old layout. Fresh followers seed exactly on the
          // trail on their first emit, so the swap is a coherent single frame.
          if (!DISTANCE_TRAIN_LOD_ENABLED) {
            const z = map.getZoom()
            const nextCars =
              trainCars > 1
                ? z < TRAIN_CARS_EXIT_ZOOM
                  ? 1
                  : TRAIN_CARS
                : z >= TRAIN_CARS_MIN_ZOOM
                  ? TRAIN_CARS
                  : 1
            if (nextCars !== trainCars) {
              trainCars = nextCars
              for (const k of renderHeading.keys()) if (!k.endsWith(':0')) renderHeading.delete(k)
            }
          } else {
            trainCars = TRAIN_CARS
          }

          // Lane offset inputs. Rendering and motion share the canonical centerline, but
          // the painted route lines are fanned out sideways so the four routes sharing a
          // trunk stay legible (ROUTE_OFFSETS). Shift each rendered car by that same display
          // offset so it remains on its own colored line. Read per frame because the fan is in SCREEN
          // pixels, so its ground width changes on every zoom; the map centre's latitude is
          // close enough for the whole viewport (cos varies <1% across NYC).
          const mapCenter = map.getCenter()
          const laneZoom = map.getZoom()
          const laneLat = mapCenter.lat
          const mapContainer = map.getContainer()
          const lodCamera = {
            longitude: mapCenter.lng,
            latitude: mapCenter.lat,
            zoom: laneZoom,
            pitch: map.getPitch(),
            bearing: map.getBearing(),
            width: mapContainer.clientWidth,
            height: mapContainer.clientHeight,
          }
          const projectForLod = (position: readonly [number, number]) => {
            try {
              const point = map.project([position[0], position[1]])
              return Number.isFinite(point.x) && Number.isFinite(point.y)
                ? { x: point.x, y: point.y }
                : null
            } catch {
              return null
            }
          }
          const frameVehicleDisplay = useSettingsStore.getState().vehicleDisplay
          let classificationMs = 0
          let classificationTrainCount = 0
          let classificationCacheHits = 0
          let classificationCacheMisses = 0
          let visibleTrainCount = 0
          const activeVisibleTrainIds: string[] = []

          const now = Date.now() / 1000
          const coords = stopCoords
          // The store now keeps live-updating vehicles across short feed gaps and
          // prunes them once they age out (VEHICLE_TTL_MS), so this list is the
          // authoritative set: no frozen-snapshot ghost layer needed.
          const vList = vehicles

          const tid = frameTrackedVehicleId
          const wid = frameWatchedVehicleId

          // Reusable datum pool + per-frame heading-smoothing factor. emit() mutates pooled
          // objects in place (no per-frame allocation) and eases each instance's rendered
          // heading toward its live target with a shortest-angle exponential (frame-rate
          // independent via `now`), so models glide without GC stutter and never snap-rotate.
          // Runs on every submitted vehicle frame; close-up modes submit at display rate.
          const ordinaryPool = ordinaryDatumPool
          const pinnedPool = pinnedDatumPool
          let ordinaryDn = 0
          let pinnedDn = 0
          const hmap = renderHeading
          let hk = 1
          if (shouldSetData) {
            const hLast = headingLastNow
            const hdt = hLast > 0 ? Math.max(0, Math.min(0.5, now - hLast)) : 0
            headingLastNow = now
            hk = hdt > 0 ? 1 - Math.exp(-hdt / HEADING_TAU) : 1
            // Bound the map so headings for long-gone vehicles can't accumulate forever. A
            // clear just re-seeds every live heading to its target next emit (invisible).
            if (hmap.size > 4000) hmap.clear()
            if (centerHead.size > 4000) centerHead.clear()
            // Same bound for the bus position-follow map. A clear re-seeds (snaps) every
            // live bus next emit.
            if (emitSmooth.size > 4000) emitSmooth.clear()
            // Trails hold ~100+ points per train; same bound-and-clear pattern. A clear
            // re-seeds each live train's tail from its track slice next frame (invisible).
            if (trails.size > 1500) trails.clear()
            // Glide-state maps are keyed by vehicle/trip id, and trip ids turn over all
            // day, so without a bound they grow for the life of the session. A clear
            // re-adopts every live vehicle from the feed next frame (a one-frame re-seed
            // at the schedule's current position, same as the stale-state path).
            if (anim.size > 4000) anim.clear()
            if (busGlides.size > 4000) busGlides.clear()
            // segCache is bounded differently from every map above. It is keyed by
            // (route, prevStop, nextStop) rather than by vehicle, so it does not turn over
            // with the fleet: it accumulates one entry per stop pair the day's service
            // ever runs, express patterns and reroutes included, and each entry holds a
            // polyline (~50 points). Measured on a live session it climbs steadily and
            // never falls: ~400 → ~900 entries (1.1 → 2.2 MB of retained arrays) in three
            // minutes, released only by a reload. That was the leak.
            //
            // It gets FIFO eviction rather than the clear-when-large above because its
            // entries are the expensive ones to rebuild: a miss whose own route doesn't
            // cover the pair rescans every route's shapes (see the fallback in the glide
            // block). Clearing all of it at once would re-derive a segment for the whole
            // live fleet in a single frame, a visible hitch. Dropping the oldest slice
            // instead spreads that over many frames, and a Map iterates in insertion
            // order, so the oldest keys are simply the first ones out. The cap holds the
            // live fleet's working set many times over; anything evicted is cold.
            if (segCache.size > SEG_CACHE_MAX) {
              let toDrop = segCache.size - SEG_CACHE_MAX + SEG_CACHE_EVICT
              for (const key of segCache.keys()) {
                if (toDrop-- <= 0) break
                segCache.delete(key)
              }
            }
          }
          const smoothRenderedPosition = (
            key: string,
            targetLon: number,
            targetLat: number,
          ): [number, number] => {
            if (!Number.isFinite(targetLon) || !Number.isFinite(targetLat)) return [targetLon, targetLat]
            const rs = emitSmooth.get(key)
            const dt = Math.min(0.1, frameDtMs / 1000)
            if (!rs) {
              emitSmooth.set(key, [targetLon, targetLat, 0, 0])
              return [targetLon, targetLat]
            }
            if (dt > 0) {
              if (approxMeters(rs[0], rs[1], targetLon, targetLat) > RENDER_SNAP_M) {
                rs[0] = targetLon
                rs[1] = targetLat
                rs[2] = 0
                rs[3] = 0
              } else {
                const omega = 2 / RENDER_POS_TAU
                const x = omega * dt
                const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)
                let change = rs[0] - targetLon
                let temp = (rs[2] + omega * change) * dt
                rs[2] = (rs[2] - omega * temp) * exp
                rs[0] = targetLon + (change + temp) * exp
                change = rs[1] - targetLat
                temp = (rs[3] + omega * change) * dt
                rs[3] = (rs[3] - omega * temp) * exp
                rs[1] = targetLat + (change + temp) * exp
              }
            }
            return [rs[0], rs[1]]
          }
          const emit = (
            vehicleId: string,
            lon: number,
            lat: number,
            col: [number, number, number, number],
            routeId: string,
            rawBearing: number,
            type: VehicleType,
            segIdx: number,
            destinationName?: string,
            carLabel?: string,
            trainRenderProfile?: TrainRenderProfile,
          ) => {
            const boarded = vehicleId === tid
            const watched = vehicleId === wid
            let bearing = rawBearing
            if (Number.isFinite(rawBearing)) {
              const hkey = `${vehicleId}:${segIdx}`
              const prev = hmap.get(hkey)
              if (prev != null) {
                const diff = ((rawBearing - prev + 540) % 360) - 180 // shortest signed turn
                bearing = (prev + diff * hk + 360) % 360
              }
              hmap.set(hkey, bearing)
            }

            // Every subway car is sampled from one breadcrumb path. Only buses retain
            // a separate Cartesian follow spring; otherwise cars cut across curves.
            if (type === 'bus' && !watched) {
              ;[lon, lat] = smoothRenderedPosition(vehicleId, lon, lat)
            }

            if (
              watched && segIdx === 0 &&
              vehicleId === useVehicleWatchStore.getState().watchedVehicleId
            ) {
              const camera = useVehicleWatchStore.getState()
              watchCameraFollow = followWatchCameraPose(
                watchCameraFollow,
                { longitude: lon, latitude: lat, vehicleBearing: bearing },
                frameDtMs / 1000,
              )
              const pose = watchCameraPose({
                ...watchCameraFollow,
                orbitYaw: camera.orbitYaw,
                pitch: camera.pitch,
                zoom: camera.zoom,
                viewportWidth: map.getContainer().clientWidth,
                viewportHeight: map.getContainer().clientHeight,
              })
              map.easeTo({ ...pose, duration: 0, animate: false, noMoveStart: true })
            }

            const targetPool = boarded || watched ? pinnedPool : ordinaryPool
            const targetIndex = boarded || watched ? pinnedDn : ordinaryDn
            const d = targetPool[targetIndex]
            if (d) {
              d.vehicleId = vehicleId
              d.position[0] = lon
              d.position[1] = lat
              d.color = col
              d.routeId = routeId
              d.bearing = bearing
              d.type = type
              d.segIdx = segIdx
              d.boarded = boarded
              d.watched = watched
              d.destinationName = destinationName
              d.carLabel = carLabel
              d.trainRenderProfile = trainRenderProfile
              d.carIndex = type === 'subway' ? segIdx : undefined
              d.destination = type === 'subway' ? (destinationName || `${routeId} TRAIN`) : undefined
              d.carNumber = type === 'subway' ? (carLabel || vehicleId.slice(-5)) : undefined
            } else {
              targetPool[targetIndex] = {
                vehicleId, position: [lon, lat], color: col, routeId, bearing, type, segIdx,
                boarded, watched, destinationName, carLabel, trainRenderProfile,
                carIndex: type === 'subway' ? segIdx : undefined,
                destination: type === 'subway' ? (destinationName || `${routeId} TRAIN`) : undefined,
                carNumber: type === 'subway' ? (carLabel || vehicleId.slice(-5)) : undefined,
              }
            }
            if (boarded || watched) pinnedDn++
            else ordinaryDn++
          }

          // This intentionally overlaps classificationMs: layerBuildMs measures the complete
          // renderer-side submission path from visible-train processing through layer creation.
          const layerBuildStarted = benchmarkSink ? performance.now() : 0
          for (const v of vList) {
            let lon = v.longitude,
              lat = v.latitude,
              bearing = v.bearing
            let trackBearing = bearing
            let curTrackSeg: ShapeSeg | null = null,
              curT = 0

            const feedDep = v.prevStopDeparture
            const feedArr = v.nextStopArrival
            const feedHasSeg = !!(
              v.prevStopId && v.stopId && feedDep != null && feedArr != null && feedArr > feedDep &&
              coords[v.prevStopId] && coords[v.stopId]
            )
            let a = anim.get(v.vehicleId)

            // Stale glide state (see STALE_STATE_GAP_S): the segment's clock kept running
            // while this train wasn't rendered, so resuming would place it far behind the
            // live schedule. Drop it and re-adopt fresh below at the schedule's current spot.
            if (a && now - a.at > STALE_STATE_GAP_S) {
              anim.delete(v.vehicleId)
              a = undefined
            }

            // Cold start: `/api/stops/coords` hasn't landed yet, so coords[] is empty and NO
            // subway can form a segment. Without one the fallbacks below draw the train at the
            // feed's raw fix, and that fix is a straight-line chord between two stations
            // (derivePositionsFromTripUpdates), not a point on the track. Trains read as spawned
            // at random through the middle of blocks, and worse, the trail seeds along that
            // chord's bearing, so every follower car in the consist is parked inside a building
            // and stays there until the trail scrolls off. The backend only emits a subway when
            // it has BOTH stop ids and both times, so a missing segment here means our coords
            // haven't arrived, so hold the train out of the render for the beat it takes rather
            // than place it wrong. Buses are unaffected: they carry real GPS fixes.
            if (v.type === 'subway' && !feedHasSeg && !a && !benchmarkFleetActive) continue

            if (feedHasSeg || a) {
              // ── Frozen-segment glide with finish-before-advance ─────────────────
              // The model glides a FROZEN segment (its stops + timestamps captured when the
              // segment was adopted). Per-poll feed revisions are NOT applied mid-glide, so
              // nothing can yank the train sideways. It only re-syncs with the feed when it
              // reaches the stop, where the hand-off is continuous (it's standing there).
              if (!a && feedHasSeg) {
                // First sighting: adopt the feed segment, placed where the schedule says it
                // is right now so it appears in the correct spot.
                const p0 = Math.max(0, Math.min(1, (now - feedDep!) / (feedArr! - feedDep!)))
                a = {
                  prev: v.prevStopId!, next: v.stopId, dep: feedDep!, arr: feedArr!,
                  progress: p0, at: now, nextSeq: v.stopSequence ?? 0, vel: 0,
                }
                anim.set(v.vehicleId, a)
              }
              a = a!

              // Integration dt from the wall clock, clamped so a tab refocus / long GC gap
              // eases in over the next frames instead of teleporting the model in one step.
              const dt = Math.min(0.25, now - a.at)
              a.at = now
              // Real-world length of the segment being glided right now, so the catch-up speed
              // can be bounded by a PHYSICAL speed and never teleport. Uses the current
              // (pre-hand-off) stops; if either coord is missing we fall back to a 1 m length.
              const gp = coords[a.prev],
                gn = coords[a.next]
              const segDistM = gp && gn ? Math.max(1, approxMeters(gp.lon, gp.lat, gn.lon, gn.lat)) : 1
              // Wall-clock target along the FROZEN segment. dep/arr never change after adoption,
              // so the target is a pure function of the clock: it never yanks the train sideways
              // on a mid-glide feed revision, and a feed that has "moved on" simply pins it at 1.
              const dur = Math.max(1, a.arr - a.dep)
              const target = Math.max(0, Math.min(1, (now - a.dep) / dur))
              // Critically-damped follow of progress → target (SmoothDamp). Unlike the old hard
              // step clamp (whose catch-up and clock-tracking regimes met at a velocity step,
              // so every large-dt poll/GC frame produced a stall-then-speed-up forward stutter),
              // this is C1-continuous: the on-screen SPEED is always continuous. In steady state
              // it converges to the target's own rate (constant velocity = real schedule speed,
              // no pulsing); a behind-feed catch-up eases in and decelerates smoothly into the
              // stop. maxSpeed enforces the physical MAX_CATCHUP_MPS ceiling via the change clamp
              // (a smooth bound, not an output cut), so a collapsed hand-off segment can never
              // dart. Frame-rate independent and stable for any dt. (Unity's SmoothDamp form.)
              if (dt > 0) {
                const maxSpeed = MAX_CATCHUP_MPS / segDistM // progress units / s
                const omega = 2 / PROGRESS_SMOOTH_TIME
                const x = omega * dt
                const expf = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)
                const maxChange = maxSpeed * PROGRESS_SMOOTH_TIME
                let change = a.progress - target
                change = Math.max(-maxChange, Math.min(maxChange, change))
                const sdTarget = a.progress - change
                const temp = (a.vel + omega * change) * dt
                a.vel = (a.vel - omega * temp) * expf
                let out = sdTarget + (change + temp) * expf
                // No overshoot past the live target (keeps motion forward-only / monotonic).
                if ((target - a.progress > 0) === (out > target)) {
                  out = target
                  a.vel = (out - target) / dt
                }
                a.progress = Math.max(a.progress, out)
              }

              // Hand-off: once standing at the stop, adopt the feed's current segment and
              // FREEZE its timing. Continuous because the new segment starts at this stop.
              //
              // Forward-only guard. The feed can momentarily REGRESS a train to an earlier
              // stop (a stale snapshot from another upstream server arriving out of order,
              // or a trip re-assignment) and adopting it builds a backward segment
              // (a.next → earlier stop), gliding the model in reverse: the "snap backwards"
              // the user reported. The old id-only check (≠ a.next && ≠ a.prev) caught only a
              // regression to the immediately-prior stop; a jump two-or-more stops back still
              // slipped through. When the feed carries a usable monotonic stopSequence, require
              // it to strictly advance; otherwise fall back to the id guard (NYC's subway feed
              // omits stopSequence on some polls, leaving it 0: never freeze a train on that).
              // A feed running AHEAD (sequence jumps several stops) still hands off here and the
              // bounded MAX_CATCHUP_MPS glide closes the gap smoothly. A genuinely stuck/
              // permanently-regressed trip just dwells at the stop until VEHICLE_TTL_MS prunes
              // and re-seeds it: far less jarring than a visible reverse glide.
              const feedSeq = v.stopSequence ?? 0
              const seqUsable = feedSeq > 0 && a.nextSeq > 0
              const canAdvance = seqUsable
                ? feedSeq > a.nextSeq
                : v.stopId !== a.next && v.stopId !== a.prev
              if (a.progress >= 0.999 && feedHasSeg && canAdvance) {
                a = {
                  prev: a.next, next: v.stopId, dep: now, arr: Math.max(now + 1, feedArr!),
                  progress: 0, at: now, nextSeq: feedSeq, vel: 0,
                }
                anim.set(v.vehicleId, a)
              }

              const prevC = coords[a.prev]
              const nextC = coords[a.next]
              if (prevC && nextC) {
                // On-track geometry between the two stops (cached, shared across trains).
                const segCacheKey = `${v.routeId}|${a.prev}|${a.next}`
                let trackSeg: ShapeSeg | null
                if (segCache.has(segCacheKey)) {
                  trackSeg = segCache.get(segCacheKey) ?? null
                } else {
                  // Pick the shape whose closest points to both stops have the smallest
                  // combined distance: routes each train onto its correct branch.
                  const baseRouteId = ROUTE_ALIAS[v.routeId] ?? v.routeId
                  let best = matchSegmentInShapes(
                    routeShapes.get(baseRouteId), prevC, nextC, baseRouteId,
                  )
                  // If the train's own route doesn't pass near both stops (e.g. an F train
                  // rerouted onto the G's Crosstown line, where no F shape covers Greenpoint
                  // Av ↔ 21 St), its shapes can't describe this segment and would snap the
                  // model onto a wrong, far-away slice of track. Search every route's shapes
                  // for the corridor that actually covers these stops.
                  if (best.dist > SEG_MATCH_TOL_SQ) {
                    for (const [candidateRouteId, shapes] of routeShapes) {
                      const cand = matchSegmentInShapes(
                        shapes, prevC, nextC, candidateRouteId,
                      )
                      if (cand.dist < best.dist) best = cand
                    }
                  }
                  // Only render when some canonical route genuinely covers the stop pair.
                  // The resolved endpoints are projections on that exact drawn polyline.
                  trackSeg = best.dist <= SEG_MATCH_TOL_SQ ? best.seg : null
                  // Raw station coordinates are matching inputs only; they never become
                  // motion geometry or pull a train away from the painted line.
                  segCache.set(segCacheKey, trackSeg)
                }

                if (trackSeg && trackSeg.pts.length >= 2) {
                  const [sLon, sLat] = sampleSeg(trackSeg, a.progress)
                  lon = sLon
                  lat = sLat
                  bearing = bearingAtT(trackSeg, a.progress)
                  trackBearing = bearing
                }
                curTrackSeg = trackSeg
                curT = a.progress
              }
              // A subway without canonical geometry has no honest drawable position.
              // Never invent a stop-to-stop chord across streets: keep the live feed state
              // so it can recover when geometry becomes available, but omit the vehicle
              // until its current segment resolves onto the same source used by route-lines.
              if (
                v.type === 'subway' &&
                (!curTrackSeg || !Number.isFinite(lon) || !Number.isFinite(lat)) &&
                !benchmarkFleetActive
              ) continue
              // Never emit a non-finite center for non-production fixtures: that would
              // place the model (and all its spread segments below) at NaN for a frame.
              if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
                lon = v.longitude
                lat = v.latitude
              }
              if (!Number.isFinite(bearing)) bearing = Number.isFinite(v.bearing) ? v.bearing : 0
            } else if (v.type === 'bus') {
              // No stop-time/shape segment for this bus → live-glide between its
              // consecutive GPS fixes. Runs ahead of the cull + tracked-marker checks so
              // the glided position drives all of them, just like the subway path above.
              const g = busGlide(v, now)
              lon = g.lon
              lat = g.lat
              bearing = g.bearing
            } else if (v.timestamp && (v.speed ?? 0) > 2) {
              const elapsed = now - v.timestamp
              if (elapsed > 0 && elapsed < 45) {
                const mps = v.speed! / 2.237
                const dist = mps * elapsed
                const bearRad = (bearing * Math.PI) / 180
                const R = 6_371_000
                lat = lat + (dist / R) * (180 / Math.PI) * Math.cos(bearRad)
                lon = lon + ((dist / R) * (180 / Math.PI) * Math.sin(bearRad)) / Math.cos((lat * Math.PI) / 180)
              }
            }

            // Record the rendered center onto the train's breadcrumb trail. Runs before
            // culling (like the position math) so an off-screen train re-entering the
            // viewport, or a zoom past the multi-car threshold, finds real path history.
            // A > RENDER_SNAP_M leap is a genuine re-placement: re-seed the tail rather
            // than record a cross-city jump the followers would later replay.
            if (v.type === 'subway') {
              const trail = trails.get(v.vehicleId)
              const lastPt = trail?.[trail.length - 1]
              const trailStepM = v.vehicleId === wid ? WATCH_TRAIL_STEP_M : TRAIL_STEP_M
              if (!trail || !lastPt || approxMeters(lastPt.lon, lastPt.lat, lon, lat) > RENDER_SNAP_M) {
                trails.set(v.vehicleId, seedTrail(lon, lat, bearing, curTrackSeg, curT, trailStepM))
              } else {
                recordTrailPoint(trail, { lon, lat }, trailStepM, TRAIL_KEEP_M)
              }
            }

            // Metres to shift this vehicle sideways so it lands on its own drawn route line
            // (see laneZoom above). Signed by `dir` because MapLibre offsets a line to the
            // right of ITS OWN vertex order, which is the left of a train running the shape
            // backwards. Applied to what's drawn only; the breadcrumb trail above stays in
            // true-track space, so the consist keeps bending along the real curve and a zoom
            // change re-derives the lane instead of smearing recorded history sideways.
            const laneM =
              v.type === 'subway'
                ? routeLineOffsetMetres(
                    curTrackSeg?.sourceRouteId ?? v.routeId,
                    laneZoom,
                    laneLat,
                    curTrackSeg?.dir ?? 1,
                  )
                : 0
            const isPinned = v.vehicleId === tid || v.vehicleId === wid
            const vehicleNeedsData = isPinned || ordinaryFleetDue
            let benchmarkResolved: {
              profile: TrainRenderProfile
              cacheHit: boolean
            } | undefined

            // The production path classifies only projected-visible trains. A benchmark
            // additionally times the full 100-train fixture before culling so the explicit
            // classification budget is measurable without weakening production culling.
            if (benchmarkSink && vehicleNeedsData && v.type === 'subway') {
              const classificationStarted = performance.now()
              benchmarkResolved = resolveTrainRenderProfile({
                vehicleId: v.vehicleId,
                lon,
                lat,
                bearing,
                boarded: v.vehicleId === tid,
                watched: v.vehicleId === wid,
                vehicleDisplay: frameVehicleDisplay,
                camera: lodCamera,
                project: projectForLod,
              })
              classificationMs += performance.now() - classificationStarted
              classificationTrainCount++
              if (benchmarkResolved.cacheHit) classificationCacheHits++
              else classificationCacheMisses++
              activeVisibleTrainIds.push(v.vehicleId)
            }

            // Viewport culling: skip adding off-screen vehicles to the GeoJSON
            // payload. Position math still runs above so tState stays current and
            // the vehicle animates smoothly the moment it re-enters the viewport.
            if (
              vehicleNeedsData && shouldCullProjectedVehicle({
                anchor: [lon, lat],
                heading: bearing,
                project: projectForLod,
                viewport: { width: lodCamera.width, height: lodCamera.height },
                consistLengthMetres: v.type === 'subway'
                  ? (TRAIN_CARS - 1) * CAR_SPACING_M + 18.35
                  : 24,
                pinned: isPinned,
              })
            ) {
              continue
            }

            // Move the rider's pulsing ring onto the vehicle it belongs to. This runs
            // BEFORE the bus branch takes its `continue` below, which is why it covers both
            // fleets — the ring is created for every boarded vehicle now, not just subways
            // (see the effect at the bottom of this file). No `continue` here either: the
            // boarded vehicle falls through and is emitted like any other. It is no longer
            // tinted (see the note over the bus body layer) and no longer forced to a mesh
            // (see `shouldUseLabelOnlyVehicleLayers`), so this ring and the highlighted
            // `boarded-route` line are what mark it in every display mode.
            if (v.vehicleId === tid && riderMarker) {
              const halo = laneM ? offsetPoint({ lon, lat }, bearing + 90, laneM) : { lon, lat }
              riderMarker.setLngLat([halo.lon, halo.lat])
            }

            // Ease the CENTER heading per train (shortest-angle, frame-rate independent via the
            // shared per-frame factor hk) BEFORE it drives the rigid car spread, so a sharp
            // track-direction change at a corner / hand-off rotates the whole train gently
            // instead of whipping the end cars. Without it an off-center car jumps off·sin(Δθ)
            // instantly (a ~90° flip ≈ a full train length), which read as cars teleporting in/
            // out and the model flipping. Seeded to the target on first sight (no spin-up).
            if (Number.isFinite(bearing)) {
              const ckey = v.vehicleId
              const prevH = centerHead.get(ckey)
              if (prevH != null) {
                const diff = ((bearing - prevH + 540) % 360) - 180
                bearing = (prevH + diff * hk + 360) % 360
              }
              centerHead.set(ckey, bearing)
            }

            if (vehicleNeedsData) {
              const col = vehicleColorRgb(v, frameTint)

              // Buses are one rigid box: emit a single datum at the live-glided
              // position computed above (no segment spread). Facing is the bus's travel
              // direction, held steady while it's stopped (see busGlide).
              if (v.type === 'bus') {
                emit(v.vehicleId, lon, lat, col, v.routeId, bearing, v.type, 0, v.destinationName, v.label)
                continue
              }

              const resolved = benchmarkResolved ?? resolveTrainRenderProfile({
                  vehicleId: v.vehicleId,
                  lon,
                  lat,
                  bearing,
                  boarded: v.vehicleId === tid,
                  watched: v.vehicleId === wid,
                  vehicleDisplay: frameVehicleDisplay,
                  camera: lodCamera,
                  project: projectForLod,
                })
              if (benchmarkSink) visibleTrainCount++
              if (!benchmarkResolved) {
                activeVisibleTrainIds.push(v.vehicleId)
              }

              // Each car places a front and rear truck on the train's OWN breadcrumb trail;
              // the rigid body sits at their midpoint and faces along the chord between them.
              // This makes the visible wheelsets constrain the car through a curve instead of
              // letting the whole body pivot around one centre pose. Each follower samples the
              // breadcrumb trail at its coupling distance, so the formation bends along
              // curves by replaying the path the front already smoothly traced: a follower
              // can only be where the center has already been, so it is exactly as smooth
              // as the front. No car ever samples track geometry independently (the
              // original pop/teleport bug). Straight-behind spread covers a missing/
              // degenerate trail at the same coupling distance.
              const carPoses = layoutTrailingConsist(
                { lon, lat, bearing },
                trails.get(v.vehicleId),
                resolved.profile.geometry === 'marker'
                  ? 1
                  : DISTANCE_TRAIN_LOD_ENABLED ? TRAIN_CARS : trainCars,
                CAR_SPACING_M,
                resolved.profile.geometry === 'marker' ? 0 : CAR_TRUCK_CENTRES_M,
                laneM,
                trackBearing,
              )
              for (const car of carPoses) {
                emit(
                  v.vehicleId, car.lon, car.lat, col, v.routeId, car.bearing,
                  v.type, car.index, v.destinationName, v.label, resolved.profile,
                )
              }
            }
          }

          if (shouldSetData) {
            if (ordinaryFleetDue) {
              ordinaryDatumPool.length = ordinaryDn
              ordinaryVehicleLayers = buildVehicleLayers(ordinaryDatumPool, 'ordinary', !!benchmarkSink)
              if (benchmarkSink) {
                ordinaryVehicleLayerMetrics = {
                  ...vehicleLayerMetrics,
                  tiers: { ...vehicleLayerMetrics.tiers },
                }
              }
            }
            if (hasPinnedCloseup) {
              pinnedDatumPool.length = pinnedDn
              pinnedVehicleLayers = buildVehicleLayers(pinnedDatumPool, 'pinned', !!benchmarkSink)
              if (benchmarkSink) {
                pinnedVehicleLayerMetrics = {
                  ...vehicleLayerMetrics,
                  tiers: { ...vehicleLayerMetrics.tiers },
                }
              }
            } else if (ordinaryFleetDue) {
              pinnedDatumPool.length = 0
              pinnedVehicleLayers = []
              pinnedVehicleLayerMetrics = emptyVehicleLayerMetrics()
            }
            deckOverlay?.setProps({
              layers: [...ordinaryVehicleLayers, ...pinnedVehicleLayers, ...buildFerryLayers(), ...buildRailLayers(), ...buildNjBusLayers()],
            })
            if (DISTANCE_TRAIN_LOD_ENABLED && ordinaryFleetDue) {
              trainLodClassificationCache.cleanup(activeVisibleTrainIds)
              trainRenderLodPolicy.cleanup(activeVisibleTrainIds)
            }
            if (benchmarkSink) {
              const ordinaryMetrics = ordinaryVehicleLayerMetrics
              const pinnedMetrics = pinnedVehicleLayerMetrics
              const observedDrawCalls = trainLodDrawCallCounter.consume()
              vehicleLayerMetrics = {
                layerBuildMs: performance.now() - layerBuildStarted,
                trainDatumCount: ordinaryMetrics.trainDatumCount + pinnedMetrics.trainDatumCount,
                stickerInstanceCount:
                  ordinaryMetrics.stickerInstanceCount + pinnedMetrics.stickerInstanceCount,
                triangles: ordinaryMetrics.triangles + pinnedMetrics.triangles,
                drawCalls: observedDrawCalls,
                scenegraphLayerCount:
                  ordinaryMetrics.scenegraphLayerCount + pinnedMetrics.scenegraphLayerCount,
                emptyModelTierLayerCount:
                  ordinaryMetrics.emptyModelTierLayerCount + pinnedMetrics.emptyModelTierLayerCount,
                tiers: {
                  marker: ordinaryMetrics.tiers.marker + pinnedMetrics.tiers.marker,
                  L2: ordinaryMetrics.tiers.L2 + pinnedMetrics.tiers.L2,
                  L1: ordinaryMetrics.tiers.L1 + pinnedMetrics.tiers.L1,
                  L0: ordinaryMetrics.tiers.L0 + pinnedMetrics.tiers.L0,
                },
              }
              // Derive this independently from submitted lead datums so a tier-counter
              // omission, duplicate pin transition, or marker expansion cannot make the
              // visible/tier invariant pass by construction.
              const submittedTrainIds = new Set<string>()
              for (const pool of [ordinaryDatumPool, pinnedDatumPool]) {
                for (const datum of pool) {
                  if (datum.type === 'subway' && datum.segIdx === 0) {
                    submittedTrainIds.add(datum.vehicleId)
                  }
                }
              }
              visibleTrainCount = submittedTrainIds.size
              if (timestamp - lastTrainLodMemorySample >= 1_000) {
                lastTrainLodMemorySample = timestamp
                const memory = (performance as Performance & {
                  memory?: { usedJSHeapSize: number }
                }).memory
                if (memory && Number.isFinite(memory.usedJSHeapSize)) {
                  benchmarkSink.recordMemory({
                    atMs: timestamp,
                    usedMiB: memory.usedJSHeapSize / (1024 * 1024),
                    source: 'performance-memory',
                  })
                }
              }
              benchmarkSink.recordFrame({
                atMs: timestamp,
                frameMs: frameDtMs,
                layerBuildMs: vehicleLayerMetrics.layerBuildMs,
                classificationMs,
                classificationTrainCount,
                visibleTrainCount,
                trainDatumCount: vehicleLayerMetrics.trainDatumCount,
                stickerInstanceCount: vehicleLayerMetrics.stickerInstanceCount,
                triangles: vehicleLayerMetrics.triangles,
                drawCalls: vehicleLayerMetrics.drawCalls,
                scenegraphLayerCount: vehicleLayerMetrics.scenegraphLayerCount,
                emptyModelTierLayerCount: vehicleLayerMetrics.emptyModelTierLayerCount,
                classificationCacheHits,
                classificationCacheMisses,
                tiers: vehicleLayerMetrics.tiers,
              })
            }
          }

          animFrame = requestAnimationFrame(frame)
        }

        animFrame = requestAnimationFrame(frame)

        // The vehicles the map draws: the filter/selection gate, plus ALWAYS the one
        // the rider boarded. Its model carries the boarded tint and the frame loop moves
        // the pulse ring to it; dropping it from this list (because the Subway/Bus pill or
        // a route selection excludes its line) silently leaves the rider with an untinted
        // map and a ring frozen where they got on.
        const visibleVehicles = (
          state: ReturnType<typeof useTransitStore.getState>,
        ): VehiclePosition[] => {
          // Ferries are drawn by their own path (buildFerryLayers / visibleFerries).
          // Railroad trains likewise (buildRailLayers / visibleRail), and NJ Transit's buses
          // (buildNjBusLayers / visibleNjBus).
          const all = Object.values(state.vehicles).filter((v) => v.type !== 'ferry' && v.type !== 'rail' && v.type !== 'njbus')
          // A route selection (Routes panel or active trip plan) overrides the Subway/Bus
          // type gate: the user asked for those exact lines, so their vehicles show
          // whichever pill is active, and nothing else.
          const focus = focusedRouteIds(state)
          const visible =
            focus.size > 0
              ? all.filter((v) => focus.has(v.routeId))
              : all.filter((v) => showsMode(state.transitFilter, v.type))

          const pinnedIds = [
            useRiderStore.getState().trackedVehicleId,
            useVehicleWatchStore.getState().watchedVehicleId,
          ]
          for (const pinnedId of pinnedIds) {
            if (!pinnedId || visible.some((v) => v.vehicleId === pinnedId)) continue
            const pinned = state.vehicles[pinnedId]
            if (pinned) visible.push(pinned)
          }
          return visible
        }

        // The boats to draw: every ferry while the filter includes Ferry, or those of a
        // selected ferry route. Nothing at all until the network has loaded — a boat drawn
        // before its route's colour is registered would bake the fallback navy into the
        // badge cache for the session.
        const visibleFerries = (state: ReturnType<typeof useTransitStore.getState>): VehiclePosition[] => {
          if (!useFerryStore.getState().network) return []
          const focus = focusedRouteIds(state)
          const all = Object.values(state.vehicles).filter((v) => v.type === 'ferry')
          if (focus.size > 0) return all.filter((v) => focus.has(v.routeId))
          return showsMode(state.transitFilter, 'ferry') ? all : []
        }

        // The trains to draw: every LIRR / Metro-North train while the filter includes Rail,
        // or those of a selected branch. Nothing until the network has loaded, for the
        // ferry's reason (a badge rasterised before its branch colour would stay blue).
        const visibleRail = (state: ReturnType<typeof useTransitStore.getState>): VehiclePosition[] => {
          if (!useRailStore.getState().network) return []
          const focus = focusedRouteIds(state)
          const all = Object.values(state.vehicles).filter((v) => v.type === 'rail')
          if (focus.size > 0) return all.filter((v) => focus.has(v.routeId))
          return showsMode(state.transitFilter, 'rail') ? all : []
        }

        // The NJ Transit buses to draw: every one while the filter includes NJ Bus, or those of
        // a selected route. Nothing until the network has loaded, for the rail's reason — a
        // badge rasterised before registration would print the feed's internal route id.
        const visibleNjBus = (state: ReturnType<typeof useTransitStore.getState>): VehiclePosition[] => {
          if (!useNjBusStore.getState().network) return []
          const focus = focusedRouteIds(state)
          const all = Object.values(state.vehicles).filter((v) => v.type === 'njbus')
          if (focus.size > 0) return all.filter((v) => focus.has(v.routeId))
          return showsMode(state.transitFilter, 'njbus') ? all : []
        }

        // Subscribe to store changes imperatively: nothing here goes through the reactive
        // graph. Fires when vehicles, filter, or selected route changes.
        const unsubVehicles = useTransitStore.subscribe((state, prev) => {
          if (benchmarkFleetActive) return
          if (
            state.vehicles === prev.vehicles &&
            state.transitFilter === prev.transitFilter &&
            state.selectedRouteIds === prev.selectedRouteIds &&
            state.tripRouteIds === prev.tripRouteIds
          )
            return

          const visible = visibleVehicles(state)
          vehicles = visible
          ferries = visibleFerries(state)
          railTrains = visibleRail(state)
          njBuses = visibleNjBus(state)

          const filterOrRouteChanged =
            state.transitFilter !== prev.transitFilter ||
            state.selectedRouteIds !== prev.selectedRouteIds ||
            state.tripRouteIds !== prev.tripRouteIds

          if (filterOrRouteChanged) {
            // Push a synchronous deck.gl update so vehicles disappear/appear in the same
            // frame as the filter tap without waiting for the animation loop's next tick.
            if (deckOverlay) {
              const nowSec = Date.now() / 1000
              const tid = useRiderStore.getState().trackedVehicleId
              const wid = useVehicleWatchStore.getState().watchedVehicleId
              const tint = useLayersStore.getState()
              // Camera reads hoisted out of the loop: they are the same for every vehicle
              // in this rebuild, and getCenter() allocates.
              const rebuildZoom = map.getZoom()
              const rebuildCenter = map.getCenter()
              const rebuildLat = rebuildCenter.lat
              const rebuildContainer = map.getContainer()
              const rebuildCamera = {
                longitude: rebuildCenter.lng,
                latitude: rebuildCenter.lat,
                zoom: rebuildZoom,
                pitch: map.getPitch(),
                bearing: map.getBearing(),
                width: rebuildContainer.clientWidth,
                height: rebuildContainer.clientHeight,
              }
              const rebuildProject = (position: readonly [number, number]) => {
                try {
                  const point = map.project([position[0], position[1]])
                  return Number.isFinite(point.x) && Number.isFinite(point.y)
                    ? { x: point.x, y: point.y }
                    : null
                } catch {
                  return null
                }
              }
              const rebuildDisplay = useSettingsStore.getState().vehicleDisplay
              const datums: VehicleDatum[] = []
              for (const v of visible) {
                const col = vehicleColorRgb(v, tint)
                const boarded = v.vehicleId === tid
                const watched = v.vehicleId === wid
                // Buses render as a single rigid box: one datum at their live-glided
                // position so a filter toggle reveals them mid-glide, not snapped.
                if (v.type === 'bus') {
                  const g = busGlide(v, nowSec)
                  if (shouldCullProjectedVehicle({
                    anchor: [g.lon, g.lat],
                    heading: g.bearing,
                    project: rebuildProject,
                    viewport: { width: rebuildCamera.width, height: rebuildCamera.height },
                    consistLengthMetres: 24,
                    pinned: boarded || watched,
                  })) continue
                  datums.push({
                    vehicleId: v.vehicleId, position: [g.lon, g.lat], color: col,
                    routeId: v.routeId, bearing: g.bearing, type: v.type, segIdx: 0,
                    boarded, watched,
                    destinationName: v.destinationName, carLabel: v.label,
                  })
                  continue
                }
                // Same trail sampling as the frame loop, anchored at the trail's newest
                // point (the last RENDERED center) when one exists, so a filter toggle
                // reveals the train exactly where it is on screen, not at the raw feed fix.
                const trail = trails.get(v.vehicleId)
                const newest = trail?.[trail.length - 1]
                const anm = anim.get(v.vehicleId)
                const rebuildTrackSeg = anm
                  ? segCache.get(`${v.routeId}|${anm.prev}|${anm.next}`) ?? null
                  : null
                if (!rebuildTrackSeg && !benchmarkFleetActive) continue
                const rebuildTrackPosition = rebuildTrackSeg && anm
                  ? sampleSeg(rebuildTrackSeg, anm.progress)
                  : null
                const lon0 = newest?.lon ?? rebuildTrackPosition?.[0] ?? v.longitude
                const lat0 = newest?.lat ?? rebuildTrackPosition?.[1] ?? v.latitude
                if (shouldCullProjectedVehicle({
                  anchor: [lon0, lat0],
                  heading: v.bearing,
                  project: rebuildProject,
                  viewport: { width: rebuildCamera.width, height: rebuildCamera.height },
                  consistLengthMetres: (TRAIN_CARS - 1) * CAR_SPACING_M + 18.35,
                  pinned: boarded || watched,
                })) continue
                // Same drawn-route-line lane shift the frame loop applies, so a filter
                // toggle can't reveal the train one lane off its colored line. `dir` comes
                // from the cached slice for the segment it is currently gliding.
                const rebuildTrackBearing = rebuildTrackSeg && anm
                  ? bearingAtT(rebuildTrackSeg, anm.progress)
                  : v.bearing
                const profile = resolveTrainRenderProfile({
                  vehicleId: v.vehicleId,
                  lon: lon0,
                  lat: lat0,
                  bearing: v.bearing,
                  boarded,
                  watched,
                  vehicleDisplay: rebuildDisplay,
                  camera: rebuildCamera,
                  project: rebuildProject,
                }).profile
                const carPoses = layoutTrailingConsist(
                  { lon: lon0, lat: lat0, bearing: v.bearing },
                  trail,
                  profile.geometry === 'marker'
                    ? 1
                    : DISTANCE_TRAIN_LOD_ENABLED ? TRAIN_CARS : trainCars,
                  CAR_SPACING_M,
                  profile.geometry === 'marker' ? 0 : CAR_TRUCK_CENTRES_M,
                  routeLineOffsetMetres(
                    rebuildTrackSeg?.sourceRouteId ?? v.routeId,
                    rebuildZoom,
                    rebuildLat,
                    rebuildTrackSeg?.dir ?? 1,
                  ),
                  rebuildTrackBearing,
                )
                for (const car of carPoses) {
                  datums.push({
                    vehicleId: v.vehicleId,
                    position: [car.lon, car.lat],
                    color: col, routeId: v.routeId, bearing: car.bearing, type: v.type,
                    segIdx: car.index, boarded, watched,
                    destinationName: v.destinationName, carLabel: v.label,
                    trainRenderProfile: profile,
                    carIndex: car.index,
                    destination: v.destinationName || `${v.routeId} TRAIN`,
                    carNumber: v.label || v.vehicleId.slice(-5),
                  })
                }
              }
              const rebuildOrdinaryDatums = datums.filter((d) => !d.boarded && !d.watched)
              const rebuildPinnedDatums = datums.filter((d) => d.boarded || d.watched)
              ordinaryVehicleLayers = buildVehicleLayers(rebuildOrdinaryDatums, 'ordinary')
              pinnedVehicleLayers = rebuildPinnedDatums.length
                ? buildVehicleLayers(rebuildPinnedDatums, 'pinned')
                : []
              deckOverlay.setProps({
                layers: [...ordinaryVehicleLayers, ...pinnedVehicleLayers, ...buildFerryLayers(), ...buildRailLayers(), ...buildNjBusLayers()],
              })
            }

            applyLayerVisibility()
            updateSelectedBusRoutes()
          }

          // Let the animation loop continue smoothing positions on subsequent frames.
          lastFrame = 0
        })

        // Apply the selected-station highlight by swapping the two highlight layers'
        // filters. Cheap (no source setData).
        const applySelectedStation = (stationId: string | null) => {
          selectedStationId = stationId
          const filter = stationId
            ? (['==', ['get', 'id'], stationId] as any)
            : (['==', ['get', 'id'], '__none__'] as any)
          if (map.getLayer('stop-selected-pulse')) map.setFilter('stop-selected-pulse', filter)
          if (map.getLayer('stop-selected-core')) map.setFilter('stop-selected-core', filter)
        }

        // Imperative subscription: fires only when the selected station changes.
        const unsubSelectedStation = useTransitStore.subscribe((state, prev) => {
          if (state.selectedStationId === prev.selectedStationId) return
          applySelectedStation(state.selectedStationId)
        })
        applySelectedStation(useTransitStore.getState().selectedStationId)

        // Same treatment for the tapped bus stop: swap the two bus-stop highlight
        // layers' filters to the selected id (the rAF loop pulses the ring while set).
        const applySelectedBusStop = (busStopId: string | null) => {
          selectedBusStopId = busStopId
          const filter = busStopId
            ? (['==', ['get', 'id'], busStopId] as any)
            : (['==', ['get', 'id'], '__none__'] as any)
          if (map.getLayer('bus-stop-selected-pulse')) map.setFilter('bus-stop-selected-pulse', filter)
          if (map.getLayer('bus-stop-selected-core')) map.setFilter('bus-stop-selected-core', filter)
        }
        const unsubSelectedBusStop = useTransitStore.subscribe((state, prev) => {
          if (state.selectedBusStopId === prev.selectedBusStopId) return
          applySelectedBusStop(state.selectedBusStopId)
        })
        applySelectedBusStop(useTransitStore.getState().selectedBusStopId)

        // The open ferry landing's highlight: the same id-filter swap, without the pulse.
        const applySelectedFerryLanding = (landingId: string | null) => {
          if (map.getLayer('ferry-landing-selected')) {
            map.setFilter('ferry-landing-selected', ['==', ['get', 'id'], landingId ?? '__none__'])
          }
        }
        const unsubSelectedFerryLanding = useTransitStore.subscribe((state, prev) => {
          if (state.selectedFerryLandingId === prev.selectedFerryLandingId) return
          applySelectedFerryLanding(state.selectedFerryLandingId)
        })
        applySelectedFerryLanding(useTransitStore.getState().selectedFerryLandingId)

        // The ferry network landing (a lazy load the Ferry filter kicks off): draw the lines
        // and landings, and admit the boats that were already arriving on the socket but held
        // back until their route colours existed.
        const unsubFerry = useFerryStore.subscribe((state, prev) => {
          if (state.network === prev.network) return
          applyFerryNetwork()
          ferries = visibleFerries(useTransitStore.getState())
          applyLayerVisibility()
        })
        applyFerryNetwork()

        // The open rail station's highlight.
        const applySelectedRailStation = (stationId: string | null) => {
          if (map.getLayer('rail-station-selected')) {
            map.setFilter('rail-station-selected', ['==', ['get', 'id'], stationId ?? '__none__'])
          }
        }
        const unsubSelectedRailStation = useTransitStore.subscribe((state, prev) => {
          if (state.selectedRailStationId === prev.selectedRailStationId) return
          applySelectedRailStation(state.selectedRailStationId)
        })
        applySelectedRailStation(useTransitStore.getState().selectedRailStationId)

        // The rail network landing (a lazy load the Rail filter kicks off): draw the lines
        // and stations, and admit the trains held back until their branch colours existed.
        const unsubRail = useRailStore.subscribe((state, prev) => {
          if (state.network === prev.network) return
          applyRailNetwork()
          railTrains = visibleRail(useTransitStore.getState())
          applyLayerVisibility()
        })
        applyRailNetwork()

        // The open NJ Transit stop's highlight.
        const applySelectedNjBusStop = (stopId: string | null) => {
          if (map.getLayer('njbus-stop-selected')) {
            map.setFilter('njbus-stop-selected', ['==', ['get', 'id'], stopId ?? '__none__'])
          }
        }
        const unsubSelectedNjBusStop = useTransitStore.subscribe((state, prev) => {
          if (state.selectedNjBusStopId === prev.selectedNjBusStopId) return
          applySelectedNjBusStop(state.selectedNjBusStopId)
        })
        applySelectedNjBusStop(useTransitStore.getState().selectedNjBusStopId)

        // The NJ Transit network landing (a lazy load the NJ Bus filter kicks off): draw the
        // lines and stops, and admit the buses held back until their route numbers existed.
        const unsubNjBus = useNjBusStore.subscribe((state, prev) => {
          if (state.network === prev.network) return
          applyNjBusNetwork()
          njBuses = visibleNjBus(useTransitStore.getState())
          applyLayerVisibility()
        })
        applyNjBusNetwork()

        // Redraw the boarded-bus route highlight whenever the ride or its stop list
        // changes (board, alight, or the 30 s onward-calls refresh).
        const unsubBoardedRoute = useRiderStore.subscribe((state, prev) => {
          if (benchmarkFleetActive) return
          if (
            state.isRiding === prev.isRiding &&
            state.trackedVehicleId === prev.trackedVehicleId &&
            state.upcomingStops === prev.upcomingStops
          )
            return
          // Boarding is what makes a vehicle exempt from the filter, so the visible set is
          // recomputed here too; otherwise the newly boarded vehicle stays off the map
          // until some unrelated transit-store change happens to refresh it.
          if (state.trackedVehicleId !== prev.trackedVehicleId) {
            vehicles = visibleVehicles(useTransitStore.getState())
          }
          updateBoardedRoute()
        })
        updateBoardedRoute()

        // Watching pins a vehicle into the render set without granting rider state.
        // Orbit values are consumed by the frame loop; only id changes rebuild the list.
        const unsubWatchedVehicle = useVehicleWatchStore.subscribe((state, prev) => {
          if (benchmarkFleetActive) return
          if (state.watchedVehicleId === prev.watchedVehicleId) return
          watchCameraFollow = null

          // ⚠️ Entering the chase camera BORROWS the map camera — the frame loop `easeTo`s
          // it onto the vehicle at WATCH_DEFAULT_ZOOM/PITCH (18.2 / 78°) every tick — so
          // leaving has to hand it back. Nothing did, and the map was simply abandoned at
          // that pose. Every render mode except `labels` picks its geometry off the ZOOM
          // (`r160LodForZoom` / `xcel40LodForZoom` and the projected-size policy that
          // replaced it), and 18.2 is deep in mesh territory, so the whole fleet stayed
          // fully modelled after the 3D toggle went off. It reads as "the toggle did
          // nothing", but `vehicleDisplay` was never overridden — the camera it is
          // resolved against was, and never given back.
          //
          // Zoom, pitch and bearing are restored. The CENTRE deliberately is not captured:
          // it belongs to wherever the map was when watching began, and a vehicle followed
          // for even a few minutes is nowhere near there — restoring it would throw the
          // rider across the city to stare at empty track. Where they are looking is
          // right; how close, how tilted and which way up is what was borrowed.
          if (state.watchedVehicleId && !prev.watchedVehicleId) {
            preWatchCamera = {
              zoom: map.getZoom(),
              pitch: map.getPitch(),
              bearing: map.getBearing(),
            }
          } else if (!state.watchedVehicleId && preWatchCamera) {
            // Safe to animate: `watched` is already false, so this frame's loop no longer
            // drives the camera and there is nothing to fight the ease. `offset` is reset
            // because the chase pose parks the vehicle below centre (`watchCameraPose`),
            // and easeTo applies it against the destination centre.
            map.easeTo({ ...preWatchCamera, offset: [0, 0], duration: 450 })
            preWatchCamera = null
          }

          if (state.watchedVehicleId) {
            // Re-seed the watched train at the denser trail spacing on its next frame.
            trails.delete(state.watchedVehicleId)
            const prefix = `${state.watchedVehicleId}:`
            for (const key of renderHeading.keys()) {
              if (key.startsWith(prefix) && !key.endsWith(':0')) renderHeading.delete(key)
            }
          }
          vehicles = visibleVehicles(useTransitStore.getState())
          lastFrame = 0
        })

        // Seed `vehicles` immediately; the animation loop picks it up on its first tick.
        // Same rule as the subscription above: a selection overrides the type gate.
        vehicles = TRAIN_LOD_BENCHMARK_AUTO_RUN
          ? []
          : visibleVehicles(useTransitStore.getState())
        ferries = TRAIN_LOD_BENCHMARK_AUTO_RUN ? [] : visibleFerries(useTransitStore.getState())
        railTrains = TRAIN_LOD_BENCHMARK_AUTO_RUN ? [] : visibleRail(useTransitStore.getState())
        njBuses = TRAIN_LOD_BENCHMARK_AUTO_RUN ? [] : visibleNjBus(useTransitStore.getState())

        // Re-apply base-layer visibility whenever a layer is toggled. The two vehicle-tint
        // layers need no handler: the frame loop reads them every frame, so a toggle
        // recolors on the next rAF tick.
        const unsubLayers = useLayersStore.subscribe((state, prev) => {
          if (
            state.routeLines === prev.routeLines &&
            state.stations === prev.stations &&
            state.busStops === prev.busStops &&
            state.buildings === prev.buildings
          )
            return
          applyLayerVisibility()
        })

        // The nearby panel lists bus stops regardless of the transit filter, so it asks
        // for the bus-stop set even on a subway-filtered session (where the lazy load
        // above wouldn't otherwise fire). loadBusStops is idempotent, so an ask that
        // arrives after the data already loaded is a no-op.
        const unsubBusStopsReq = useStopsStore.subscribe((state, prev) => {
          if (state.busStopsRequest === prev.busStopsRequest) return
          loadBusStops()
        })
        if (useStopsStore.getState().busStopsRequest > 0) loadBusStops()

        // Honor the filter and layer state the app started on (both are restored from
        // localStorage), so a session that ended on "bus" shows bus stops immediately.
        applyLayerVisibility()
        updateSelectedBusRoutes()

        // Store unsubscribes so cleanup can cancel them
        storeUnsub = () => {
          unsubVehicles()
          unsubSelectedFerryLanding()
          unsubFerry()
          unsubSelectedRailStation()
          unsubRail()
          unsubSelectedNjBusStop()
          unsubNjBus()
          unsubSelectedStation()
          unsubSelectedBusStop()
          unsubBoardedRoute()
          unsubWatchedVehicle()
          unsubLayers()
          unsubBusStopsReq()
        }

        if (TRAIN_LOD_BENCHMARK_AUTO_RUN) {
          const profile = trainLodBenchmarkProfileFromEnv(
            { ...import.meta.env },
            globalThis.navigator?.userAgent ?? 'unknown-browser',
          )
          if (profile) {
            benchmarkFleetActive = true

            const resetBenchmarkRenderer = () => {
              vehicles = []
              benchmarkWatchedVehicleId = null
              benchmarkBoardedVehicleId = null
              watchCameraFollow = null
              anim.clear()
              busGlides.clear()
              renderHeading.clear()
              emitSmooth.clear()
              centerHead.clear()
              trails.clear()
              trainRenderLodPolicy.clear()
              trainLodClassificationCache.clear()
              ordinaryDatumPool.length = 0
              pinnedDatumPool.length = 0
              ordinarySignPool.length = 0
              pinnedSignPool.length = 0
              ordinaryBusSignPool.length = 0
              pinnedBusSignPool.length = 0
              ordinaryVehicleLayers = []
              pinnedVehicleLayers = []
              vehicleLayerMetrics = emptyVehicleLayerMetrics()
              ordinaryVehicleLayerMetrics = emptyVehicleLayerMetrics()
              pinnedVehicleLayerMetrics = emptyVehicleLayerMetrics()
              trainLodDrawCallCounter.clearPending()
              lastFleetLayerUpdate = -Infinity
              lastPinnedVehicleKey = ''
              lastTrainLodMemorySample = -Infinity
              lastFrame = 0
              headingLastNow = 0
              trainCars = 1
              deckOverlay?.setProps({ layers: [] })
            }

            benchmarkRunner = createTrainLodBenchmarkRunner({
              adapter: {
                prepare: () => {
                  benchmarkModelCacheToken = ''
                  resetBenchmarkRenderer()
                },
                clearModelCache: (context) => {
                  // A unique URL bypasses deck/luma's process-global loader cache for every
                  // cold repetition without reloading the app or contaminating replay time.
                  benchmarkModelCacheToken = `${context.scenario.id}-${context.repetition}`
                  r160ModelLoadStarted.clear()
                  r160ModelLoadReported.clear()
                },
                setCamera: (camera) => {
                  map.stop?.()
                  map.jumpTo({
                    center: [camera.longitude, camera.latitude],
                    zoom: camera.zoom,
                    pitch: camera.pitch,
                    bearing: camera.bearing,
                  })
                  lastFleetLayerUpdate = -Infinity
                  lastFrame = 0
                },
                setFleet: (fixtureVehicles) => {
                  benchmarkWatchedVehicleId =
                    fixtureVehicles.find((vehicle) => vehicle.watched)?.vehicleId ?? null
                  benchmarkBoardedVehicleId =
                    fixtureVehicles.find((vehicle) => vehicle.boarded)?.vehicleId ?? null
                  vehicles = fixtureVehicles.map(trainLodFixtureVehiclePosition)
                  lastPinnedVehicleKey = ''
                  lastFleetLayerUpdate = -Infinity
                },
                cleanup: () => {
                  benchmarkModelCacheToken = ''
                  resetBenchmarkRenderer()
                },
              },
            })

            void runTrainLodBenchmarkProfile(
              benchmarkRunner,
              profile,
              publishTrainLodBenchmarkCompactScenarioReport,
            ).then(() => {
              console.info('TRAIN_LOD_BENCHMARK_COMPLETE')
            }).catch((error) => {
              console.error(`TRAIN_LOD_BENCHMARK_ERROR ${JSON.stringify({
                message: error instanceof Error ? error.message : String(error),
              })}`)
            })
          }
        }
      })
    })

    return () => {
      cancelled = true
      benchmarkRunner?.cancel('TransitMap disposed')
      benchmarkRunner = null
      benchmarkFleetActive = false
      benchmarkWatchedVehicleId = null
      benchmarkBoardedVehicleId = null
      benchmarkModelCacheToken = ''
      container.style.cssText = originalContainerStyle
      mapControls.resetView = () => {}
      mapControls.flyTo = () => {}
      mapControls.reloadWithSchedule = () => {}
      if (animFrame) cancelAnimationFrame(animFrame)
      animFrame = 0
      riderMarker?.remove()
      riderMarker = null

      // Every step is isolated. deck.gl's finalize() and MapLibre's remove() drive the
      // SAME GL context (interleaved mode), so a throw in an early step used to skip
      // every later one, including the map.remove() that releases the canvas.
      const step = (fn: () => void) => {
        try {
          fn()
        } catch {}
      }

      if (deckOverlay) {
        const overlay = deckOverlay
        deckOverlay = null
        step(() => map?.removeControl(overlay))
        step(() => overlay.finalize())
      }
      step(() => storeUnsub?.())
      storeUnsub = null
      step(() => stopSchemeWatch?.())
      stopSchemeWatch = null

      // Grab the canvas BEFORE remove(): afterwards the map won't hand it back.
      const canvas: HTMLCanvasElement | null = map?.getCanvas?.() ?? null
      step(() => map?.remove())
      map = null

      // MapLibre's remove() deletes the GL objects it owns but leaves the WebGL CONTEXT
      // alive until the canvas is garbage collected, which, while anything still
      // references the canvas, may be much later or never. Each teardown therefore
      // stacked another live context, and a context is not cheap: measured at ~16
      // programs, 6 samplers and ~100 MB of textures apiece. In dev that is one leaked
      // context per HMR save, so the map got slower with every edit until the tab died.
      // loseContext() hands the whole thing back to the driver immediately.
      step(() => {
        const gl = (canvas?.getContext('webgl2') ?? canvas?.getContext('webgl')) as
          | WebGLRenderingContext
          | WebGL2RenderingContext
          | null
        gl?.getExtension('WEBGL_lose_context')?.loseContext()
      })
    }
  })

  $effect(() => {
    const tid = trackedVehicleId.value
    if (!map || !tid) return
    // Use the rider store snapshot captured by startRide at board time: same
    // reliable source as the rider marker below. The transit store lookup can be
    // undefined (vehicle pruned / not yet present this frame), which silently
    // skips the zoom and leaves the user wherever they were; falling back to it
    // only when the snapshot is somehow missing keeps us robust.
    const v = useRiderStore.getState().trackedVehicle ?? useTransitStore.getState().vehicles[tid]
    if (v) map.flyTo({ center: [v.longitude, v.latitude], zoom: 15, duration: 1000 })
  })

  // Write the user's location into the 'user-location' source imperatively (same
  // mutate-the-source pattern the whole map uses). Runs ~1 Hz at most (GPS cadence).
  // When userLocation is null (permission denied / no fix yet) the source is cleared,
  // so the dot simply disappears and the map keeps working.
  $effect(() => {
    const loc = userLocation
    if (!map) return
    const src = map.getSource('user-location') as any
    if (!src) return // 'load' hasn't fired yet; this effect re-runs on the next userLocation change

    if (!loc) {
      src.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    // Reported accuracy (m) → pixels for the halo, at the current zoom & latitude.
    // MapLibre metersPerPixel = 40075016.686 * cos(lat) / (512 * 2^zoom). Capped so a
    // poor fix doesn't flood the screen. Recomputed on userLocation change only (not on
    // pure zoom); acceptable for MVP.
    const zoom = map.getZoom()
    const mpp = (40075016.686 * Math.cos((loc.latitude * Math.PI) / 180)) / (512 * 2 ** zoom)
    const accuracyPx = loc.accuracy ? Math.min(loc.accuracy / mpp, 120) : 0

    src.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [loc.longitude, loc.latitude] },
          properties: { accuracyPx },
        },
      ],
    })
  })

  // Draw / clear the planned-trip overlay. A zustand subscription rather than a
  // selector: itinerary picks are rare user actions and must not wake this component
  // (the whole map lives here). Framing (fitBounds) happens only when a trip is picked,
  // never on clear.
  $effect(() => {
    return useTripPlanStore.subscribe((state, prev) => {
      if (state.activeItinerary === prev.activeItinerary) return
      if (!map || !map.getSource('trip-itinerary')) return
      applyItinerary(map, state.activeItinerary, state.activeItinerary !== null)
    })
  })

  // Pulsing "this is your ride" ring, on EVERY boarded vehicle — moved to the vehicle's
  // position each frame in the animation loop.
  //
  // It was subway-only, on the reasoning that a bus was already marked by its boarded body
  // tint. Both halves of that stopped being true: the tint was removed (see the note over
  // the bus body layer — a flat per-instance colour repaints the model rather than marking
  // it, and an MTA bus IS its colour), and boarding no longer forces a mesh, so in `labels`
  // mode a boarded bus is one route badge among a field of route badges with nothing to
  // say it is yours. The ring is the signal that survives every display mode, because it is
  // a DOM marker rather than anything the LOD ladder can demote.
  //
  // Nothing about it was ever subway-shaped: it is a circle in `getRouteColor(routeId)`,
  // and the frame loop's `setLngLat` runs before the bus branch takes its `continue`, so
  // the position update already covered buses. `laneM` — the drawn-route lane offset the
  // halo is shifted by — is subway-only by construction and falls through to the plain
  // position for a bus, which is correct: bus routes are not lane-offset.
  $effect(() => {
    const tid = trackedVehicleId.value
    if (!map || !MarkerClass) return

    if (!tid) {
      riderMarker?.remove()
      riderMarker = null
      return
    }

    if (riderMarker) return

    // Use rider store (set by startRide): more reliable than transit store lookup
    const v = useRiderStore.getState().trackedVehicle
    if (!v) return

    injectRiderPulseCSS()
    const color = getRouteColor(v.routeId)
    const el = document.createElement('div')
    // Fixed 48×48 container so MapLibre anchor:'center' places its midpoint on the vehicle
    el.style.cssText = 'pointer-events:none; position:relative; width:48px; height:48px;'
    // Build via DOM rather than innerHTML so `color` can never be an HTML-injection sink
    // (it's a route color today, but assigning to .style is inert even for arbitrary input).
    const ring = document.createElement('div')
    ring.className = 'rider-ring'
    ring.style.color = color
    el.append(ring)
    riderMarker = new MarkerClass({ element: el, anchor: 'center' })
      .setLngLat([v.longitude, v.latitude])
      .addTo(map)
    // Smooth the marker's CSS transform between setLngLat calls so the ring
    // glides rather than teleporting on each animation frame.
    const parent = riderMarker.getElement().parentElement
    if (parent) (parent as HTMLElement).style.transition = 'transform 200ms linear'
  })
</script>

<div style="flex: 1; background-color: var(--app-ground, #000000); width: 100%; height: 100%;">
  <div bind:this={container} style="width: 100%; height: 100%;"></div>
</div>
